import { useState, useEffect, useMemo, useCallback, useRef, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ChevronLeft, ChevronRight, Plus, X, Loader2,
  LayoutGrid, BarChart2, Calendar, Clock,
  MapPin, Users, ChevronDown, Search, Check, Trash2, RotateCcw,
  FileSpreadsheet, Sun, DollarSign, CheckSquare, Square, AlertTriangle,
  Car, Download, AlertCircle, Send, Info as InfoIcon
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardHeader } from "@/components/DashboardHeader";
import ModalPortal from "@/components/ModalPortal";
import SubTabs from "@/components/SubTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// #42.1: "Normal timer" er det kanoniske valg for en normal arbejdsdag (ikke "").
const VAGTTYPER = ["Normal timer", "Behandler timer", "Syg", "Ferie/Fri", "Lægeeksamination", "Lægeundervisning", "Barnets første sygedag", "Andet"];

// #42.1: "Normal" og "Normal timer" er slået sammen. Kanonisk navn = "Normal timer".
// Tom vagttype (hubbens egen default) + legacy "Normal" → "Normal timer".
const NORMAL_TIMER = "Normal timer";
function kanonVagttype(vt) {
  if (!vt || vt === "Normal" || vt === "Normal timer") return NORMAL_TIMER;
  return vt;
}

const VAGTTYPE_META = {
  "":                        { label: "Normal timer",      color: "bg-[#FF2A6D]/10 text-[#FF2A6D]",       dot: "bg-[#FF2A6D]" },
  "Normal timer":            { label: "Normal timer",      color: "bg-[#FF2A6D]/10 text-[#FF2A6D]",       dot: "bg-[#FF2A6D]" },
  "Behandler timer":         { label: "Behandler timer",   color: "bg-blue-500/10 text-blue-500",          dot: "bg-blue-500" },
  "Syg":                     { label: "Syg",               color: "bg-red-500/10 text-red-500",            dot: "bg-red-500" },
  "Ferie":                   { label: "Ferie",             color: "bg-emerald-500/10 text-emerald-500",    dot: "bg-emerald-500" },
  "Fri":                     { label: "Fri",               color: "bg-purple-500/10 text-purple-500",      dot: "bg-purple-500" },
  "Ferie/Fri":               { label: "Ferie/Fri",         color: "bg-emerald-500/10 text-emerald-500",    dot: "bg-emerald-500" },
  "Lægeeksamination":        { label: "Lægeeksamination",  color: "bg-amber-500/10 text-amber-500",        dot: "bg-amber-500" },
  "Lægeundervisning":        { label: "Lægeundervisning",  color: "bg-amber-500/10 text-amber-500",        dot: "bg-amber-500" },
  "Barnets første sygedag":  { label: "Barnets første sygedag", color: "bg-orange-500/10 text-orange-500", dot: "bg-orange-500" },
  "Andet":                   { label: "Andet",             color: "bg-slate-500/10 text-slate-500",        dot: "bg-slate-500" },
  "__godkendt__":            { label: "Godkendt stempling", color: "bg-green-500/15 text-green-700 border border-green-500/30", dot: "bg-green-500" },
};

const ALLE_BYER = ["Alle", "København", "Aarhus", "Odense", "Aalborg", "Kolding", "Administration", "TNBC"];
const CITY_DB = { "København": "København", "Aarhus": "Aarhus", "Odense": "Odense", "Aalborg": "Aalborg", "Kolding": "Kolding" };

// By-farver fjernet (Opfølgning #31): vagt-bokse bruger ikke længere by-baseret
// farvekodning. Status = baggrund, vagttype = ramme. Bynavnet står i teksten.

// Returnér 3-4 tegns forkortelse for en lokation
function cityShort(city) {
  if (!city) return "";
  const map = {
    "København": "Kbh",
    "Aarhus": "Aar",
    "Odense": "Odn",
    "Aalborg": "Aal",
    "Kolding": "Kld",
    "TNBC": "TNBC",
    "Administration": "Adm",
    "Hjemmearbejdsplads": "Hjem",
  };
  return map[city] || city.slice(0, 3);
}

const DK_HELLIGDAGE = new Set([
  "2025-01-01","2025-04-17","2025-04-18","2025-04-21","2025-05-16","2025-05-29","2025-06-05","2025-06-08","2025-06-09","2025-12-25","2025-12-26",
  "2026-01-01","2026-04-02","2026-04-03","2026-04-06","2026-05-01","2026-05-14","2026-05-24","2026-05-25","2026-06-05","2026-12-25","2026-12-26",
  "2027-01-01","2027-03-25","2027-03-26","2027-03-29","2027-04-23","2027-06-03","2027-06-05","2027-06-13","2027-06-14","2027-12-25","2027-12-26",
]);

const DK_HELLIGDAGE_NAVNE = {
  "2025-01-01": "Nytårsdag", "2025-04-17": "Skærtorsdag", "2025-04-18": "Langfredag",
  "2025-04-21": "2. Påskedag", "2025-05-16": "Kristi Himmelfartsdag", "2025-05-29": "Grundlovsdag",
  "2025-06-05": "Grundlovsdag", "2025-06-08": "Pinsedag", "2025-06-09": "2. Pinsedag",
  "2025-12-25": "1. Juledag", "2025-12-26": "2. Juledag",
  "2026-01-01": "Nytårsdag", "2026-04-02": "Skærtorsdag", "2026-04-03": "Langfredag",
  "2026-04-06": "2. Påskedag", "2026-05-01": "Store Bededag", "2026-05-14": "Kristi Himmelfartsdag",
  "2026-05-24": "Pinsedag", "2026-05-25": "2. Pinsedag", "2026-06-05": "Grundlovsdag",
  "2026-12-25": "1. Juledag", "2026-12-26": "2. Juledag",
  "2027-01-01": "Nytårsdag", "2027-03-25": "Skærtorsdag", "2027-03-26": "Langfredag",
  "2027-03-29": "2. Påskedag", "2027-04-23": "Store Bededag", "2027-06-03": "Kristi Himmelfartsdag",
  "2027-06-05": "Grundlovsdag", "2027-06-13": "Pinsedag", "2027-06-14": "2. Pinsedag",
  "2027-12-25": "1. Juledag", "2027-12-26": "2. Juledag",
};

const MND_FULL = ["januar","februar","marts","april","maj","juni","juli","august","september","oktober","november","december"];
const MND_SHORT = ["jan","feb","mar","apr","maj","jun","jul","aug","sep","okt","nov","dec"];
const UGEDAGE = ["Ma","Ti","On","To","Fr","Lø","Sø"];

function vagtLabel(v) {
  if (v.vagttype === "__godkendt__") return "Godkendt stempling";
  if (v.vagttype === "Andet" && v.andet_navn) return v.andet_navn;
  return VAGTTYPE_META[v.vagttype]?.label || "Normal arbejdsdag";
}

// ── Opfølgning #31: ensartet vagt-boks ──────────────────────────────────────
// Drop by-farver. Status kommunikeres på to akser:
//   Baggrund = godkendelses-status (neutral / lysegrøn / lyserød)
//   Ramme    = vagttype (mørk grå standard / gul for Fri+Ferie / rød for Syg)
// Syg/Ferie/Fri beholder deres type-baggrund (som i dag); øvrige vagter får
// status-baggrund. Tekst er mørk for læsbarhed.

const FRAVAERS_TYPER = new Set(["Syg", "Ferie", "Ferie/Fri", "Fri"]);

// ── Opfølgning #36: timer pr. dag uden dobbeltælling ────────────────────────
// Princip: "stempling overtager planlagt vagt". Der er KUN én vagt pr.
// medarbejder pr. dag, og timer tælles KUN én gang:
//   • Findes der mindst én stempling den dag → brug summen af stemplet tid
//     (timer_actual). Den planlagte vagt er en ramme og tælles IKKE oveni.
//   • Ingen stempling → brug den planlagte vagts timer.
//   • #60: Fraværs-vagter (Syg/Ferie/Fri/Barnets/Læge*) TÆLLER nu med som
//     almindelige planlagte timer (behandles som Normal timer for månedsløn).
// Bruges ALLE steder hvor en dags-/uge-/periode-total beregnes, så der aldrig
// opstår dobbeltælling (uge-visning, lønsum, periode, eksport).
function dagTimer(vagterForDag = [], stemplerForDag = []) {
  // Stempling vundet: brug faktisk stemplet tid (ad-hoc såvel som matchet).
  if (stemplerForDag.length > 0) {
    return stemplerForDag.reduce((s, r) => s + (parseFloat(r.timer_actual) || 0), 0);
  }
  // Ingen stempling: planlagt tid — inkl. fravær (#60).
  return vagterForDag.reduce((s, v) => s + (parseFloat(v.timer) || 0), 0);
}

// Ramme efter vagttype.
function vagtRamme(vagttype) {
  if (vagttype === "Syg") return "border border-red-500";
  if (vagttype === "Ferie" || vagttype === "Fri" || vagttype === "Ferie/Fri") return "border border-yellow-500";
  return "border border-neutral-700 dark:border-neutral-400";
}

// Returnér { bg, border, text } for en planlagt vagt ud fra vagttype + status.
// erGodkendt/erAfvist udledes af kalderen (matchede stemplinger + flags).
function vagtBoksStil(v, erGodkendt, erAfvist) {
  const border = vagtRamme(v.vagttype);
  // Fraværs-typer (Syg/Ferie/Fri) beholder deres type-baggrund som i dag.
  if (FRAVAERS_TYPER.has(v.vagttype)) {
    const meta = VAGTTYPE_META[v.vagttype] || {};
    return { bg: meta.color || "", border, text: "text-foreground" };
  }
  // Øvrige: baggrund = godkendelses-status.
  let bg = "bg-card";                                   // neutral (afventer / ingen stempling)
  if (erAfvist) bg = "bg-red-500/15";                  // lyserød — afvist / ikke godkendt
  else if (erGodkendt) bg = "bg-green-500/15";         // lysegrøn — godkendt (uanset historik)
  return { bg, border, text: "text-foreground" };
}

// Returnér struktureret data for et clock_record.
// { tid: "HH:MM–HH:MM", adresse, vagttype, isAdhoc, clockInVia, clockInTid }
function formatStempling(r) {
  const fmtTid = (iso) => {
    if (!iso) return null;
    const m = String(iso).match(/T(\d{2}:\d{2})/);
    return m ? m[1] : null;
  };
  const fra = fmtTid(r.clock_in);
  const til = fmtTid(r.clock_out || r.effective_clock_in);
  const tid = fra && til ? `${fra}–${til}` : (fra ? `${fra}–…` : "");
  const adresse = r.clock_in_adresse || r.clock_in_lokation || "Ukendt lokation";
  const vagttype = r.vagttype || "";
  return {
    tid,
    adresse,
    vagttype,
    isAdhoc: !!r.er_adhoc,
    clockInVia: r.clock_in_via || "",
    clockInTid: fra || "",
  };
}

// Tekst-variant til tooltips (én linje, kompakt).
function formatStemplingTekst(r) {
  const s = formatStempling(r);
  const dele = [s.tid, s.vagttype, s.adresse].filter(Boolean);
  return dele.join(", ");
}

function isoFromDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return isoFromDate(d);
}

function mondayOfWeek(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return isoFromDate(d);
}

// ISO-ugenummer for en Date (torsdagen i ugen afgør årets uge-nr).
function isoUgeNr(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const thu = new Date(d); thu.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const jan1 = new Date(thu.getFullYear(), 0, 1);
  return Math.ceil(((thu - jan1) / 86400000 + 1) / 7);
}

function weekLabel(mondayIso) {
  const d = new Date(mondayIso + "T00:00:00");
  const thu = new Date(d); thu.setDate(d.getDate() + 3);
  // Vis månedsnavnet baseret på torsdagen i ugen (ISO standard)
  const mnd = MND_FULL[thu.getMonth()];
  return `Uge ${isoUgeNr(d)} · ${mnd.charAt(0).toUpperCase() + mnd.slice(1)} ${thu.getFullYear()}`;
}

// #38.2: kontrakttimer for en periode beregnet dag-for-dag ud fra gyldig-fra-
// historikken, så en ændring af timer pr. uge IKKE påvirker bagud. For hver dag
// i [fraIso, tilIso] bruges den sats der var gyldig den dag (timer_pr_uge / 7).
// historik: [{timer_pr_uge, gyldig_fra}]. nuvaerende = den aktuelle sats (bruges
// hvis ingen historik-entry endnu er gyldig, fx før første registrerede ændring).
function kontraktTimerForPeriode(fraIso, tilIso, historik, nuvaerende) {
  if (!fraIso || !tilIso) return null;
  const hist = (historik || [])
    .filter(h => h && h.gyldig_fra && (h.timer_pr_uge != null))
    .map(h => ({ fra: String(h.gyldig_fra).slice(0, 10), tpu: parseFloat(h.timer_pr_uge) }))
    .filter(h => !isNaN(h.tpu))
    .sort((a, b) => a.fra.localeCompare(b.fra));
  const nuv = (nuvaerende != null && !isNaN(parseFloat(nuvaerende))) ? parseFloat(nuvaerende) : null;
  // Ingen historik og ingen aktuel sats → ingen kontrakt.
  if (hist.length === 0 && nuv == null) return null;
  // Sats gældende for en given dato.
  const satsForDato = (iso) => {
    let gaeldende = null;
    for (const h of hist) { if (h.fra <= iso) gaeldende = h.tpu; else break; }
    // Før første historik-entry: brug aktuel sats som bedste bud (bagud-kompat
    // for medarbejdere der havde timer_pr_uge før historikken fandtes).
    if (gaeldende == null) gaeldende = nuv;
    return gaeldende;
  };
  let total = 0;
  const fra = new Date(fraIso + "T00:00:00");
  const til = new Date(tilIso + "T00:00:00");
  const cur = new Date(fra);
  while (cur <= til) {
    const iso = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,"0")}-${String(cur.getDate()).padStart(2,"0")}`;
    const s = satsForDato(iso);
    if (s != null) total += s / 7; // ugentlig sats fordelt pr. dag
    cur.setDate(cur.getDate() + 1);
  }
  return Math.round(total * 10) / 10;
}

// #44.1: +/- timer "I alt år-til-dato" beregnes nu autoritativt på backend
// (/admin/aarsaldo-alle) og læses i Timeoversigt, så tallet er identisk med
// Arbejdsplan-boksen inkl. manuelle måneds-overrides.

function formatDayHeader(iso) {
  const d = new Date(iso + "T00:00:00");
  const dow = UGEDAGE[(d.getDay() + 6) % 7];
  return `${dow} ${d.getDate()}/${d.getMonth()+1}`;
}

function formatLoen(kr) {
  return kr.toLocaleString("da-DK", { maximumFractionDigits: 0 }) + " kr";
}

// Søndag = getDay() === 0. Vises gult som helligdage.
function isSondag(iso) {
  try {
    return new Date(iso + "T00:00:00").getDay() === 0;
  } catch { return false; }
}

// Format timer_actual (decimal-timer) som "XtYm" — bruges i uge-grid under planlagte vagter.
function fmtTimerMin(t) {
  if (t == null) return "";
  const num = typeof t === "number" ? t : parseFloat(t);
  if (!isFinite(num)) return "";
  const h = Math.floor(num);
  const m = Math.round((num - h) * 60);
  if (h > 0 && m > 0) return `${h}t ${m}m`;
  if (h > 0) return `${h}t`;
  return `${m}m`;
}

// Træk HH:MM ud af en ISO clock-tid.
function fmtTidHHMM(iso) {
  if (!iso) return "";
  const m = String(iso).match(/T(\d{2}:\d{2})/);
  return m ? m[1] : "";
}

function isoUgenummer(isoStr) {
  const d = new Date(isoStr + "T00:00:00");
  const thu = new Date(d); thu.setDate(d.getDate() + (4 - (d.getDay() || 7)));
  const ystart = new Date(thu.getFullYear(), 0, 1);
  return Math.ceil(((thu - ystart) / 86400000 + 1) / 7);
}

function mondayToWeekValue(mondayIso) {
  const d = new Date(mondayIso + "T00:00:00");
  const wk = isoUgenummer(mondayIso);
  const thu = new Date(d); thu.setDate(d.getDate() + 3);
  return `${thu.getFullYear()}-W${String(wk).padStart(2, "0")}`;
}

// ── Medarbejder-dropdown med søgning ────────────────────────────────────────
function MedarbejderPicker({ value, onChange, medarbejdere, placeholder = "Vælg medarbejder" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const filtered = useMemo(() =>
    medarbejdere.filter(m => m.name.toLowerCase().includes(q.toLowerCase())),
    [medarbejdere, q]
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30 transition-colors"
      >
        <span className={value ? "text-foreground" : "text-muted-foreground"}>{value || placeholder}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 w-full bg-background border border-border rounded-2xl shadow-2xl z-50 overflow-hidden">
          <div className="p-2 border-b border-border">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-secondary">
              <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <input
                autoFocus
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Søg navn..."
                className="flex-1 bg-transparent text-sm focus:outline-none"
              />
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted-foreground">Ingen resultater</p>
            ) : filtered.map(m => (
              <button
                key={m.name}
                type="button"
                onClick={() => { onChange(m.name, m.city); setOpen(false); setQ(""); }}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors text-left hover:bg-secondary ${value === m.name ? "bg-[#FF2A6D]/5 text-[#FF2A6D]" : ""}`}
              >
                <span className="font-medium">{m.name}</span>
                <span className="text-[13px] text-muted-foreground">{m.city}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Vagt-modal ────────────────────────────────────────────────────────────────
// Lokationstekst til "Ret vagt"-popup. Superadmin ser fuld adresse
// (clock_in_adresse_fuld), øvrige ser den maskerede by/navn (clock_in_adresse).
function stemplingLokationTekst(r, felt, isSuperAdmin) {
  if (!r) return "";
  const fuld = r[`${felt}_fuld`];
  const masket = r[felt] || r[felt.replace("_adresse", "_lokation")];
  return ((isSuperAdmin ? (fuld || masket) : masket) || "").trim();
}

function VagtModal({ draft, setDraft, editVagt, onSave, onDelete, onKopier, onClose, saving, medarbejdere, stemplinger = [], isSuperAdmin = false, byOptions = null, onAnmodAendring = null, erSimplificeret = false, erMedarbejderMedVagtplanRet = false, onGodkendStempling = null, onAfvisStempling = null }) {
  const isHelligdag = DK_HELLIGDAGE.has(draft.dato);
  const helligdagNavn = DK_HELLIGDAGE_NAVNE[draft.dato];
  const isAndet = draft.vagttype === "Andet";
  // Krav #4: er denne vagt stemplet (godkendt eller afventer)? Så vises en
  // anden popup med adskilt PLANLAGT- og STEMPLET-sektion.
  const aktiveStempler = (stemplinger || []).filter(
    r => r.status !== "rejected" && r.ikke_godkendt !== true
  );
  const erStemplet = aktiveStempler.length > 0;

  const handleSaveWithConfirm = () => {
    if (isHelligdag) {
      const navn = helligdagNavn ? ` (${helligdagNavn})` : "";
      if (!window.confirm(`${draft.dato} er en helligdag${navn}. Er du sikker på at du vil oprette en vagt?`)) return;
    }
    onSave();
  };

  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-sm shadow-2xl space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">
            {editVagt ? (erStemplet ? "Ret vagt (stemplet)" : "Ret vagt") : "Ny vagt"}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          {/* Medarbejder */}
          <div>
            <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Medarbejder</label>
            <MedarbejderPicker
              value={draft.medarbejder}
              onChange={(name, city) => setDraft(p => ({ ...p, medarbejder: name, city: city || p.city }))}
              medarbejdere={medarbejdere}
            />
          </div>

          {/* Dato */}
          <div>
            <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Dato</label>
            <input
              type="date"
              value={draft.dato}
              onChange={e => setDraft(p => ({ ...p, dato: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
            />
            {isHelligdag && (
              <p className="text-[13px] text-amber-500 mt-1">
                Helligdag{helligdagNavn ? ` — ${helligdagNavn}` : ""} · Du vil blive bedt om bekræftelse
              </p>
            )}
          </div>

          {/* PLANLAGT-sektion (kun mærket når vagten er stemplet — Krav #4) */}
          {erStemplet && (
            <div className="flex items-center gap-2 pt-1">
              <span className="text-[11px] font-bold text-[#FF2A6D] uppercase tracking-widest">Planlagt</span>
              <div className="flex-1 h-px bg-border" />
            </div>
          )}

          {/* Vagttype */}
          <div>
            <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Vagttype</label>
            <div className="grid grid-cols-2 gap-1.5">
              {VAGTTYPER.map(vt => {
                const meta = VAGTTYPE_META[vt];
                const active = draft.vagttype === vt;
                return (
                  <button
                    key={vt || "normal"}
                    type="button"
                    onClick={() => setDraft(p => ({ ...p, vagttype: vt }))}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-[13px] font-semibold border transition-all text-left ${
                      active
                        ? "border-[#FF2A6D] bg-[#FF2A6D]/10 text-[#FF2A6D]"
                        : "border-border hover:border-[#FF2A6D]/40 hover:bg-secondary"
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full shrink-0 ${meta?.dot || "bg-[#FF2A6D]"}`} />
                    <span className="truncate">{meta?.label || "Normal"}</span>
                    {active && <Check className="w-3 h-3 ml-auto shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Andet — eget navn */}
          {isAndet && (
            <div>
              <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Navn på vagttype</label>
              <input
                value={draft.andet_navn || ""}
                onChange={e => setDraft(p => ({ ...p, andet_navn: e.target.value }))}
                placeholder="Fx kursus, møde, konference..."
                className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
              />
            </div>
          )}

          {/* Fra–Til (Arbejdstimer) */}
          <div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Fra</label>
                <input
                  type="time"
                  value={draft.fra}
                  onChange={e => setDraft(p => ({ ...p, fra: e.target.value }))}
                  readOnly={erMedarbejderMedVagtplanRet && erStemplet}
                  title={erMedarbejderMedVagtplanRet && erStemplet ? "Kan ikke ændres når der er stemplet ind/ud" : undefined}
                  className={`w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30${erMedarbejderMedVagtplanRet && erStemplet ? " opacity-50 cursor-not-allowed" : ""}`}
                />
              </div>
              <div className="flex-1">
                <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Til</label>
                <input
                  type="time"
                  value={draft.til}
                  onChange={e => setDraft(p => ({ ...p, til: e.target.value }))}
                  readOnly={erMedarbejderMedVagtplanRet && erStemplet}
                  title={erMedarbejderMedVagtplanRet && erStemplet ? "Kan ikke ændres når der er stemplet ind/ud" : undefined}
                  className={`w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30${erMedarbejderMedVagtplanRet && erStemplet ? " opacity-50 cursor-not-allowed" : ""}`}
                />
              </div>
            </div>
          </div>

          {/* By */}
          <div>
            <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Afdeling</label>
            <select
              value={draft.city}
              onChange={e => setDraft(p => ({ ...p, city: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
            >
              <option value="">Vælg afdeling</option>
              {(byOptions || ALLE_BYER.filter(b => b !== "Alle")).map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          {/* STEMPLET-sektion (Krav #4) — read-only info om de faktiske stemplinger.
              Adresse vises kun for superadmin; øvrige ser maskeret by/navn. */}
          {erStemplet && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 pt-1">
                <span className="text-[11px] font-bold text-green-600 uppercase tracking-widest">Stemplet</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              {aktiveStempler.map(r => {
                const indTid = fmtTidHHMM(r.clock_in);
                const udTid = fmtTidHHMM(r.clock_out);
                const totalStr = fmtTimerMin(r.timer_actual);
                const indLok = stemplingLokationTekst(r, "clock_in_adresse", isSuperAdmin);
                const udLok = stemplingLokationTekst(r, "clock_out_adresse", isSuperAdmin);
                const afventer = r.status === "completed";
                return (
                  <div key={r.clock_id} className="rounded-xl border border-green-500/30 bg-green-500/5 p-3 space-y-1.5 text-[13px]">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-muted-foreground">Stemplet ind</span>
                      <span className="text-right font-semibold">
                        {indTid || "?"}{indLok ? <span className="font-normal text-muted-foreground"> · {indLok}</span> : null}
                      </span>
                    </div>
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-muted-foreground">Stemplet ud</span>
                      <span className="text-right font-semibold">
                        {udTid || (afventer ? "—" : "åben")}{udLok ? <span className="font-normal text-muted-foreground"> · {udLok}</span> : null}
                      </span>
                    </div>
                    {totalStr && (
                      <div className="flex items-center justify-between gap-2 pt-1 border-t border-green-500/20">
                        <span className="text-muted-foreground">Total</span>
                        <span className="font-bold">{totalStr}</span>
                      </div>
                    )}
                    {afventer && !erSimplificeret && (
                      <div className="text-[12px] text-amber-600 font-semibold">Afventer godkendelse</div>
                    )}
                  </div>
                );
              })}
              {isSuperAdmin && stemplinger.some(s => s.status === "afventer") && (
                <div className="flex gap-2 mt-2 pt-2 border-t border-border">
                  <button
                    onClick={() => onGodkendStempling && onGodkendStempling(stemplinger.find(s => s.status === "afventer"))}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500 text-white text-[13px] font-semibold hover:opacity-90 transition-opacity"
                  >
                    <Check className="w-3.5 h-3.5" /> Godkend stempling
                  </button>
                  <button
                    onClick={() => onAfvisStempling && onAfvisStempling(stemplinger.find(s => s.status === "afventer"))}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/40 text-red-600 text-[13px] font-semibold hover:bg-red-500/10 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" /> Afvis stempling
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Note */}
          <div>
            <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Note (valgfrit)</label>
            <input
              value={draft.note}
              onChange={e => setDraft(p => ({ ...p, note: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
              placeholder="Fx møde, kursus..."
            />
          </div>

          {/* Opfølgning #60 (DEL 6): Anmod om ændring af vagten (Syg/Ferie/Fri/Barnets).
              Vises KUN for rene medarbejdere (erSimplificeret), ikke for admin eller medarbejdere med vagtplan-ret. */}
          {onAnmodAendring && erSimplificeret && !isSuperAdmin && !erMedarbejderMedVagtplanRet && (
            <button
              type="button"
              onClick={onAnmodAendring}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-amber-500/40 text-amber-600 hover:bg-amber-500/10 transition-colors"
            >
              <AlertCircle className="w-4 h-4" />
              Anmod om ændring
            </button>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          {/* Slet-knap: vises for isSuperAdmin ALTID, for erMedarbejderMedVagtplanRet kun når ingen stempling */}
          {editVagt && (isSuperAdmin || (erMedarbejderMedVagtplanRet && !erStemplet)) && (
            <button
              onClick={onDelete}
              className="px-4 py-2 rounded-xl text-sm border border-red-500/30 text-red-500 hover:bg-red-500/10 transition-colors"
            >
              Slet
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary transition-colors ml-auto"
          >
            Annuller
          </button>
          {/* Gem-knap: vises for isSuperAdmin og erMedarbejderMedVagtplanRet, SKJULT for erSimplificeret */}
          {(isSuperAdmin || erMedarbejderMedVagtplanRet) && (
            <button
              onClick={handleSaveWithConfirm}
              disabled={saving || !draft.dato || !draft.medarbejder}
              className="px-4 py-2 rounded-xl text-sm bg-[#FF2A6D] text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Gem
            </button>
          )}
        </div>
      </div>
    </ModalPortal>
  );
}

// ── Opfølgning #60 (DEL 6): "Anmod om ændring"-dialog ───────────────────────
// En medarbejder med vagtplan-adgang kan anmode om at en vagt konverteres til
// fravær (Syg/Ferie/Fri/Barnets) — enten hele vagten eller kun et tidsinterval.
// Sender POST /admin/vagt-aendring-anmodning. Godkendelse sker af en godkender
// under "Stempling til godkendelse"-fanen.
const ANMOD_TYPER = ["Syg", "Ferie/Fri", "Barnets første sygedag"];

function AnmodOmAendringModal({ vagt, hdrs, onClose, onSent }) {
  const [nyVagttype, setNyVagttype] = useState("Syg");
  const [omfang, setOmfang] = useState("hele"); // "hele" | "delvis"
  const [fraTid, setFraTid] = useState(vagt?.fra || "09:00");
  const [tilTid, setTilTid] = useState(vagt?.til || "17:00");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [fejl, setFejl] = useState("");

  const subLinje = [
    vagt?.dato,
    (vagt?.fra && vagt?.til) ? `${vagt.fra} - ${vagt.til}` : null,
    vagt?.city || null,
  ].filter(Boolean).join(" · ");

  const send = async () => {
    if (!vagt?.vagt_id) { setFejl("Denne vagt kan ikke ændres (mangler vagt-id)."); return; }
    if (omfang === "delvis" && (!fraTid || !tilTid)) { setFejl("Angiv både fra- og til-tidspunkt."); return; }
    setSending(true);
    setFejl("");
    try {
      await axios.post(
        `${API}/admin/vagt-aendring-anmodning`,
        {
          vagt_id: vagt.vagt_id,
          ny_vagttype: nyVagttype,
          omfang,
          fra_tid: omfang === "delvis" ? fraTid : undefined,
          til_tid: omfang === "delvis" ? tilTid : undefined,
          note: note.trim(),
        },
        { headers: hdrs }
      );
      onSent && onSent();
    } catch (e) {
      setFejl(e?.response?.data?.detail || "Kunne ikke sende anmodningen");
      setSending(false);
    }
  };

  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-sm shadow-2xl space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-bold text-base truncate">Anmod om ændring{vagt?.medarbejder ? ` – ${vagt.medarbejder}` : ""}</h3>
            {subLinje && <p className="text-[13px] text-muted-foreground mt-0.5">{subLinje}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Type */}
        <div>
          <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Type</label>
          <div className="grid grid-cols-2 gap-1.5">
            {ANMOD_TYPER.map(t => {
              const meta = VAGTTYPE_META[t] || {};
              const active = nyVagttype === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setNyVagttype(t)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl text-[13px] font-semibold border transition-all text-left ${
                    active
                      ? "border-[#FF2A6D] bg-[#FF2A6D]/10 text-[#FF2A6D]"
                      : "border-border hover:border-[#FF2A6D]/40 hover:bg-secondary"
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 ${meta?.dot || "bg-[#FF2A6D]"}`} />
                  <span className="truncate">{meta?.label || t}</span>
                  {active && <Check className="w-3 h-3 ml-auto shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Omfang */}
        <div>
          <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Omfang</label>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              { key: "hele", label: "Hele vagten" },
              { key: "delvis", label: "Kun fra/til tidspunkt" },
            ].map(o => {
              const active = omfang === o.key;
              return (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setOmfang(o.key)}
                  className={`px-3 py-2 rounded-xl text-[13px] font-semibold border transition-all text-left ${
                    active
                      ? "border-[#FF2A6D] bg-[#FF2A6D]/10 text-[#FF2A6D]"
                      : "border-border hover:border-[#FF2A6D]/40 hover:bg-secondary"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
          {omfang === "delvis" && (
            <div className="flex gap-2 mt-2">
              <div className="flex-1">
                <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Fra</label>
                <input type="time" value={fraTid} onChange={e => setFraTid(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
              <div className="flex-1">
                <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Til</label>
                <input type="time" value={tilTid} onChange={e => setTilTid(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
            </div>
          )}
        </div>

        {/* Note */}
        <div>
          <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Note (valgfri)</label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            placeholder="Fx årsag eller bemærkning..."
            className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30 resize-none"
          />
        </div>

        {fejl && <p className="text-[13px] text-red-500">{fejl}</p>}

        <div className="flex gap-2 pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary transition-colors ml-auto"
          >
            Annullér
          </button>
          <button
            onClick={send}
            disabled={sending}
            className="px-4 py-2 rounded-xl text-sm bg-[#FF2A6D] text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center gap-2"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send anmodning
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

// ── Konflikt-modal: vises ved overlap mellem ny og eksisterende vagt ─────────
function ConflictModal({ existing, newDraft, onResolve, saving }) {
  const fmtTidRange = (v) => v?.fra && v?.til ? `${v.fra}–${v.til}` : "(hele dagen)";
  const fmtDatoNice = (iso) => {
    try {
      const d = new Date(iso + "T00:00:00");
      return d.toLocaleDateString("da-DK", { day: "numeric", month: "long" });
    } catch { return iso; }
  };
  const existingCity = (existing?.city || "").trim();
  const newCity = (newDraft?.city || "").trim();
  const sameCity = existingCity && newCity && existingCity.toLowerCase() === newCity.toLowerCase();
  // Add er disabled hvis byerne er forskellige (overlap i forskellige byer)
  // ELLER hvis byerne er ens (samme by + overlap = også konflikt).
  // Praktisk talt: hvis vi er her, er der ALTID overlap → Add altid disabled.
  const addDisabled = true;
  const addReason = sameCity
    ? "Samme by + overlappende tid — kan ikke tilføjes som ekstra vagt"
    : "Forskellige byer + overlappende tid — kan ikke tilføjes som ekstra vagt";
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-md shadow-2xl space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" /> Vagt-konflikt
          </h3>
        </div>
        <p className="text-sm">
          <span className="font-semibold">{existing?.medarbejder}</span> har allerede en vagt
          {existingCity ? ` i ${existingCity}` : ""} {fmtDatoNice(existing?.dato)} kl. {fmtTidRange(existing)}.
          Hvad skal der ske med den nye vagt ({fmtTidRange(newDraft)}{newCity ? ` · ${newCity}` : ""})?
        </p>

        <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-2 text-[13px]">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="font-semibold">Eksisterende:</span>
            <span>{fmtTidRange(existing)}{existingCity ? ` · ${existingCity}` : ""}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#FF2A6D]" />
            <span className="font-semibold">Ny:</span>
            <span>{fmtTidRange(newDraft)}{newCity ? ` · ${newCity}` : ""}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2 pt-1">
          <button
            onClick={() => onResolve("replace")}
            disabled={saving}
            className="w-full px-4 py-2.5 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition-opacity flex items-center justify-center gap-2"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Erstat eksisterende
          </button>
          <button
            onClick={() => onResolve("add")}
            disabled={addDisabled || saving}
            title={addDisabled ? addReason : "Opret begge vagter"}
            className="w-full px-4 py-2.5 rounded-xl border border-border text-sm font-semibold hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Tilføj som ekstra vagt
          </button>
          {addDisabled && (
            <p className="text-[12px] text-amber-600 -mt-1">{addReason}</p>
          )}
          <button
            onClick={() => onResolve("cancel")}
            disabled={saving}
            className="w-full px-4 py-2 rounded-xl text-sm text-muted-foreground hover:bg-secondary transition-colors"
          >
            Annullér
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Vagt-kontekstmenu (højreklik) ─────────────────────────────────────────────
function VagtKontekstMenu({ vagt, x, y, onClose, onAction }) {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  // Klem position så menuen ikke falder uden for viewport
  const MENU_W = 200;
  const MENU_H = 280;
  const left = typeof window !== "undefined" ? Math.min(x, window.innerWidth - MENU_W - 8) : x;
  const top  = typeof window !== "undefined" ? Math.min(y, window.innerHeight - MENU_H - 8) : y;

  const items = [
    { key: "rediger", label: "Rediger",            cls: "" },
    { key: "kopier",  label: "Kopier",             cls: "" },
    { key: "syg",     label: "Markér som syg",       cls: "text-red-500" },
    { key: "ferie",   label: "Markér som ferie/fri", cls: "text-emerald-600" },
    { key: "slet",    label: "Slet",                 cls: "text-red-500 border-t border-border mt-1 pt-2" },
  ];

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-card border border-border rounded-xl shadow-2xl py-1 min-w-[180px]"
      style={{ left, top }}
      onContextMenu={e => e.preventDefault()}
    >
      <div className="px-3 py-1.5 text-[13px] text-muted-foreground uppercase tracking-wide border-b border-border mb-1 truncate">
        {vagt?.medarbejder || "Vagt"} · {vagt?.dato || ""}
      </div>
      {items.map(it => (
        <button
          key={it.key}
          onClick={(e) => { e.stopPropagation(); onAction(it.key, vagt); }}
          className={`w-full text-left px-3 py-1.5 text-[13px] font-medium hover:bg-secondary transition-colors ${it.cls}`}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

// ── Ferie-modal ───────────────────────────────────────────────────────────────
function FerieModal({ medarbejdere, onClose, onCreated, hdrs }) {
  const todayIso = isoFromDate(new Date());
  const [medarbejder, setMedarbejder] = useState("");
  const [fraDato, setFraDato] = useState(todayIso);
  const [tilDato, setTilDato] = useState(todayIso);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  // Konflikt: vises når backend returnerer 409 ved overlap med eksisterende periode
  const [conflict, setConflict] = useState(null); // { existing, kan_udvide_til }

  const datoFejl = fraDato && tilDato && tilDato < fraDato;
  const kanGemme = !!medarbejder && !!fraDato && !!tilDato && !datoFejl && !saving;

  const postFerie = (action) => {
    const qs = action ? `?action=${encodeURIComponent(action)}` : "";
    return axios.post(
      `${API}/hub/vagtplan/ferie${qs}`,
      { medarbejder, fra_dato: fraDato, til_dato: tilDato, note },
      { headers: hdrs }
    );
  };

  const handleSubmit = async () => {
    if (!kanGemme) return;
    setSaving(true);
    setResult(null);
    setConflict(null);
    try {
      const r = await postFerie();
      setResult({ ok: true, ...r.data });
      await onCreated();
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.detail?.conflict) {
        const det = e.response.data.detail;
        setConflict({ existing: det.existing, kan_udvide_til: det.kan_udvide_til });
      } else {
        const det = e?.response?.data?.detail;
        const msg = typeof det === "string" ? det : (det?.message || "Fejl ved oprettelse");
        setResult({ ok: false, error: msg });
      }
    }
    setSaving(false);
  };

  const handleUdvid = async () => {
    setSaving(true);
    try {
      const r = await postFerie("udvid");
      setConflict(null);
      setResult({ ok: true, ...r.data, udvidet: true });
      await onCreated();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = typeof det === "string" ? det : (det?.message || "Fejl ved udvidelse");
      setConflict(null);
      setResult({ ok: false, error: msg });
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-sm shadow-2xl space-y-3 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">Opret ferie</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-[13px] text-muted-foreground">
          Konverterer eksisterende vagter i intervallet til ferie. Helligdage og dage uden vagter springes over.
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Medarbejder</label>
            <MedarbejderPicker
              value={medarbejder}
              onChange={(name) => setMedarbejder(name)}
              medarbejdere={medarbejdere}
            />
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Fra</label>
              <input
                type="date"
                value={fraDato}
                onChange={e => setFraDato(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
              />
            </div>
            <div className="flex-1">
              <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Til</label>
              <input
                type="date"
                value={tilDato}
                onChange={e => setTilDato(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
              />
            </div>
          </div>
          {datoFejl && (
            <p className="text-[13px] text-red-500">Til-dato skal være efter eller samme som fra-dato.</p>
          )}

          <div>
            <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Note (valgfrit)</label>
            <input
              value={note}
              onChange={e => setNote(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
              placeholder="Fx sommerferie, vinterferie..."
            />
          </div>

          {result && result.ok && (
            <div className="text-[13px] px-3 py-2 rounded-xl bg-emerald-500/10 text-emerald-600 border border-emerald-500/30">
              {result.udvidet ? (
                <>Ferie udvidet — perioden dækker nu {result.arbejdsdage} arbejdsdag{result.arbejdsdage === 1 ? "" : "e"}.</>
              ) : (
                <>Ferie oprettet — {result.converted} arbejdsdag{result.converted === 1 ? "" : "e"} er konverteret til Ferie i perioden {fraDato}–{tilDato}.</>
              )}
              {result.skipped > 0 && <> ({result.skipped} dag{result.skipped === 1 ? "" : "e"} uden vagt/helligdag sprunget over.)</>}
            </div>
          )}
          {result && !result.ok && (
            <div className="text-[13px] px-3 py-2 rounded-xl bg-red-500/10 text-red-600 border border-red-500/30">
              {result.error}
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary transition-colors ml-auto"
          >
            {result && result.ok ? "Luk" : "Annuller"}
          </button>
          {!(result && result.ok) && (
            <button
              onClick={handleSubmit}
              disabled={!kanGemme}
              className="px-4 py-2 rounded-xl text-sm bg-[#FF2A6D] text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Opret ferie
            </button>
          )}
        </div>
      </div>

      {/* Konflikt-popup: overlapping ferie eksisterer allerede */}
      {conflict && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-md shadow-2xl space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h3 className="font-bold text-base">Ferie overlapper eksisterende ferie</h3>
            </div>
            <p className="text-sm leading-relaxed">
              <span className="font-semibold">{conflict.existing?.medarbejder || medarbejder}</span> har
              allerede registreret ferie fra <span className="font-mono">{conflict.existing?.fra_dato}</span>–
              <span className="font-mono">{conflict.existing?.til_dato}</span>
              {typeof conflict.existing?.antal_arbejdsdage === "number" && (
                <> ({conflict.existing.antal_arbejdsdage} dage)</>
              )}.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleUdvid}
                disabled={saving}
                className="w-full px-4 py-2.5 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition-opacity flex items-center justify-center gap-2"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Udvid den eksisterende ferie til {conflict.kan_udvide_til?.fra}–{conflict.kan_udvide_til?.til}
              </button>
              <button
                onClick={() => setConflict(null)}
                disabled={saving}
                className="w-full px-4 py-2 rounded-xl border border-border text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-40"
              >
                Annullér ny oprettelse
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Ret/slet ferie-periode ──────────────────────────────────────────────────
// Modal til redigering eller sletning af en eksisterende ferie-periode.
// Kalder PUT/DELETE /hub/ferie-perioder/{periode_id}.
function RetFerieModal({ periode, onClose, onSaved, hdrs }) {
  const [fraDato, setFraDato] = useState(periode?.fra_dato || "");
  const [tilDato, setTilDato] = useState(periode?.til_dato || "");
  const [note, setNote] = useState(periode?.note || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Beregn arbejdsdage live (man-fre, ikke helligdag)
  const liveArbejdsdage = useMemo(() => {
    if (!fraDato || !tilDato || tilDato < fraDato) return 0;
    let n = 0;
    const start = new Date(fraDato + "T00:00:00");
    const end = new Date(tilDato + "T00:00:00");
    const cur = new Date(start);
    while (cur <= end) {
      const iso = isoFromDate(cur);
      const dow = cur.getDay();
      if (dow >= 1 && dow <= 5 && !DK_HELLIGDAGE.has(iso)) n++;
      cur.setDate(cur.getDate() + 1);
    }
    return n;
  }, [fraDato, tilDato]);

  const datoFejl = fraDato && tilDato && tilDato < fraDato;
  const kanGemme = !!fraDato && !!tilDato && !datoFejl && !saving;

  const handleGem = async () => {
    if (!kanGemme) return;
    setSaving(true);
    setError(null);
    try {
      await axios.put(
        `${API}/hub/ferie-perioder/${periode.periode_id || periode._id || periode.id}`,
        { fra_dato: fraDato, til_dato: tilDato, note },
        { headers: hdrs }
      );
      await onSaved();
      onClose();
    } catch (e) {
      const det = e?.response?.data?.detail;
      setError(typeof det === "string" ? det : (det?.message || "Fejl ved opdatering"));
    }
    setSaving(false);
  };

  const handleSlet = async () => {
    if (!window.confirm(`Slet ferie for ${periode?.medarbejder} (${periode?.fra_dato}–${periode?.til_dato})?\n\nFeriedagene tilbageføres til feriebanken.`)) return;
    setSaving(true);
    setError(null);
    try {
      await axios.delete(
        `${API}/hub/ferie-perioder/${periode.periode_id || periode._id || periode.id}`,
        { headers: hdrs }
      );
      await onSaved();
      onClose();
    } catch (e) {
      const det = e?.response?.data?.detail;
      setError(typeof det === "string" ? det : (det?.message || "Fejl ved sletning"));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-sm shadow-2xl space-y-3 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">Ret ferie</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Medarbejder</label>
            <div className="px-3 py-2 rounded-xl border border-border bg-secondary/30 text-sm font-medium">
              {periode?.medarbejder || "—"}
            </div>
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Fra</label>
              <input
                type="date"
                value={fraDato}
                onChange={e => setFraDato(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
              />
            </div>
            <div className="flex-1">
              <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Til</label>
              <input
                type="date"
                value={tilDato}
                onChange={e => setTilDato(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
              />
            </div>
          </div>
          {datoFejl && (
            <p className="text-[13px] text-red-500">Til-dato skal være efter eller samme som fra-dato.</p>
          )}

          <div>
            <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Note (valgfrit)</label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30 resize-none"
              placeholder="Fx sommerferie, vinterferie..."
            />
          </div>

          <div className="text-[13px] px-3 py-2 rounded-xl bg-secondary/30 border border-border flex items-center justify-between">
            <span className="text-muted-foreground">Faktiske arbejdsdage</span>
            <span className="font-bold text-foreground">{liveArbejdsdage} dag{liveArbejdsdage === 1 ? "" : "e"}</span>
          </div>

          {error && (
            <div className="text-[13px] px-3 py-2 rounded-xl bg-red-500/10 text-red-600 border border-red-500/30">
              {error}
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-1 items-center">
          <button
            onClick={handleSlet}
            disabled={saving}
            className="px-3 py-2 rounded-xl text-sm bg-red-500 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" /> Slet ferie
          </button>
          <button
            onClick={onClose}
            disabled={saving}
            className="ml-auto px-3 py-2 rounded-xl text-sm border border-border hover:bg-secondary transition-colors disabled:opacity-40"
          >
            Annullér
          </button>
          <button
            onClick={handleGem}
            disabled={!kanGemme}
            className="px-4 py-2 rounded-xl text-sm bg-[#FF2A6D] text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center gap-2"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Gem ændringer
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Kopier modal ──────────────────────────────────────────────────────────────
function KopierModal({ mode, view, currentMonday, activeMaaned, medarbejdere, onClose, onKopier, hdrs, loadVagter }) {
  const [fraMandag, setFraMandag] = useState(currentMonday);
  const [tilMandag, setTilMandag] = useState(() => addDays(currentMonday, 7));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  // Enkelt-mode
  const [enkKildeMed, setEnkKildeMed] = useState("");
  const [enkKildeDato, setEnkKildeDato] = useState("");
  const [enkMaalDato, setEnkMaalDato] = useState("");
  const [enkMaalMed, setEnkMaalMed] = useState("");
  // Medarb-mode
  const [marbKildeMed, setMarbKildeMed] = useState("");
  const [marbFraDato, setMarbFraDato] = useState("");
  const [marbTilDato, setMarbTilDato] = useState("");
  const [marbMaalMed, setMarbMaalMed] = useState("");
  const [marbDagOffset, setMarbDagOffset] = useState(0);
  const isUgeMaaned = mode === "uge_eller_maaned";
  const isEnkelt = mode === "enkelt";
  const isMedarb = mode === "medarb";
  const isUge = view === "uge";

  const mndLabel = useMemo(() => {
    if (!activeMaaned) return "";
    return `${MND_FULL[activeMaaned.month]} ${activeMaaned.year}`;
  }, [activeMaaned]);

  const nextMndLabel = useMemo(() => {
    if (!activeMaaned) return "";
    const nextMonth = activeMaaned.month === 11 ? 0 : activeMaaned.month + 1;
    const nextYear = activeMaaned.month === 11 ? activeMaaned.year + 1 : activeMaaned.year;
    return `${MND_FULL[nextMonth]} ${nextYear}`;
  }, [activeMaaned]);

  const handleKopier = async () => {
    setLoading(true);
    setResult(null);
    if (isUgeMaaned) {
      const res = await onKopier(isUge ? { fraMandag, tilMandag } : null);
      setResult(res);
    } else if (isEnkelt) {
      try {
        const r = await axios.post(`${API}/hub/vagtplan/kopier-enkelt`, {
          kilde_medarbejder: enkKildeMed,
          kilde_dato: enkKildeDato,
          maal_dato: enkMaalDato,
          maal_medarbejder: enkMaalMed || enkKildeMed,
        }, { headers: hdrs });
        await loadVagter();
        setResult({ inserted: r.data.inserted, skipped: r.data.skipped });
      } catch (e) {
        setResult({ error: e?.response?.data?.detail || "Fejl ved kopiering" });
      }
    } else if (isMedarb) {
      try {
        const r = await axios.post(`${API}/hub/vagtplan/kopier-medarb`, {
          kilde_medarbejder: marbKildeMed,
          fra_dato: marbFraDato,
          til_dato: marbTilDato,
          maal_medarbejder: marbMaalMed,
          dag_offset: marbDagOffset,
        }, { headers: hdrs });
        await loadVagter();
        setResult({ inserted: r.data.inserted, skipped: r.data.skipped });
      } catch (e) {
        setResult({ error: e?.response?.data?.detail || "Fejl ved kopiering" });
      }
    }
    setLoading(false);
  };

  const title = isUgeMaaned ? (isUge ? "Kopier uge" : "Kopier måned") : isEnkelt ? "Kopier enkelt vagt" : "Kopier medarbejders plan";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-sm shadow-2xl space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3">
          {isUgeMaaned && (
            <>
              {isUge ? (
                <>
                  <div>
                    <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Kilde — uge der kopieres fra</label>
                    <input type="date" value={fraMandag} onChange={e => setFraMandag(mondayOfWeek(e.target.value))}
                      className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
                    <p className="text-[13px] text-muted-foreground mt-1">{weekLabel(fraMandag)}</p>
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Mål — uge der kopieres til</label>
                    <input type="date" value={tilMandag} onChange={e => setTilMandag(mondayOfWeek(e.target.value))}
                      className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
                    <p className="text-[13px] text-muted-foreground mt-1">{weekLabel(tilMandag)}</p>
                  </div>
                </>
              ) : (
                <div className="rounded-xl bg-secondary/40 p-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Fra måned</span><span className="font-semibold capitalize">{mndLabel}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Kopieres til</span><span className="font-semibold capitalize">{nextMndLabel}</span></div>
                </div>
              )}
              <p className="text-[13px] text-muted-foreground">Helligdage og allerede eksisterende vagter springes over.</p>
            </>
          )}

          {isEnkelt && (
            <>
              <div>
                <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Kilde medarbejder</label>
                <MedarbejderPicker value={enkKildeMed} onChange={(name) => setEnkKildeMed(name)} medarbejdere={medarbejdere} />
              </div>
              <div>
                <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Kilde dato</label>
                <input type="date" value={enkKildeDato} onChange={e => setEnkKildeDato(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
              <div>
                <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Mål dato</label>
                <input type="date" value={enkMaalDato} onChange={e => setEnkMaalDato(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
              <div>
                <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Mål medarbejder (valgfrit — blank = samme)</label>
                <MedarbejderPicker value={enkMaalMed} onChange={(name) => setEnkMaalMed(name)} medarbejdere={medarbejdere} placeholder="Samme som kilde" />
              </div>
            </>
          )}

          {isMedarb && (
            <>
              <div>
                <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Kilde medarbejder</label>
                <MedarbejderPicker value={marbKildeMed} onChange={(name) => setMarbKildeMed(name)} medarbejdere={medarbejdere} />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Fra dato</label>
                  <input type="date" value={marbFraDato} onChange={e => setMarbFraDato(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
                </div>
                <div className="flex-1">
                  <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Til dato</label>
                  <input type="date" value={marbTilDato} onChange={e => setMarbTilDato(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
                </div>
              </div>
              <div>
                <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Mål medarbejder</label>
                <MedarbejderPicker value={marbMaalMed} onChange={(name) => setMarbMaalMed(name)} medarbejdere={medarbejdere} />
              </div>
              <div>
                <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Dag-offset (dage at rykke frem)</label>
                <input type="number" value={marbDagOffset} onChange={e => setMarbDagOffset(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
            </>
          )}
        </div>

        {result && (
          <div className={`rounded-xl px-4 py-3 text-sm font-semibold ${result.error ? "bg-red-500/10 text-red-500" : "bg-emerald-500/10 text-emerald-600"}`}>
            {result.error ? result.error : `${result.inserted} vagter kopieret, ${result.skipped} sprunget over`}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary transition-colors">
            {result ? "Luk" : "Annuller"}
          </button>
          {!result && (
            <button onClick={handleKopier} disabled={loading}
              className="px-4 py-2 rounded-xl text-sm bg-[#FF2A6D] text-white font-semibold hover:opacity-90 disabled:opacity-40 flex items-center gap-2">
              {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Kopier
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Eksport modal ─────────────────────────────────────────────────────────────
function ExportModal({ vagter, activeMaaned, currentMonday, view, onClose, token, isSuperAdmin, cityFilter, fetchRange }) {
  const [excelBusy, setExcelBusy] = useState(false);

  // Opfølgning #31: fuld Excel-eksport via backend (Podio-ID, stempel-tider,
  // faktiske timer, lokation, godkendelses-status, note, auto/ad-hoc). Kun superadmin.
  const handleExcel = async () => {
    setExcelBusy(true);
    try {
      const fra = (periodeMode === "custom" && customFra) ? customFra : fetchRange?.fra;
      const til = (periodeMode === "custom" && customTil) ? customTil : fetchRange?.til;
      const params = new URLSearchParams();
      if (fra) params.set("fra_dato", fra);
      if (til) params.set("til_dato", til);
      if (cityFilter && cityFilter !== "Alle") params.set("city", cityFilter);
      const res = await axios.get(`${API}/hub/vagtplan/eksport/excel?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `vagtplan-${fra || "alle"}_${til || ""}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch (err) {
      alert("Kunne ikke eksportere til Excel: " + (err.response?.data?.detail || err.message));
    } finally {
      setExcelBusy(false);
    }
  };

  const [cols, setCols] = useState({
    navn: true, dato: true, vagttype: true, fraTil: true, timer: true, city: true, note: false,
  });
  const [periodeMode, setPeriodeMode] = useState("aktuel"); // "aktuel" | "custom"
  const [customFra, setCustomFra] = useState("");
  const [customTil, setCustomTil] = useState("");
  const [inclGodkendt, setInclGodkendt] = useState(true);
  const [inclIkkeGodkendt, setInclIkkeGodkendt] = useState(false);

  const toggleCol = (k) => setCols(p => ({ ...p, [k]: !p[k] }));

  const handleExport = () => {
    let filtered = vagter;
    if (periodeMode === "custom" && customFra && customTil) {
      filtered = filtered.filter(v => v.dato >= customFra && v.dato <= customTil);
    }
    // Filtrér efter godkendt/ikke-godkendt
    filtered = filtered.filter(v => {
      const erIkkeOk = !!v.ikke_godkendt;
      if (erIkkeOk) return inclIkkeGodkendt;
      return inclGodkendt;
    });

    const headers = [];
    if (cols.navn) headers.push("Navn");
    if (cols.dato) headers.push("Dato");
    if (cols.vagttype) headers.push("Vagttype");
    if (cols.fraTil) headers.push("Fra;Til");
    if (cols.timer) headers.push("Timer");
    if (cols.city) headers.push("Afdeling");
    if (cols.note) headers.push("Note");

    const rows = filtered.sort((a, b) => a.dato.localeCompare(b.dato) || a.medarbejder.localeCompare(b.medarbejder)).map(v => {
      const parts = [];
      if (cols.navn) parts.push(v.medarbejder || "");
      if (cols.dato) parts.push(v.dato || "");
      if (cols.vagttype) parts.push(VAGTTYPE_META[v.vagttype]?.label || "Normal arbejdsdag");
      if (cols.fraTil) parts.push(`${v.fra || ""};${v.til || ""}`);
      if (cols.timer) parts.push(v.timer != null ? String(v.timer) : "");
      if (cols.city) parts.push(v.city || "");
      if (cols.note) parts.push(v.note || "");
      return parts.join(";");
    });

    // Flatten headers (fraTil er allerede to kolonner)
    const headerRow = headers.join(";").replace("Fra;Til", "Fra;Til");
    const csv = [headerRow, ...rows].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vagtplan-eksport-${isoFromDate(new Date())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-sm shadow-2xl space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">Eksportér vagtplan</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-2">
          <p className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Kolonner</p>
          {[
            { key: "navn", label: "Navn" },
            { key: "dato", label: "Dato" },
            { key: "vagttype", label: "Vagttype" },
            { key: "fraTil", label: "Fra/Til tid" },
            { key: "timer", label: "Timer" },
            { key: "city", label: "Afdeling/By" },
            { key: "note", label: "Note" },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
              <input
                type="checkbox"
                checked={cols[key]}
                onChange={() => toggleCol(key)}
                className="w-4 h-4 rounded accent-[#FF2A6D]"
              />
              {label}
            </label>
          ))}
        </div>

        <div className="space-y-2">
          <p className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Inkludér status</p>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={inclGodkendt} onChange={e => setInclGodkendt(e.target.checked)} className="w-4 h-4 rounded accent-[#FF2A6D]" />
            Inkludér godkendte vagter
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={inclIkkeGodkendt} onChange={e => setInclIkkeGodkendt(e.target.checked)} className="w-4 h-4 rounded accent-[#FF2A6D]" />
            Inkludér ikke-godkendte vagter
          </label>
        </div>

        <div className="space-y-2">
          <p className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Periode</p>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="radio" checked={periodeMode === "aktuel"} onChange={() => setPeriodeMode("aktuel")} className="accent-[#FF2A6D]" />
            Aktuel visning ({vagter.length} vagter)
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="radio" checked={periodeMode === "custom"} onChange={() => setPeriodeMode("custom")} className="accent-[#FF2A6D]" />
            Tilpasset periode
          </label>
          {periodeMode === "custom" && (
            <div className="flex gap-2 pt-1">
              <div className="flex-1">
                <label className="block text-[13px] text-muted-foreground mb-1">Fra</label>
                <input type="date" value={customFra} onChange={e => setCustomFra(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
              <div className="flex-1">
                <label className="block text-[13px] text-muted-foreground mb-1">Til</label>
                <input type="date" value={customTil} onChange={e => setCustomTil(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
            </div>
          )}
        </div>

        {isSuperAdmin && (
          <p className="text-[12px] text-muted-foreground">
            Excel-eksporten indeholder alle kolonner (Podio-ID, stempel-tider, faktiske timer,
            lokation/adresse, godkendelses-status, note, auto-udstemplet, ad-hoc) til løn/audit.
          </p>
        )}

        <div className="flex gap-2 justify-end pt-1 flex-wrap">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary transition-colors">
            Annuller
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary transition-colors flex items-center gap-2"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> CSV (valgte kolonner)
          </button>
          {isSuperAdmin && (
            <button
              onClick={handleExcel}
              disabled={excelBusy}
              className="px-4 py-2 rounded-xl text-sm bg-[#FF2A6D] text-white font-semibold hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-50"
            >
              {excelBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
              Eksportér Excel (fuld)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Grid view (uge: medarbejder × dag) ──────────────────────────────────────
function UgeGrid({ days, vagter, clockRecords = [], medarbejdere, canManage, onCellClick, onVagtClick, cityFilter, showLoen, loenSats, skjulte, onToggleSkjul, selectMode, selectedVagter, onKontekstAction, erSimplificeret = false, onMarkerVagt = null }) {
  const [contextMenu, setContextMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortCol, setSortCol] = useState("navn"); // "navn" | "timer"
  const [sortDir, setSortDir] = useState("asc");
  // Medarbejdere der har vagter ELLER stemplinger denne uge + alle fra listen
  const rows = useMemo(() => {
    const datoSet = new Set(days.map(d => d.iso));
    const ugeMedarb = new Set(vagter.filter(v => datoSet.has(v.dato)).map(v => v.medarbejder));
    clockRecords.forEach(r => { if (datoSet.has(r.dato)) ugeMedarb.add(r.medarbejder); });
    const all = new Set([...medarbejdere.map(m => m.name), ...ugeMedarb]);
    return [...all].sort();
  }, [days, vagter, clockRecords, medarbejdere]);

  const vagtMap = useMemo(() => {
    const m = {};
    vagter.forEach(v => {
      const key = `${v.medarbejder}|${v.dato}`;
      if (!m[key]) m[key] = [];
      m[key].push(v);
    });
    return m;
  }, [vagter]);

  const clockMap = useMemo(() => {
    const m = {};
    clockRecords.forEach(r => {
      const key = `${r.medarbejder}|${r.dato}`;
      if (!m[key]) m[key] = [];
      m[key].push(r);
    });
    // Sortér efter clock_in
    Object.values(m).forEach(arr => arr.sort((a, b) => (a.clock_in || "").localeCompare(b.clock_in || "")));
    return m;
  }, [clockRecords]);

  // Daglig timer-sum per kolonne (til header) — filtreret på cityFilter.
  // Opf. #36: ingen dobbeltælling. Pr. medarbejder pr. dag tæller vi ÉN gang
  // via dagTimer() (stempling overtager planlagt vagt).
  const dagTimerMap = useMemo(() => {
    const m = {};
    const filterCities = (cityFilter && cityFilter !== "Alle" && cityFilter !== "Læger")
      ? cityFilter.split(",") : null;
    days.forEach(d => {
      const medSet = new Set();
      // Grupér dagens (city-filtrerede) vagter og stemplinger pr. medarbejder.
      const vPerMed = {};
      const rPerMed = {};
      vagter.forEach(v => {
        if (v.dato !== d.iso) return;
        if (filterCities && !filterCities.includes(v.city)) return;
        const n = v.medarbejder || "";
        (vPerMed[n] = vPerMed[n] || []).push(v);
        if (n) medSet.add(n);
      });
      clockRecords.forEach(r => {
        if (r.dato !== d.iso) return;
        if (filterCities && !filterCities.includes(r.by || "")) return;
        const n = r.medarbejder || "";
        (rPerMed[n] = rPerMed[n] || []).push(r);
        if (n) medSet.add(n);
      });
      let timer = 0;
      medSet.forEach(n => { timer += dagTimer(vPerMed[n] || [], rPerMed[n] || []); });
      m[d.iso] = { timer, medCount: medSet.size };
    });
    return m;
  }, [days, vagter, clockRecords, cityFilter]);

  // Daglig lønsum — kun når showLoen. Opf. #36: samme dagTimer-regel som
  // dagTimerMap (stempling overtager planlagt vagt), ingen dobbeltælling.
  const dagLoenMap = useMemo(() => {
    if (!showLoen) return {};
    const m = {};
    days.forEach(d => {
      let timer = 0;
      rows.forEach(name => {
        timer += dagTimer(vagtMap[`${name}|${d.iso}`] || [], clockMap[`${name}|${d.iso}`] || []);
      });
      m[d.iso] = timer;
    });
    return m;
  }, [showLoen, days, rows, vagtMap, clockMap]);

  // Beregn ugetimer pr. medarbejder (til sortering + display)
  // VIGTIGT: alle hooks SKAL kaldes før early-return (React rules-of-hooks)
  const rowTimerMap = useMemo(() => {
    const m = {};
    rows.forEach(name => {
      let t = 0;
      days.forEach(d => {
        // Opf. #36: én timer-værdi pr. dag (stempling overtager planlagt vagt).
        t += dagTimer(vagtMap[`${name}|${d.iso}`] || [], clockMap[`${name}|${d.iso}`] || []);
      });
      m[name] = t;
    });
    return m;
  }, [rows, days, vagtMap, clockMap]);

  const visibleRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = q ? rows.filter(n => n.toLowerCase().includes(q)) : [...rows];
    list.sort((a, b) => {
      if (sortCol === "navn") {
        return sortDir === "asc" ? a.localeCompare(b) : b.localeCompare(a);
      }
      const av = rowTimerMap[a] || 0;
      const bv = rowTimerMap[b] || 0;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return list;
  }, [rows, searchQuery, sortCol, sortDir, rowTimerMap]);

  if (rows.length === 0) return (
    <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
      <Users className="w-10 h-10 mb-3 opacity-30" />
      <p className="text-sm">Ingen medarbejdere eller vagter denne uge</p>
    </div>
  );

  const handleKontekst = (action, vagt) => {
    setContextMenu(null);
    if (action === "rediger") {
      onVagtClick(vagt);
      return;
    }
    if (onKontekstAction) onKontekstAction(action, vagt);
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronDown className="w-3 h-3 opacity-30 inline-block" />;
    return <ChevronDown className={`w-3 h-3 inline-block ${sortDir === "asc" ? "rotate-180" : ""}`} />;
  };
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir(col === "navn" ? "asc" : "desc"); }
  };

  return (
    <>
    {/* Søgefelt */}
    <div className="mb-3 flex items-center gap-2">
      <div className="relative flex-1 max-w-[280px]">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <input
          type="search"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Søg medarbejder…"
          className="w-full pl-8 pr-3 py-1.5 rounded-xl border border-border bg-background text-[13px] focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
        />
      </div>
      {searchQuery && (
        <span className="text-[13px] text-muted-foreground">
          {visibleRows.length}/{rows.length}
        </span>
      )}
    </div>
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full min-w-[1200px] text-[13px] border-collapse">
        <thead>
          <tr className="bg-secondary/50 border-b border-border">
            <th
              onClick={() => toggleSort("navn")}
              className="text-left px-3 py-2.5 font-semibold text-muted-foreground w-44 sticky left-0 bg-secondary/50 z-30 cursor-pointer hover:text-foreground select-none"
            >
              <span className="inline-flex items-center gap-1">Medarbejder <SortIcon col="navn" /></span>
            </th>
            {days.map((d) => {
              const iso = d.iso;
              const dt = new Date(iso+"T00:00:00");
              const isToday = iso === isoFromDate(new Date());
              const sondag = isSondag(iso);
              const hellig = DK_HELLIGDAGE.has(iso);
              const helligNavn = DK_HELLIGDAGE_NAVNE[iso];
              return (
                <th
                  key={iso}
                  title={hellig ? (helligNavn || "Helligdag") : undefined}
                  className={`px-1 py-2.5 text-center font-semibold min-w-[150px] ${
                    isToday ? "bg-[#FF2A6D]/[0.035]" : (hellig || sondag) ? "bg-amber-500/5" : d.isWeekend ? "bg-secondary/20" : ""
                  } ${
                    (hellig || sondag) ? "text-amber-500" : d.isWeekend ? "text-muted-foreground/60" : "text-muted-foreground"
                  }`}
                >
                  <span className="block text-[13px]">{UGEDAGE[(dt.getDay()+6)%7]}</span>
                  <span className={`block text-base font-bold mt-0.5 leading-none ${isToday ? "text-[#FF2A6D]" : ""}`}>
                    {dt.getDate()}
                    {hellig && (
                      <span className="inline-block w-1 h-1 rounded-full bg-amber-500 ml-0.5 align-top" />
                    )}
                  </span>
                </th>
              );
            })}
            {!erSimplificeret && (
              <th
                onClick={() => toggleSort("timer")}
                className="px-2 py-2.5 text-right font-semibold text-muted-foreground min-w-[64px] cursor-pointer hover:text-foreground select-none"
              >
                <span className="inline-flex items-center gap-1">Timer <SortIcon col="timer" /></span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((name, ri) => {
            // Opf. #36: uge-total uden dobbeltælling (stempling overtager planlagt).
            const ugeTimer = days.reduce((s, d) =>
              s + dagTimer(vagtMap[`${name}|${d.iso}`] || [], clockMap[`${name}|${d.iso}`] || []), 0);
            const erSkjult = skjulte?.has(name);
            return (
            <tr key={name} className="group border-b border-border last:border-0">
              <td
                style={{ position: "sticky", left: 0, zIndex: 30, backgroundColor: ri % 2 === 0 ? "#ffffff" : "#ebebed" }}
                className="px-3 py-2 font-semibold text-[13px] border-r border-border"
              >
                <div className="flex items-center justify-between gap-1 max-w-[160px]">
                  <span className="text-wrap break-words leading-snug" title={name}>{name}</span>
                  {canManage && onToggleSkjul && (
                    <button
                      onClick={() => onToggleSkjul(name)}
                      title="Skjul"
                      className="opacity-0 group-hover:opacity-60 shrink-0 text-muted-foreground hover:text-[#FF2A6D] transition-all"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </td>
              {days.map(d => {
                const key = `${name}|${d.iso}`;
                const cellVagter = vagtMap[key] || [];
                const cellStempler = clockMap[key] || [];
                const isHelligdag = DK_HELLIGDAGE.has(d.iso);
                const sondag = isSondag(d.iso);
                const isToday = d.iso === isoFromDate(new Date());
                const isEmpty = cellVagter.length === 0 && cellStempler.length === 0;
                // Stemplinger med vagt_id matchet til en konkret vagt vises UNDER vagt-boksen.
                // Stemplinger uden vagt_id (rene ad-hoc) vises som selvstændige kort.
                const stemplerByVagt = {};
                const adhocStempler = [];
                cellStempler.forEach(r => {
                  if (r.vagt_id) {
                    if (!stemplerByVagt[r.vagt_id]) stemplerByVagt[r.vagt_id] = [];
                    stemplerByVagt[r.vagt_id].push(r);
                  } else {
                    adhocStempler.push(r);
                  }
                });
                return (
                  <td
                    key={d.iso}
                    onClick={() => canManage && isEmpty && onCellClick(d.iso, name)}
                    
                    className={`px-1 py-1.5 align-top min-h-[52px] transition-colors ${
                      isToday ? "bg-[#FF2A6D]/[0.035]"
                        : (isHelligdag || sondag) ? "bg-amber-500/5"
                        : d.isWeekend ? "bg-secondary/20" : ""
                    } ${canManage && isEmpty ? "cursor-pointer hover:bg-[#FF2A6D]/10" : ""}`}
                  >
                    <div className="space-y-0.5">
                      {cellVagter.map(v => {
                        const isSelected = selectedVagter?.has(v.vagt_id);
                        const erstattet = !!v.erstattet_af_adhoc;
                        const ikkeOk = !!v.ikke_godkendt;
                        const extraClass = erstattet
                          ? "line-through text-muted-foreground opacity-60"
                          : "";
                        const extraTitle = erstattet
                          ? " · Erstattet af ad-hoc stempling"
                          : ikkeOk ? " · Ikke godkendt" : "";
                        const matchede = stemplerByVagt[v.vagt_id] || [];
                        const harGodkendtStempling = (cellStempler || []).some(rr =>
                          rr.status === "approved" && (
                            (v.medarbejder_item_id && rr.medarbejder_item_id && rr.medarbejder_item_id === v.medarbejder_item_id) ||
                            rr.medarbejder === name
                          )
                        );
                        // Opfølgning #31: baggrund = godkendelses-status (status er sandhed,
                        // ikke historikken). Godkendt = lysegrøn UANSET om vagten blev
                        // auto-udstemplet og senere rettet/godkendt. Afvist = lyserød.
                        const erGodkendt = harGodkendtStempling
                          || v.vagttype === "__godkendt__"
                          || matchede.some(r => r.status === "approved");
                        const erAfvist = ikkeOk || matchede.some(r => r.status === "rejected");
                        const boksStil = vagtBoksStil(v, erGodkendt, erAfvist);
                        // Andet-linje: vagttype-label hvis ikke "Normal arbejdsdag", ellers v.city
                        const erNormal = !v.vagttype || v.vagttype === "" || v.vagttype === "Normal" || v.vagttype === "Normal timer";
                        const vagttypeEllerCity = erNormal ? (v.city || "") : vagtLabel(v);
                        // Tredje-linje: lokationsnavn. Vagt-objektet har ingen adresse-felt,
                        // så vi falder tilbage til første matchede stemplings clock_in_adresse.
                        // Backend har allerede oversat adressen til lokationsnavn (superadmin)
                        // eller maskeret by (øvrige), så her vises bare den resolvede værdi.
                        const lokationNavn = (matchede[0]?.clock_in_adresse || matchede[0]?.clock_in_lokation || "").trim();
                        // Krav #1: ALLE vagt-bokse skal have samme højde i samme række.
                        // Fast min-height (ikke proportional til varighed), så grid'et er
                        // roligt og symmetrisk — også for bokse med stempling (kompakt 1-linje).
                        const cellMinHeight = 64;
                        // Tid + status-tone for stempling
                        const stemplingStatusKlasse = (r) => {
                          if (r.status === "rejected" || r.ikke_godkendt === true) return "text-red-600 line-through";
                          if (r.status === "approved") return "text-green-700";
                          return "text-muted-foreground";
                        };
                        return (
                          <div key={v.vagt_id}>
                            <div
                              onClick={e => { e.stopPropagation(); if (canManage) { onVagtClick(v); } else if (erSimplificeret) { onMarkerVagt && onMarkerVagt(v); } }}
                              onContextMenu={e => {
                                if (!canManage) return;
                                e.preventDefault();
                                e.stopPropagation();
                                setContextMenu({ vagt: v, x: e.clientX, y: e.clientY });
                              }}
                              style={{ minHeight: cellMinHeight, zIndex: 0 }}
                              className={`relative rounded-md px-2 py-1.5 ${boksStil.bg} ${boksStil.border} ${boksStil.text} ${extraClass} ${canManage ? "cursor-pointer hover:opacity-80 transition-opacity" : (erSimplificeret ? "cursor-pointer hover:opacity-80 transition-opacity" : "")} ${isSelected ? "ring-2 ring-blue-500 ring-offset-1" : ""}`}
                              title={`Planlagt: ${v.fra && v.til ? `${v.fra}–${v.til} ` : ""}${vagtLabel(v)}${v.note ? ` · ${v.note}` : ""}${v.city ? ` · ${v.city}` : ""}${lokationNavn ? ` · ${lokationNavn}` : ""}${v.godkendt_auto ? " · Godkendt automatisk" : ""}${extraTitle}`}
                            >
                              {/* Linje 1: tid + godkendelses-tick.
                                  Opf. #76: simplificerede medarbejdere ser godkendt stemplings-tid
                                  (sandheden) i stedet for planlagt tid når stempling er godkendt.
                                  Afventer-stempling vises med sin stemplede tid + badge.
                                  Afvist eller ingen stempling: planlagt tid. */}
                              {(() => {
                                const godkendtR = erSimplificeret ? matchede.find(r => r.status === "approved") : null;
                                const afventerR = erSimplificeret && !godkendtR ? matchede.find(r => r.status === "completed") : null;
                                const afvistR = erSimplificeret && !godkendtR && !afventerR ? matchede.find(r => r.status === "rejected" || r.ikke_godkendt) : null;
                                const erIgangR = erSimplificeret && !godkendtR && !afventerR ? matchede.find(r => (r.status === "open" || r.status === "paused")) : null;
                                const visTid = godkendtR
                                  ? (() => { const i = fmtTidHHMM(godkendtR.clock_in); const u = fmtTidHHMM(godkendtR.clock_out); const t = fmtTimerMin(godkendtR.timer_actual); return `${i}${u ? `–${u}` : "–åben"}${t ? ` · ${t}` : ""}`; })()
                                  : afventerR
                                  ? (() => { const i = fmtTidHHMM(afventerR.clock_in); const u = fmtTidHHMM(afventerR.clock_out); return `${i}${u ? `–${u}` : "–åben"}`; })()
                                  : erIgangR
                                  ? (() => { const i = fmtTidHHMM(erIgangR.clock_in); return `${i || "?"}–åben`; })()
                                  : (v.fra && v.til ? `${v.fra} – ${v.til}` : vagtLabel(v));
                                return (
                                  <div className="flex items-center justify-between gap-1">
                                    <div className="font-bold text-[13px] leading-tight flex items-center gap-1 min-w-0">
                                      {selectMode && (
                                        isSelected
                                          ? <CheckSquare className="w-3 h-3 shrink-0 text-blue-500" />
                                          : <Square className="w-3 h-3 shrink-0 opacity-50" />
                                      )}
                                      <span className="truncate">{visTid}</span>
                                      {v.importeret && !erSimplificeret && (
                                        <span title="Importeret fra det gamle vagtsystem" className="shrink-0 text-blue-500 text-[11px]">⤓</span>
                                      )}
                                    </div>
                                    {harGodkendtStempling && (
                                      <Check
                                        className="shrink-0 w-3 h-3 text-green-600"
                                        title="Stempling godkendt"
                                      />
                                    )}
                                    {erIgangR && erSimplificeret && (
                                      <Clock className="shrink-0 w-3 h-3 text-amber-500" title="Igangværende" />
                                    )}
                                  </div>
                                );
                              })()}
                              {/* Linje 2: vagttype eller afdeling — mørkere tekst (Opf. #31) */}
                              {vagttypeEllerCity && (
                                <div className="text-[13px] text-neutral-700 dark:text-neutral-300 leading-tight mt-0.5">
                                  {vagttypeEllerCity}
                                </div>
                              )}
                              {/* Linje 3: lokationsnavn (eller maskeret by). Backend-resolvet. */}
                              {lokationNavn && (
                                <div className="text-[13px] text-neutral-700 dark:text-neutral-300 leading-tight break-words">
                                  {lokationNavn}
                                </div>
                              )}
                              {v.note && (
                                <div className="text-[12px] text-neutral-600 dark:text-neutral-400 leading-tight mt-0.5 break-words">
                                  {v.note}
                                </div>
                              )}
                              {/* Opf. #76: status-badges for simplificerede medarbejdere */}
                              {erSimplificeret && (() => {
                                const godkendtR = matchede.find(r => r.status === "approved");
                                const afventerR = !godkendtR && matchede.find(r => r.status === "completed");
                                const afvistR = !godkendtR && !afventerR && matchede.find(r => r.status === "rejected" || r.ikke_godkendt);
                                const igangR = !godkendtR && !afventerR && matchede.find(r => r.status === "open" || r.status === "paused");
                                if (afvistR) return (
                                  <div className="mt-0.5">
                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-500/10 px-1.5 py-0.5 rounded-full leading-none">
                                      Stempling afvist
                                    </span>
                                  </div>
                                );
                                if (afventerR) return (
                                  <div className="mt-0.5">
                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-500/10 px-1.5 py-0.5 rounded-full leading-none">
                                      Afventer godkendelse
                                    </span>
                                  </div>
                                );
                                if (igangR) return (
                                  <div className="mt-0.5">
                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-500/10 px-1.5 py-0.5 rounded-full leading-none">
                                      Igangværende
                                    </span>
                                  </div>
                                );
                                if (v.godkendt_auto) return (
                                  <div className="mt-0.5">
                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-500/10 px-1.5 py-0.5 rounded-full leading-none">
                                      <Check className="w-2.5 h-2.5" /> Godkendt automatisk
                                    </span>
                                  </div>
                                );
                                return null;
                              })()}
                              {/* Opfølgning #57 (DEL 2): auto-godkendt fravær (Syg/Ferie/Fri/Barnets) */}
                              {v.godkendt_auto && !erSimplificeret && (
                                <div className="mt-0.5">
                                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-500/10 px-1.5 py-0.5 rounded-full leading-none"
                                    title="Automatisk godkendt (fravær)">
                                    <Check className="w-2.5 h-2.5" /> Godkendt automatisk
                                  </span>
                                </div>
                              )}
                              {/* Opfølgning #60 (DEL 6): anmodning om ændring afventer godkendelse */}
                              {v.aendring_anmodning_afventer && !erSimplificeret && (
                                <div className="mt-0.5">
                                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-500/10 px-1.5 py-0.5 rounded-full leading-none"
                                    title="Anmodning om ændring afventer godkendelse">
                                    <Clock className="w-2.5 h-2.5" /> Anmodning afventer
                                  </span>
                                </div>
                              )}
                              {/* Linje 4: stempling — kun for admin/vagtplan-ret.
                                  Simplificerede medarbejdere ser ét rent overblik (Opf. #76). */}
                              {!erSimplificeret && matchede.map(r => {
                                const indTid = fmtTidHHMM(r.clock_in);
                                const udTid = fmtTidHHMM(r.clock_out);
                                const totalStr = fmtTimerMin(r.timer_actual);
                                const toneKlasse = stemplingStatusKlasse(r);
                                return (
                                  <div
                                    key={r.clock_id}
                                    className={`text-[12px] leading-tight mt-0.5 flex items-center gap-1 ${toneKlasse}`}
                                    title={`Stemplet: ${indTid || "?"}-${udTid || "?"}${totalStr ? ` · ${totalStr}` : ""}${r.note ? ` · ${r.note}` : ""}`}
                                  >
                                    <Clock className="w-2.5 h-2.5 shrink-0" />
                                    <span className="truncate">
                                      {indTid || "?"}{udTid ? `–${udTid}` : "–åben"}
                                      {totalStr ? ` · ${totalStr}` : ""}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                      {/* Ad-hoc clock-stemplinger uden tilhørende planlagt vagt: stiplet kant.
                          Vises uanset status (open/paused/completed/approved/rejected) så vagtplanen
                          reflekterer alle reelle stemplinger — også dem der venter på godkendelse. */}
                      {adhocStempler.map(r => {
                        const s = formatStempling(r);
                        const ikkeOk = r.status === "rejected" || r.ikke_godkendt === true;
                        const _today = isoFromDate(new Date());
                        // Bug 1: en status=open record fra en tidligere dag må
                        // ALDRIG vises som "Igangværende" — det er en glemt
                        // udstempling som auto-udstemplings-jobbet vil fange.
                        const erIgang = (r.status === "open" || r.status === "paused") && r.dato === _today;
                        const erGlemt = (r.status === "open" || r.status === "paused") && r.dato < _today;
                        const erAutoUdstemplet = !!r.auto_udstemplet;
                        const afventerKorrektion = r.korrektion_status === "afventer";
                        const afventerGodkendelse = r.status === "completed" && !ikkeOk && !erAutoUdstemplet;
                        const erGodkendt = r.status === "approved";
                        // Vagttype: brug r.vagttype, ellers fallback til r.clock_in_via
                        const vagttypeTekst = (r.vagttype || r.clock_in_via || "").trim();
                        // Adresse: brug clock_in_adresse, ellers clock_in_lokation, ellers "GPS-valideret position"
                        const adresseTekst = (r.clock_in_adresse || r.clock_in_lokation || "").trim() || "GPS-valideret position";
                        // Vis åbnings-tid for igangværende; "–åben" hvis stadig stemplet ind.
                        const tidTekst = erIgang && !r.clock_out
                          ? `${fmtTidHHMM(r.clock_in) || "?"} – åben`
                          : s.tid;
                        // Opfølgning #31: ensartet boks også for ad-hoc. Ramme = neutral
                        // mørk (ad-hoc = "andet"-type), stiplet for at markere "ingen
                        // planlagt vagt". Baggrund = godkendelses-status: lysegrøn =
                        // godkendt, lyserød = afvist, neutral = afventer/igangværende.
                        // Auto-udstemplet/glemt/afventer-korrektion beholder amber-baggrund
                        // som "problem der skal rettes" (jf. spec). Tekst er mørk.
                        const adhocRamme = "border-2 border-dashed border-neutral-700 dark:border-neutral-400";
                        let statusBg = "bg-card";
                        let statusBadge = null;
                        let statusIcon = <Check className="w-3 h-3" title="GPS-valideret" />;
                        let statusExtra = "";
                        if (ikkeOk) {
                          statusBg = "bg-red-500/15";
                          statusExtra = "opacity-70 line-through";
                          statusBadge = "Afvist";
                        } else if (erGlemt) {
                          statusBg = "bg-amber-500/15";
                          statusBadge = "Glemt udstempling";
                          statusIcon = <AlertTriangle className="w-3 h-3" title="Glemt udstempling" />;
                        } else if (erAutoUdstemplet && afventerKorrektion) {
                          statusBg = "bg-amber-500/15";
                          statusBadge = "Auto-udstemplet · afventer korrektion";
                          statusIcon = <AlertTriangle className="w-3 h-3" title="Auto-udstemplet" />;
                        } else if (erAutoUdstemplet && !erGodkendt) {
                          // Auto-udstemplet men endnu IKKE godkendt → afventer.
                          statusBg = "bg-amber-500/15";
                          statusBadge = "Auto-udstemplet";
                          statusIcon = <AlertTriangle className="w-3 h-3" title="Auto-udstemplet" />;
                        } else if (erIgang) {
                          statusBg = "bg-card";
                          statusExtra = "animate-pulse";
                          statusBadge = "Igangværende";
                          statusIcon = <Clock className="w-3 h-3" title="Igangværende" />;
                        } else if (afventerGodkendelse) {
                          statusBg = "bg-card";
                          statusBadge = "Afventer godkendelse";
                        } else if (erGodkendt) {
                          // Godkendt = lysegrøn UANSET historik (også auto-udstemplet+godkendt).
                          statusBg = "bg-green-500/15";
                        }
                        const statusKlasser = `${adhocRamme} ${statusBg} text-foreground ${statusExtra}`;
                        const tooltip = `${ikkeOk ? "Ikke godkendt · " : erGlemt ? "Glemt udstempling · " : erAutoUdstemplet ? "Auto-udstemplet · " : afventerGodkendelse ? "Afventer godkendelse · " : erIgang ? "Igangværende · " : ""}Stempling: ${tidTekst}${vagttypeTekst ? ` · ${vagttypeTekst}` : ""} · ${adresseTekst}${r.note ? ` · ${r.note}` : ""}`;
                        return (
                          <div
                            key={r.clock_id}
                            onClick={e => {
                              e.stopPropagation();
                              if (canManage && onVagtClick) onVagtClick({
                                vagt_id: `clock_${r.clock_id}`,
                                dato: r.dato,
                                fra: fmtTidHHMM(r.clock_in),
                                til: r.clock_out ? fmtTidHHMM(r.clock_out) : "",
                                vagttype: r.vagttype || "",
                                city: r.clock_in_lokation || "",
                                medarbejder: r.medarbejder,
                                note: r.note || "",
                                _isAdhoc: true,
                                _clockId: r.clock_id,
                              });
                            }}
                            className={`rounded-md px-2 py-1.5 ${statusKlasser} ${canManage ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
                            title={tooltip}
                          >
                            <div className="flex items-center justify-between gap-1 font-bold text-[13px] leading-tight">
                              <span className="truncate">{tidTekst}</span>
                              <span className="shrink-0">{statusIcon}</span>
                            </div>
                            {statusBadge && (
                              <div className="text-[12px] font-semibold uppercase tracking-wide leading-tight mt-0.5 text-neutral-700 dark:text-neutral-300">
                                {statusBadge}
                              </div>
                            )}
                            {vagttypeTekst && (
                              <div className="text-[13px] leading-tight text-neutral-700 dark:text-neutral-300">{vagttypeTekst}</div>
                            )}
                            <div className="text-[13px] leading-tight text-neutral-600 dark:text-neutral-400 break-words">{adresseTekst}</div>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                );
              })}
              {!erSimplificeret && (
                <td className="px-2 py-1.5 text-right font-bold text-blue-500">
                  {ugeTimer > 0 ? `${Math.round(ugeTimer*10)/10}t` : ""}
                </td>
              )}
            </tr>
            );
          })}
        </tbody>
        <tfoot>
          {/* Dags-totaler: timer + medarbejder-antal */}
          <tr className="bg-secondary/30 border-t-2 border-border">
            <td className="px-3 py-2 font-semibold text-[12px] text-muted-foreground sticky left-0 bg-secondary/30 z-30 border-r border-border">
              Dagstotal
            </td>
            {days.map(d => {
              const stat = dagTimerMap[d.iso] || { timer: 0, medCount: 0 };
              return (
                <td key={d.iso} className="px-1 py-1.5 text-center text-[13px] text-muted-foreground">
                  {stat.timer > 0 ? (
                    <>
                      <div className="font-semibold text-foreground">{Math.round(stat.timer*10)/10}t</div>
                      <div className="text-[12px]">{stat.medCount} medarb.</div>
                    </>
                  ) : <span className="text-muted-foreground/40">—</span>}
                </td>
              );
            })}
            {!erSimplificeret && (
              <td className="px-2 py-1.5 text-right font-bold text-[12px] text-foreground">
                {(() => {
                  const sum = days.reduce((s, d) => s + ((dagTimerMap[d.iso] || {}).timer || 0), 0);
                  return sum > 0 ? `${Math.round(sum*10)/10}t` : "";
                })()}
              </td>
            )}
          </tr>
          {showLoen && (
            <tr className="bg-secondary/40 border-t border-border">
              <td className="px-3 py-2 font-bold text-[13px] sticky left-0 bg-secondary/40 z-30 border-r border-border">
                Lønsum
              </td>
              {days.map(d => {
                const timer = dagLoenMap[d.iso] || 0;
                const loen = timer * loenSats;
                return (
                  <td key={d.iso} className="px-1 py-2 text-center text-[13px]">
                    {timer > 0 ? (
                      <>
                        <div className="font-semibold">{Math.round(timer * 10) / 10}t</div>
                        <div className="text-muted-foreground">{formatLoen(Math.round(loen))}</div>
                      </>
                    ) : <span className="text-muted-foreground/40">—</span>}
                  </td>
                );
              })}
              <td className="px-2 py-2" />
            </tr>
          )}
        </tfoot>
      </table>
    </div>
    {contextMenu && (
      <VagtKontekstMenu
        vagt={contextMenu.vagt}
        x={contextMenu.x}
        y={contextMenu.y}
        onClose={() => setContextMenu(null)}
        onAction={handleKontekst}
      />
    )}
    </>
  );
}

// ── Månedsgrid (medarbejder × dag i hele måneden) ───────────────────────────
function MaanedGrid({ year, month, fraDato, tilDato, vagter, clockRecords = [], medarbejdere, canManage, onCellClick, onVagtClick, selectMode, selectedVagter }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortCol, setSortCol] = useState("navn");
  const [sortDir, setSortDir] = useState("asc");
  // Dækker den FULDE periode (fx 20.–19.) — ikke kun kalendermåneden.
  // Falder tilbage til kalendermåneden hvis fraDato/tilDato ikke er sat.
  // Dette sikrer at periode-visning og uge-visning er konsistente: hvis en
  // medarbejder har vagter i uge 29, vises de også i den periode der dækker uge 29.
  const daysInMonth = useMemo(() => {
    const days = [];
    const startIso = fraDato || `${year}-${String(month+1).padStart(2,"0")}-01`;
    const endIso = tilDato || (() => {
      const last = new Date(year, month + 1, 0).getDate();
      return `${year}-${String(month+1).padStart(2,"0")}-${String(last).padStart(2,"0")}`;
    })();
    const start = new Date(startIso + "T00:00:00");
    const end = new Date(endIso + "T00:00:00");
    const cur = new Date(start);
    while (cur <= end) {
      const iso = isoFromDate(cur);
      days.push({ date: new Date(cur), iso, isWeekend: cur.getDay() === 0 || cur.getDay() === 6 });
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  }, [year, month, fraDato, tilDato]);

  const rows = useMemo(() => {
    if (!daysInMonth.length) {
      return [...new Set(medarbejdere.map(m => m.name))].sort();
    }
    const min = daysInMonth[0].iso;
    const max = daysInMonth[daysInMonth.length - 1].iso;
    // Find medarbejdere med vagter/stempler INDEN FOR perioden (ikke en streng prefix-match)
    const mdMdArb = new Set(
      vagter.filter(v => v.dato && v.dato >= min && v.dato <= max).map(v => v.medarbejder)
    );
    clockRecords.forEach(r => {
      if (r.dato && r.dato >= min && r.dato <= max) mdMdArb.add(r.medarbejder);
    });
    const all = new Set([...medarbejdere.map(m => m.name), ...mdMdArb]);
    return [...all].sort();
  }, [daysInMonth, vagter, clockRecords, medarbejdere]);

  const vagtMap = useMemo(() => {
    const m = {};
    vagter.forEach(v => {
      const key = `${v.medarbejder}|${v.dato}`;
      if (!m[key]) m[key] = [];
      m[key].push(v);
    });
    return m;
  }, [vagter]);

  const clockMap = useMemo(() => {
    const m = {};
    clockRecords.forEach(r => {
      const key = `${r.medarbejder}|${r.dato}`;
      if (!m[key]) m[key] = [];
      m[key].push(r);
    });
    Object.values(m).forEach(arr => arr.sort((a, b) => (a.clock_in || "").localeCompare(b.clock_in || "")));
    return m;
  }, [clockRecords]);

  const todayIso = isoFromDate(new Date());

  // Daglig sum (timer + medarbejdere) til tfoot.
  // Opf. #36: ingen dobbeltælling — dagTimer() pr. medarbejder pr. dag.
  const dagTimerMap = useMemo(() => {
    const m = {};
    daysInMonth.forEach(d => {
      const medSet = new Set();
      const vPerMed = {};
      const rPerMed = {};
      vagter.forEach(v => {
        if (v.dato !== d.iso) return;
        const n = v.medarbejder || "";
        (vPerMed[n] = vPerMed[n] || []).push(v);
        if (n) medSet.add(n);
      });
      clockRecords.forEach(r => {
        if (r.dato !== d.iso) return;
        const n = r.medarbejder || "";
        (rPerMed[n] = rPerMed[n] || []).push(r);
        if (n) medSet.add(n);
      });
      let timer = 0;
      medSet.forEach(n => { timer += dagTimer(vPerMed[n] || [], rPerMed[n] || []); });
      m[d.iso] = { timer, medCount: medSet.size };
    });
    return m;
  }, [daysInMonth, vagter, clockRecords]);

  // Pr.-medarbejder total til sortering. Opf. #36: dagTimer pr. dag.
  const rowTimerMap = useMemo(() => {
    const m = {};
    rows.forEach(name => {
      let t = 0;
      daysInMonth.forEach(d => {
        t += dagTimer(vagtMap[`${name}|${d.iso}`] || [], clockMap[`${name}|${d.iso}`] || []);
      });
      m[name] = t;
    });
    return m;
  }, [rows, daysInMonth, vagtMap, clockMap]);

  const visibleRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = q ? rows.filter(n => n.toLowerCase().includes(q)) : [...rows];
    list.sort((a, b) => {
      if (sortCol === "navn") {
        return sortDir === "asc" ? a.localeCompare(b) : b.localeCompare(a);
      }
      const av = rowTimerMap[a] || 0;
      const bv = rowTimerMap[b] || 0;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return list;
  }, [rows, searchQuery, sortCol, sortDir, rowTimerMap]);

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronDown className="w-3 h-3 opacity-30 inline-block" />;
    return <ChevronDown className={`w-3 h-3 inline-block ${sortDir === "asc" ? "rotate-180" : ""}`} />;
  };
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir(col === "navn" ? "asc" : "desc"); }
  };

  if (rows.length === 0) return (
    <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
      <Users className="w-10 h-10 mb-3 opacity-30" />
      <p className="text-sm">Ingen medarbejdere eller vagter denne måned</p>
    </div>
  );

  return (
    <>
    {/* Søgefelt */}
    <div className="mb-3 flex items-center gap-2">
      <div className="relative flex-1 max-w-[280px]">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <input
          type="search"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Søg medarbejder…"
          className="w-full pl-8 pr-3 py-1.5 rounded-xl border border-border bg-background text-[13px] focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
        />
      </div>
      {searchQuery && (
        <span className="text-[13px] text-muted-foreground">
          {visibleRows.length}/{rows.length}
        </span>
      )}
    </div>
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="text-[13px] border-collapse" style={{ minWidth: `${140 + daysInMonth.length * 34}px` }}>
        <thead>
          <tr className="bg-secondary/50 border-b border-border">
            <th
              onClick={() => toggleSort("navn")}
              className="text-left px-3 py-2 font-semibold text-muted-foreground w-36 sticky left-0 bg-secondary/50 z-30 cursor-pointer hover:text-foreground select-none"
            >
              <span className="inline-flex items-center gap-1">Medarbejder <SortIcon col="navn" /></span>
            </th>
            {daysInMonth.map(d => {
              const isHelligdag = DK_HELLIGDAGE.has(d.iso);
              const sondag = isSondag(d.iso);
              const helligNavn = DK_HELLIGDAGE_NAVNE[d.iso];
              return (
                <th
                  key={d.iso}
                  className={`py-2 text-center font-semibold min-w-[32px] ${
                    (isHelligdag || sondag) ? "text-amber-500" : d.isWeekend ? "text-muted-foreground/50" : "text-muted-foreground"
                  } ${d.iso === todayIso ? "text-[#FF2A6D]" : ""}`}
                  title={isHelligdag ? (helligNavn || "Helligdag") : undefined}
                >
                  <span className="block text-[12px]">{UGEDAGE[(d.date.getDay()+6)%7]}</span>
                  <span className="block font-bold leading-none">
                    {d.date.getDate()}
                    {isHelligdag && (
                      <span className="inline-block w-1 h-1 rounded-full bg-amber-500 ml-0.5 align-top" />
                    )}
                  </span>
                </th>
              );
            })}
            <th
              onClick={() => toggleSort("timer")}
              className="px-3 py-2 text-right font-semibold text-muted-foreground min-w-[48px] cursor-pointer hover:text-foreground select-none"
            >
              <span className="inline-flex items-center justify-end gap-1">Timer <SortIcon col="timer" /></span>
            </th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((name, ri) => {
            // Opf. #36: måneds-total uden dobbeltælling (stempling overtager planlagt).
            const maanedTimer = daysInMonth.reduce((s, d) =>
              s + dagTimer(vagtMap[`${name}|${d.iso}`] || [], clockMap[`${name}|${d.iso}`] || []), 0);
            return (
              <tr key={name} className="border-b border-border last:border-0">
                <td
                  style={{ position: "sticky", left: 0, zIndex: 30, backgroundColor: ri % 2 === 0 ? "#ffffff" : "#ebebed" }}
                  className="px-3 py-1.5 font-semibold border-r border-border"
                >
                  <span className="truncate block max-w-[130px]" title={name}>{name}</span>
                </td>
                {daysInMonth.map(d => {
                  const key = `${name}|${d.iso}`;
                  const cellVagter = vagtMap[key] || [];
                  const cellStempler = clockMap[key] || [];
                  const isHelligdag = DK_HELLIGDAGE.has(d.iso);
                  const sondag = isSondag(d.iso);
                  const isToday = d.iso === todayIso;
                  const isEmpty = cellVagter.length === 0 && cellStempler.length === 0;
                  const primary = cellVagter[0];
                  const cityKort   = primary ? cityShort(primary.city) : "";
                  // Kompakt fra-til "08-15:30" (drop minutter på fra hvis :00)
                  const compactTid = (() => {
                    if (!primary || !primary.fra) return "";
                    const fra = primary.fra.slice(0,5);
                    const til = primary.til ? primary.til.slice(0,5) : "";
                    const fraKort = fra.endsWith(":00") ? fra.slice(0,2) : fra;
                    const tilKort = til.endsWith(":00") ? til.slice(0,2) : til;
                    return til ? `${fraKort}-${tilKort}` : fraKort;
                  })();
                  const harGodkendtStempling = (cellStempler || []).some(rr =>
                    rr.status === "approved" && (
                      (primary && primary.medarbejder_item_id && rr.medarbejder_item_id && rr.medarbejder_item_id === primary.medarbejder_item_id) ||
                      rr.medarbejder === name
                    )
                  );
                  // Opfølgning #31: ensartet stil — baggrund = status, ramme = vagttype.
                  const mndMatchede = primary ? (cellStempler || []).filter(r => r.vagt_id === primary.vagt_id) : [];
                  const mndGodkendt = !!primary && (harGodkendtStempling
                    || primary.vagttype === "__godkendt__"
                    || mndMatchede.some(r => r.status === "approved"));
                  const mndAfvist = !!primary && (!!primary.ikke_godkendt
                    || mndMatchede.some(r => r.status === "rejected"));
                  const boksStil = primary ? vagtBoksStil(primary, mndGodkendt, mndAfvist) : null;
                  const helligNavn = DK_HELLIGDAGE_NAVNE[d.iso];
                  const tooltipBase = isHelligdag ? (helligNavn || "Helligdag") : "";
                  const tooltipVagter = cellVagter.map(v => `P: ${v.fra && v.til ? `${v.fra}–${v.til} ` : ""}${VAGTTYPE_META[v.vagttype]?.label || "Normal"}${v.city ? ` · ${v.city}` : ""}${v.note ? ` · ${v.note}` : ""}${v.godkendt_auto ? " · Godkendt automatisk" : ""}`).join("\n");
                  const tooltipStempler = cellStempler.map(r => `S: ${formatStemplingTekst(r)}${r.er_adhoc ? " · ad-hoc" : ""}${r.note ? ` · ${r.note}` : ""}`).join("\n");
                  const tooltip = [tooltipBase, tooltipVagter, tooltipStempler].filter(Boolean).join("\n");
                  return (
                    <td
                      key={d.iso}
                      onClick={() => canManage && isEmpty && onCellClick(d.iso, name)}
                      title={tooltip || undefined}
                      
                      className={`p-0.5 text-center align-middle transition-colors ${
                        (isHelligdag || sondag) ? "bg-amber-500/5" : d.isWeekend ? "bg-secondary/20" : ri % 2 === 0 ? "" : "bg-secondary/10"
                      } ${isToday ? "ring-1 ring-inset ring-[#FF2A6D]/30" : ""}
                      ${canManage && isEmpty ? "cursor-pointer hover:bg-[#FF2A6D]/5" : ""}`}
                    >
                      {cellVagter.length > 0 ? (
                        <div
                          onClick={e => { e.stopPropagation(); canManage && onVagtClick(primary); }}
                          title={(primary?.erstattet_af_adhoc ? "Erstattet af ad-hoc stempling" : primary?.ikke_godkendt ? "Ikke godkendt" : undefined)}
                          style={{ zIndex: 0 }}
                          className={`relative rounded w-full min-h-[28px] flex flex-col items-center justify-center font-bold leading-tight ${boksStil?.bg || ""} ${boksStil?.border || ""} ${boksStil?.text || ""} ${primary?.erstattet_af_adhoc ? "line-through opacity-60" : ""} ${canManage ? "cursor-pointer" : ""} ${selectedVagter?.has(primary.vagt_id) ? "ring-2 ring-blue-500 ring-offset-1" : ""}`}
                        >
                          {harGodkendtStempling && (
                            <Check
                              className="absolute top-0.5 right-0.5 w-2.5 h-2.5 text-green-600"
                              title="Stempling godkendt"
                            />
                          )}
                          {/* Opfølgning #60 (DEL 6): anmodning om ændring afventer */}
                          {primary?.aendring_anmodning_afventer && (
                            <Clock
                              className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 text-amber-500"
                              title="Anmodning om ændring afventer godkendelse"
                            />
                          )}
                          {selectMode ? (
                            <span>{selectedVagter?.has(primary.vagt_id) ? "✓" : "○"}</span>
                          ) : (
                            <>
                              <span className="text-[12px] tabular-nums">
                                {compactTid || (primary.fra ? primary.fra.slice(0,5) : "✓")}
                              </span>
                              {cityKort && (
                                <span className="mt-0.5 text-[12px] leading-none px-1 py-[1px] rounded font-semibold text-neutral-700 dark:text-neutral-300">
                                  {cityKort}
                                </span>
                              )}
                            </>
                          )}
                          {cellVagter.length > 1 && !selectMode && (
                            <span className="absolute -top-1 -left-1 inline-flex items-center justify-center min-w-[12px] h-[12px] px-[2px] rounded-full bg-[#FF2A6D] text-white text-[12px] font-bold leading-none">
                              {cellVagter.length}
                            </span>
                          )}
                          {cellStempler.length > 0 && (
                            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[10px] h-[10px] px-[2px] rounded-full bg-green-500 text-white text-[12px] font-bold leading-none">
                              {cellStempler.length > 1 ? cellStempler.length : "·"}
                            </span>
                          )}
                        </div>
                      ) : cellStempler.length > 0 ? (
                        <div
                          className="rounded w-full h-5 flex items-center justify-center font-bold bg-green-500/15 text-green-700 border border-green-500/30"
                        >
                          {cellStempler.length > 1 ? cellStempler.length : (() => {
                            const r0 = cellStempler[0];
                            try { const m = String(r0.clock_in).match(/T(\d{2}:\d{2})/); return m ? m[1] : "·"; }
                            catch { return "·"; }
                          })()}
                        </div>
                      ) : null}
                    </td>
                  );
                })}
                <td className={`px-3 py-1.5 text-right font-bold text-blue-500 ${ri % 2 === 0 ? "" : "bg-secondary/10"}`}>
                  {maanedTimer > 0 ? `${Math.round(maanedTimer*10)/10}t` : ""}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="bg-secondary/30 border-t-2 border-border">
            <td className="px-3 py-1.5 font-semibold text-[13px] text-muted-foreground sticky left-0 bg-secondary/30 z-30 border-r border-border">
              Dagstotal
            </td>
            {daysInMonth.map(d => {
              const stat = dagTimerMap[d.iso] || { timer: 0, medCount: 0 };
              return (
                <td key={d.iso} className="px-0.5 py-1 text-center text-[12px] text-muted-foreground leading-tight">
                  {stat.timer > 0 ? (
                    <>
                      <div className="font-semibold text-foreground">{Math.round(stat.timer*10)/10}t</div>
                      <div>{stat.medCount}</div>
                    </>
                  ) : <span className="text-muted-foreground/40">—</span>}
                </td>
              );
            })}
            <td className="px-3 py-1.5 text-right font-bold text-[13px] text-foreground">
              {(() => {
                const sum = daysInMonth.reduce((s, d) => s + ((dagTimerMap[d.iso] || {}).timer || 0), 0);
                return sum > 0 ? `${Math.round(sum*10)/10}t` : "";
              })()}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
    </>
  );
}

// ── Ferie-view ────────────────────────────────────────────────────────────────
function FerieView({ year, month, fraDato, tilDato, vagter, medarbejdere, feriePerioder = [], token, onPeriodeClick }) {
  // #47.5: månedsvisning fjernet. Kun "halvaar" (6 mdr) og "helaar" (12 mdr).
  // Default: halvår.
  const [zoom, setZoom] = useState("halvaar");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortCol, setSortCol] = useState("navn"); // "navn" | "dage"
  const [sortDir, setSortDir] = useState("asc");
  // Når zoom != "maaned" henter vi ferie-perioder for det udvidede range selv.
  const [extraPerioder, setExtraPerioder] = useState([]);
  const [loadingExtra, setLoadingExtra] = useState(false);

  // Vist dato-interval afhænger af zoom.
  // - maaned:  fraDato..tilDato (periode 20.–19. fra parent).
  // - halvaar: 6 måneder, start ved 1. i fraDato's måned.
  // - helaar:  hele kalenderåret for fraDato's år.
  const range = useMemo(() => {
    if (zoom === "maaned") return { fra: fraDato, til: tilDato };
    const base = fraDato ? new Date(fraDato + "T00:00:00") : new Date(year, month, 1);
    if (zoom === "halvaar") {
      const start = new Date(base.getFullYear(), base.getMonth(), 1);
      const end = new Date(start.getFullYear(), start.getMonth() + 6, 0);
      return { fra: isoFromDate(start), til: isoFromDate(end) };
    }
    const start = new Date(base.getFullYear(), 0, 1);
    const end = new Date(base.getFullYear(), 11, 31);
    return { fra: isoFromDate(start), til: isoFromDate(end) };
  }, [zoom, fraDato, tilDato, year, month]);

  // Hent ekstra ferie-perioder når zoom != maaned (parent henter kun periode-range).
  useEffect(() => {
    if (zoom === "maaned" || !token || !range.fra || !range.til) {
      setExtraPerioder([]);
      return;
    }
    let cancelled = false;
    setLoadingExtra(true);
    axios
      .get(`${API}/hub/ferie-perioder?fra=${range.fra}&til=${range.til}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(r => { if (!cancelled) setExtraPerioder(r.data.perioder || []); })
      .catch(() => { if (!cancelled) setExtraPerioder([]); })
      .finally(() => { if (!cancelled) setLoadingExtra(false); });
    return () => { cancelled = true; };
  }, [zoom, range.fra, range.til, token]);

  const aktiveFeriePerioder = zoom === "maaned" ? feriePerioder : extraPerioder;

  // Dage i intervallet
  const days = useMemo(() => {
    const arr = [];
    if (!range.fra || !range.til) return arr;
    const start = new Date(range.fra + "T00:00:00");
    const end = new Date(range.til + "T00:00:00");
    const cur = new Date(start);
    while (cur <= end) {
      const iso = isoFromDate(cur);
      const dow = cur.getDay();
      arr.push({ date: new Date(cur), iso, isWeekend: dow === 0 || dow === 6 });
      cur.setDate(cur.getDate() + 1);
    }
    return arr;
  }, [range.fra, range.til]);

  // Synlige perioder: clamp til vist range + filtrér til medarbejdere i prop.
  const synligePerioder = useMemo(() => {
    if (!days.length) return [];
    const min = days[0].iso;
    const max = days[days.length - 1].iso;
    const tilladteNavne = new Set(medarbejdere.map(m => m.name));
    return aktiveFeriePerioder.filter(p => {
      if (!p.fra_dato || !p.til_dato) return false;
      if (p.fra_dato > max || p.til_dato < min) return false;
      if (tilladteNavne.size > 0 && !tilladteNavne.has(p.medarbejder)) return false;
      return true;
    });
  }, [aktiveFeriePerioder, days, medarbejdere]);

  // #46.1: backendens /hub/ferie-perioder forener nu ALLE ferie-kilder (formelle
  // anmodninger + vagttype=Ferie-vagter + manuelle perioder) og dedup'er pr. dag,
  // så synligePerioder allerede indeholder konverterede ferie-vagter (fx Bana
  // Joumas uge 28). Den gamle frontend-fallback er derfor fjernet — den
  // undertrykte fejlagtigt vagttype-ferie for medarbejdere med en formel periode.
  const allePerioder = synligePerioder;

  // Rækker = ALLE aktive medarbejdere (også uden ferie). Søgning filtrerer.
  const allNames = useMemo(() => {
    const names = medarbejdere.map(m => m.name);
    allePerioder.forEach(p => { if (!names.includes(p.medarbejder)) names.push(p.medarbejder); });
    return names;
  }, [medarbejdere, allePerioder]);

  // Map fra medarbejder -> perioder clamped til vist range.
  // Bevarer original periode (full) så onClick kan sende den fulde periode-doc
  // til parent (incl. uklamped fra/til/periode_id).
  const perioderByName = useMemo(() => {
    const m = {};
    const min = days.length ? days[0].iso : "";
    const max = days.length ? days[days.length - 1].iso : "";
    allePerioder.forEach(p => {
      if (!m[p.medarbejder]) m[p.medarbejder] = [];
      m[p.medarbejder].push({
        fra: p.fra_dato < min ? min : p.fra_dato,
        til: p.til_dato > max ? max : p.til_dato,
        full: p, // hele periode-doc (med periode_id hvis den findes)
      });
    });
    return m;
  }, [allePerioder, days]);

  // Antal feriedage pr. medarbejder (kalenderdage i clamped perioder)
  const dageByName = useMemo(() => {
    const m = {};
    allNames.forEach(name => {
      const arr = perioderByName[name] || [];
      m[name] = arr.reduce((s, p) => {
        const fra = new Date(p.fra + "T00:00:00");
        const til = new Date(p.til + "T00:00:00");
        return s + Math.floor((til - fra) / 86400000) + 1;
      }, 0);
    });
    return m;
  }, [allNames, perioderByName]);

  // Antal feriedage pr. medarbejder pr. ISO-uge (til uge-kolonne sortering)
  const dageByUge = useMemo(() => {
    const m = {};
    allNames.forEach(name => { m[name] = {}; });
    Object.entries(perioderByName).forEach(([name, perioder]) => {
      if (!m[name]) m[name] = {};
      perioder.forEach(p => {
        const cur = new Date(p.fra + "T00:00:00");
        const end = new Date(p.til + "T00:00:00");
        while (cur <= end) {
          const wy = `${isoUgeNr(cur)}-${cur.getFullYear()}`;
          m[name][wy] = (m[name][wy] || 0) + 1;
          cur.setDate(cur.getDate() + 1);
        }
      });
    });
    return m;
  }, [allNames, perioderByName]);

  const visibleRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = q ? allNames.filter(n => n.toLowerCase().includes(q)) : [...allNames];
    list.sort((a, b) => {
      if (sortCol === "navn") return sortDir === "asc" ? a.localeCompare(b, "da") : b.localeCompare(a, "da");
      if (sortCol === "dage") {
        const av = dageByName[a] || 0;
        const bv = dageByName[b] || 0;
        if (av === bv) return a.localeCompare(b, "da");
        return sortDir === "asc" ? av - bv : bv - av;
      }
      // Uge-sort: sortCol er et wy-id (f.eks. "23-2025")
      const av = (dageByUge[a] || {})[sortCol] || 0;
      const bv = (dageByUge[b] || {})[sortCol] || 0;
      if (av === bv) return a.localeCompare(b, "da");
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return list;
  }, [allNames, searchQuery, sortCol, sortDir, dageByName, dageByUge]);

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronDown className="w-3 h-3 opacity-30 inline-block" />;
    return <ChevronDown className={`w-3 h-3 inline-block ${sortDir === "asc" ? "rotate-180" : ""}`} />;
  };
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir(col === "navn" ? "asc" : "desc"); }
  };

  // ISO -> position i days
  const dayIndex = useMemo(() => {
    const m = {};
    days.forEach((d, i) => { m[d.iso] = i; });
    return m;
  }, [days]);

  const todayIso = isoFromDate(new Date());

  // Header-label
  const periodeLabel = useMemo(() => {
    if (!range.fra || !range.til) return `${MND_FULL[month]} ${year}`;
    const fra = new Date(range.fra + "T00:00:00");
    const til = new Date(range.til + "T00:00:00");
    if (zoom === "helaar") return `Helår ${fra.getFullYear()}`;
    if (zoom === "halvaar") return `${MND_FULL[fra.getMonth()]} – ${MND_FULL[til.getMonth()]} ${til.getFullYear()}`;
    return `${MND_FULL[month]} ${year} · Periode ${fra.getDate()}.${String(fra.getMonth()+1).padStart(2,"0")} – ${til.getDate()}.${String(til.getMonth()+1).padStart(2,"0")}`;
  }, [range, month, year, zoom]);

  // Stats
  const stats = useMemo(() => {
    const medSet = new Set();
    let totalDage = 0;
    Object.entries(perioderByName).forEach(([med, perioder]) => {
      if (perioder.length > 0) medSet.add(med);
      perioder.forEach(p => {
        const fra = new Date(p.fra + "T00:00:00");
        const til = new Date(p.til + "T00:00:00");
        totalDage += Math.floor((til - fra) / 86400000) + 1;
      });
    });
    return { medCount: medSet.size, totalDage };
  }, [perioderByName]);

  // Zoom-afhængige sizes
  const cellMinWidthClass =
    zoom === "helaar" ? "min-w-[10px]" :
    zoom === "halvaar" ? "min-w-[16px]" :
    "min-w-[28px]";
  const cellPxWidth = zoom === "helaar" ? 12 : zoom === "halvaar" ? 18 : 30;
  const rowHeightPx = zoom === "helaar" ? 16 : zoom === "halvaar" ? 20 : 24;
  const showDayLabel = zoom === "maaned";

  // Måneds-grupper i header (kun halvår/helår)
  const monthGroups = useMemo(() => {
    if (zoom === "maaned") return [];
    const groups = [];
    days.forEach(d => {
      const ym = `${d.date.getFullYear()}-${d.date.getMonth()}`;
      const last = groups[groups.length - 1];
      if (last && last.ym === ym) last.count++;
      else groups.push({ ym, count: 1, label: MND_SHORT[d.date.getMonth()] });
    });
    return groups;
  }, [days, zoom]);

  // #38.1: ugenumre i toppen — grupper sammenhængende dage på samme ISO-uge,
  // så vi kan vise "U23"/"Uge 23" som colSpan-overskrift (roligt orienteringspunkt
  // i stedet for tilfældige datoer).
  const weekGroups = useMemo(() => {
    const groups = [];
    days.forEach(d => {
      const wy = `${isoUgeNr(d.date)}-${d.date.getFullYear()}`;
      const last = groups[groups.length - 1];
      if (last && last.wy === wy) last.count++;
      else groups.push({ wy, count: 1, uge: isoUgeNr(d.date) });
    });
    return groups;
  }, [days]);

  return (
    <div className="space-y-3">
      {/* Toolbar: titel + stats + søgning + zoom */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-sm font-semibold capitalize">{periodeLabel}</div>
          <div className="text-[12px] text-muted-foreground">
            {stats.medCount} medarbejder{stats.medCount === 1 ? "" : "e"} har ferie i perioden · {stats.totalDage} feriedage i alt
            {loadingExtra ? " · indlæser…" : ""}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[140px] sm:flex-none">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Søg medarbejder…"
              className="w-full pl-8 pr-3 py-1.5 rounded-xl border border-border bg-background text-[13px] focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30 sm:w-[200px]"
            />
          </div>
          <div className="inline-flex rounded-lg border border-border bg-background overflow-hidden text-[12px] font-semibold">
            {[
              { id: "halvaar", label: "Halvår" },
              { id: "helaar",  label: "Helår" },
            ].map(o => (
              <button
                key={o.id}
                onClick={() => setZoom(o.id)}
                className={`px-2.5 py-1 transition-colors ${zoom === o.id ? "bg-[#FF2A6D] text-white" : "text-muted-foreground hover:bg-secondary"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="text-[13px] border-collapse" style={{ minWidth: `${140 + days.length * cellPxWidth}px` }}>
          <thead>
            {monthGroups.length > 0 && (
              <tr className="bg-secondary/30 border-b border-border">
                <th className="sticky left-0 bg-secondary/30 z-30 w-36" />
                {monthGroups.map((g, gi) => (
                  <th key={gi} colSpan={g.count} className="px-1 py-1 text-center text-[13px] font-semibold text-muted-foreground capitalize border-l border-border first:border-l-0">
                    {g.label}
                  </th>
                ))}
                <th className="w-12" />
              </tr>
            )}
            {/* #38.1: ugenumre-række — roligt orienteringspunkt øverst. Klikbar for sortering. */}
            <tr className="bg-secondary/40 border-b border-border">
              <th className="sticky left-0 bg-secondary/40 z-30 w-36 px-3 py-1 text-left text-[12px] font-semibold text-muted-foreground">Uge</th>
              {weekGroups.map((g, gi) => {
                const isActive = sortCol === g.wy;
                return (
                  <th key={gi} colSpan={g.count}
                    title={`Sortér efter uge ${g.uge}`}
                    onClick={() => toggleSort(g.wy)}
                    className={`px-1 py-1 text-center text-[12px] font-semibold border-l border-border first:border-l-0 whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors ${isActive ? "text-[#FF2A6D]" : "text-muted-foreground"}`}>
                    {showDayLabel ? `Uge ${g.uge}` : g.uge}{isActive ? (sortDir === "desc" ? " ▼" : " ▲") : ""}
                  </th>
                );
              })}
              <th className="w-12" />
            </tr>
            <tr className="bg-secondary/50 border-b border-border">
              <th
                onClick={() => toggleSort("navn")}
                className="text-left px-3 py-2 font-semibold text-muted-foreground w-36 sticky left-0 bg-secondary/50 z-30 cursor-pointer hover:text-foreground select-none"
              >
                <span className="inline-flex items-center gap-1">Medarbejder <SortIcon col="navn" /></span>
              </th>
              {days.map(d => {
                const isHelligdag = DK_HELLIGDAGE.has(d.iso);
                const sondag = isSondag(d.iso);
                const helligNavn = DK_HELLIGDAGE_NAVNE[d.iso];
                return (
                  <th
                    key={d.iso}
                    title={isHelligdag ? (helligNavn || "Helligdag") : d.iso}
                    className={`py-1 text-center font-semibold ${cellMinWidthClass} ${
                      (isHelligdag || sondag) ? "text-amber-500" : d.isWeekend ? "text-muted-foreground/40" : "text-muted-foreground"
                    } ${d.iso === todayIso ? "text-[#FF2A6D]" : ""}`}
                  >
                    {showDayLabel ? (
                      <>
                        <span className="block text-[12px]">{UGEDAGE[(d.date.getDay()+6)%7]}</span>
                        <span className="block font-bold leading-none">
                          {d.date.getDate()}
                          {isHelligdag && (
                            <span className="inline-block w-1 h-1 rounded-full bg-amber-500 ml-0.5 align-top" />
                          )}
                        </span>
                      </>
                    ) : (
                      /* #38.1: tilfældige dato-tal fjernet i Halvår/Helår —
                         ugenummer-rækken øverst er nu orienteringspunktet.
                         Helligdage markeres stadig med en lille prik. */
                      isHelligdag ? (
                        <span className="inline-block w-1 h-1 rounded-full bg-amber-500 align-middle" />
                      ) : null
                    )}
                  </th>
                );
              })}
              <th
                onClick={() => toggleSort("dage")}
                className="px-2 py-2 text-right font-semibold text-muted-foreground min-w-[48px] cursor-pointer hover:text-foreground select-none"
              >
                <span className="inline-flex items-center justify-end gap-1">Dage <SortIcon col="dage" /></span>
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td colSpan={days.length + 2} className="py-12 text-center text-muted-foreground text-[13px]">
                  Ingen medarbejdere matcher søgning/filter.
                </td>
              </tr>
            ) : visibleRows.map((name, ri) => {
              const perioder = (perioderByName[name] || [])
                .slice()
                .sort((a, b) => a.fra.localeCompare(b.fra));
              const ferieDage = dageByName[name] || 0;
              // Marker hvilke dage hver ferie-periode dækker (kontinuerligt, weekends inkl.)
              // og hold reference til hvilken periode der dækker hver dag (bruges til klik).
              const isFerieDay = new Array(days.length).fill(false);
              const periodeByDay = new Array(days.length).fill(null);
              perioder.forEach(p => {
                const startIdx = dayIndex[p.fra];
                const endIdx = dayIndex[p.til];
                if (startIdx == null || endIdx == null) return;
                for (let k = startIdx; k <= endIdx; k++) {
                  isFerieDay[k] = true;
                  if (!periodeByDay[k]) periodeByDay[k] = p; // første der dækker vinder
                }
              });
              const segments = [];
              let i = 0;
              while (i < days.length) {
                if (isFerieDay[i]) {
                  let j = i;
                  while (j < days.length && isFerieDay[j]) j++;
                  segments.push({
                    type: "ferie",
                    colSpan: j - i,
                    startIdx: i,
                    periode: periodeByDay[i],
                  });
                  i = j;
                } else {
                  segments.push({ type: "empty", idx: i });
                  i++;
                }
              }
              return (
                <tr key={name} className="border-b border-border last:border-0">
                  <td
                    style={{ position: "sticky", left: 0, zIndex: 30, backgroundColor: ri % 2 === 0 ? "#ffffff" : "#ebebed" }}
                    className="px-3 py-1 font-semibold border-r border-border"
                  >
                    <span className="truncate block max-w-[130px]" title={name}>{name}</span>
                  </td>
                  {segments.map(seg => {
                    if (seg.type === "ferie") {
                      // Bjælker er klikbare hvis perioden har et periode_id (ikke
                      // fallback fra rå hub_vagtplan-data). Klik åbner RetFerieModal
                      // eller godkendelses-popup hvis status === "afventer".
                      const full = seg.periode?.full;
                      const isClickable = !!(onPeriodeClick && full);
                      const erAfventer = full && full.status === "afventer";
                      const handleClick = isClickable ? () => onPeriodeClick(full) : undefined;
                      const barClass = erAfventer
                        ? "border-2 border-dashed border-amber-500 bg-amber-500/20 text-amber-700 dark:text-amber-300"
                        : "bg-emerald-500 text-white";
                      const barLabel = zoom === "maaned" && seg.colSpan > 2
                        ? (erAfventer ? `Afventer · ${seg.colSpan}d` : `Ferie · ${seg.colSpan}d`)
                        : "";
                      return (
                        <td key={`f-${seg.startIdx}`} colSpan={seg.colSpan} className="p-0.5 align-middle">
                          <div
                            onClick={handleClick}
                            title={isClickable
                              ? (erAfventer
                                  ? `Afventer godkendelse${full.fra_dato ? ` (${full.fra_dato}–${full.til_dato})` : ""}`
                                  : `Klik for at se detaljer${full.fra_dato ? ` (${full.fra_dato}–${full.til_dato})` : ""}`)
                              : undefined}
                            className={`rounded w-full flex items-center justify-center font-bold truncate transition-opacity ${barClass} ${
                              zoom === "helaar" ? "h-3 text-[12px]" : zoom === "halvaar" ? "h-4 text-[12px]" : "h-5 text-[13px] px-2"
                            } ${isClickable ? "cursor-pointer hover:opacity-90" : ""}`}
                          >
                            {barLabel}
                          </div>
                        </td>
                      );
                    }
                    const d = days[seg.idx];
                    const sondag = isSondag(d.iso);
                    const hellig = DK_HELLIGDAGE.has(d.iso);
                    return (
                      <td
                        key={`e-${seg.idx}`}
                        className={`p-0 align-middle ${
                          (hellig || sondag) ? "bg-amber-500/5" : d.isWeekend ? "bg-secondary/20" : ""
                        } ${d.iso === todayIso ? "ring-1 ring-inset ring-[#FF2A6D]/30" : ""}`}
                        style={{ height: `${rowHeightPx}px` }}
                      />
                    );
                  })}
                  <td className="px-2 py-1 text-right font-bold text-emerald-600">
                    {ferieDage > 0 ? `${ferieDage}d` : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Liste-view ────────────────────────────────────────────────────────────────
function ListeView({ vagter, canManage, onVagtClick, onNyVagtDag }) {
  const grouped = useMemo(() => {
    const g = {};
    [...vagter].sort((a,b) => a.dato.localeCompare(b.dato) || a.medarbejder.localeCompare(b.medarbejder)).forEach(v => {
      if (!g[v.dato]) g[v.dato] = [];
      g[v.dato].push(v);
    });
    return g;
  }, [vagter]);

  const datoer = Object.keys(grouped).sort();
  const todayIso = isoFromDate(new Date());

  if (datoer.length === 0) return (
    <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
      <Calendar className="w-10 h-10 mb-3 opacity-30" />
      <p className="text-sm">Ingen vagter i denne periode</p>
    </div>
  );

  return (
    <div className="space-y-2">
      {datoer.map(dato => {
        const d = new Date(dato + "T00:00:00");
        const dow = UGEDAGE[(d.getDay()+6)%7];
        const isToday = dato === todayIso;
        const isHelligdag = DK_HELLIGDAGE.has(dato);
        const helligNavn = DK_HELLIGDAGE_NAVNE[dato];
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        const sondag = d.getDay() === 0;
        return (
          <div key={dato} className="rounded-xl border border-border overflow-hidden">
            <div className={`px-4 py-2 flex items-center justify-between ${isToday ? "bg-[#FF2A6D]/10" : "bg-secondary/30"}`}>
              <div className="flex items-center gap-2">
                <span className={`font-bold text-sm ${isToday ? "text-[#FF2A6D]" : (isHelligdag || sondag) ? "text-amber-500" : isWeekend ? "text-muted-foreground/60" : "text-foreground"}`}>
                  {dow} {d.getDate()}. {MND_FULL[d.getMonth()]} {d.getFullYear()}
                </span>
                {isHelligdag && <span className="text-[13px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 font-semibold">{helligNavn || "Helligdag"}</span>}
                {isToday && <span className="text-[13px] px-2 py-0.5 rounded-full bg-[#FF2A6D]/10 text-[#FF2A6D] font-semibold">I dag</span>}
              </div>
              {canManage && (
                <button
                  onClick={() => onNyVagtDag(dato)}
                  className="flex items-center gap-1 text-[13px] font-semibold text-muted-foreground hover:text-[#FF2A6D] transition-colors"
                >
                  <Plus className="w-3 h-3" /> Tilføj
                </button>
              )}
            </div>
            <div className="divide-y divide-border">
              {grouped[dato].map(v => {
                const meta = VAGTTYPE_META[v.vagttype] || VAGTTYPE_META[""];
                return (
                  <div
                    key={v.vagt_id}
                    onClick={() => canManage && onVagtClick(v)}
                    className={`px-4 py-3 flex items-center gap-3 ${canManage ? "cursor-pointer hover:bg-secondary/30 transition-colors" : ""}`}
                  >
                    <span className={`w-2 h-2 rounded-full shrink-0 ${meta.dot}`} />
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-sm block truncate">{v.medarbejder}</span>
                      <span className="text-[13px] text-muted-foreground">
                        {vagtLabel(v)}{v.fra && v.til ? ` · ${v.fra}–${v.til}` : ""}
                        {v.timer ? ` · ${v.timer}t` : ""}
                        {v.note ? ` · ${v.note}` : ""}
                      </span>
                    </div>
                    {v.city && (
                      <span className="flex items-center gap-1 text-[13px] text-muted-foreground shrink-0">
                        <MapPin className="w-3 h-3" />{v.city}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Timeoversigt ─────────────────────────────────────────────────────────────
function Timeoversigt({ vagter, clockRecords = [], medarbejdere, showLoen, loenSats, isSuperAdmin, token, kanAabneStamkort }) {
  const navigate = useNavigate();
  // #44.1: autoritativ +/- timer YTD pr. medarbejder (samme tal som Arbejdsplan).
  const [aarSaldoMap, setAarSaldoMap] = useState({ by_id: {}, by_navn: {} });
  useEffect(() => {
    if (!isSuperAdmin) return;
    let aktiv = true;
    axios.get(`${API}/admin/aarsaldo-alle`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (aktiv) setAarSaldoMap({ by_id: r.data?.by_id || {}, by_navn: r.data?.by_navn || {} }); })
      .catch(() => {});
    return () => { aktiv = false; };
  }, [isSuperAdmin, token]);
  const [periodeMode, setPeriodeMode] = useState("standard"); // "standard" | "custom"
  const [periodeOffset, setPeriodeOffset] = useState(0);
  const [customFra, setCustomFra] = useState("");
  const [customTil, setCustomTil] = useState("");
  const [sortCol, setSortCol] = useState("total"); // "navn" | "total" | vagttype
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  const [editingKontrakt, setEditingKontrakt] = useState(null); // name being edited
  const [kontraktInput, setKontraktInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  // Filtre på stemplinger: default som hidtil (kun godkendte vagter tæller)
  const [inclGodkendt, setInclGodkendt] = useState(true);
  const [inclIkkeGodkendt, setInclIkkeGodkendt] = useState(false);

  const standardPeriode = useMemo(() => {
    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth() + 1;
    month -= periodeOffset;
    while (month <= 0) { month += 12; year--; }
    while (month > 12) { month -= 12; year++; }
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    const fraDato = `${prevYear}-${String(prevMonth).padStart(2,"0")}-20`;
    const tilDato = `${year}-${String(month).padStart(2,"0")}-19`;
    const label = `20. ${MND_SHORT[prevMonth-1]} – 19. ${MND_SHORT[month-1]} ${year}`;
    return { fraDato, tilDato, label };
  }, [periodeOffset]);

  const periode = useMemo(() => {
    if (periodeMode === "custom" && customFra && customTil) {
      return { fraDato: customFra, tilDato: customTil, label: `${customFra} – ${customTil}` };
    }
    return standardPeriode;
  }, [periodeMode, customFra, customTil, standardPeriode]);

  // Antal uger i den valgte periode (decimal). Bruges til at omregne
  // ugentlige kontrakttimer til en periode-total.
  const antalUgerIPeriode = useMemo(() => {
    if (!periode.fraDato || !periode.tilDato) return 0;
    const fra = new Date(periode.fraDato + "T00:00:00");
    const til = new Date(periode.tilDato + "T00:00:00");
    const days = Math.round((til - fra) / 86400000) + 1; // inkl. både fra og til
    return days / 7;
  }, [periode]);

  const unsortedData = useMemo(() => {
    // ── NY LOGIK (opfølgning #14): stempling overskriver planlagt vagt ──
    // For hver (medarbejder, dato) med en approved clock_record: ignorér
    // den planlagte vagt. Den planlagte vagts timer-felt må ALDRIG bruges
    // som proxy når der findes en faktisk stempling.
    // For dage uden stempling: tæl kun den planlagte vagt hvis det er en
    // fravær-vagttype (Syg/Ferie/Fri). Åbne vagter (planlagt arbejde uden
    // stempling og uden status-markering) tæller 0 timer i oversigten.
    const STATUS_VAGTTYPER = new Set(["Syg", "Syg (delvis)", "Ferie", "Ferie/Fri", "Fri", "Barnets første sygedag"]);

    // Index over (medarbejder|dato) med approved stempling i perioden
    const stempletKeys = new Set(
      (clockRecords || [])
        .filter(r => r.status === "approved")
        .filter(r => r.dato >= periode.fraDato && r.dato <= periode.tilDato)
        .map(r => `${r.medarbejder}|${r.dato}`)
    );

    // Stemplings-checkbox-filtre: respekterer "ikke godkendt"-flag på
    // den planlagte vagt (afvist stempling efterlader vagten som åben).
    const ikkeGodkendtVagtIds = new Set(
      (clockRecords || [])
        .filter(r => r.status === "rejected" || r.ikke_godkendt === true)
        .map(r => r.vagt_id)
        .filter(Boolean)
    );

    const periodVagter = vagter.filter(v => {
      if (!(v.dato >= periode.fraDato && v.dato <= periode.tilDato)) return false;
      const erIkkeGodkendt = ikkeGodkendtVagtIds.has(v.vagt_id);
      if (erIkkeGodkendt) return inclIkkeGodkendt;
      return inclGodkendt;
    });

    const byMedarb = {};
    const ensureBucket = (name) => {
      if (!byMedarb[name]) {
        const med = medarbejdere.find(m => m.name === name);
        const kt = parseFloat(med?.kontrakttimer);
        const ktValid = !isNaN(kt) ? kt : null;
        // #38.2: kontrakt-total via gyldig-fra-historik (påvirker ikke bagud).
        const kontraktTotal = kontraktTimerForPeriode(
          periode.fraDato, periode.tilDato, med?.kontrakttimer_historik, ktValid
        );
        byMedarb[name] = {
          total: 0,
          typer: {},
          afdeling: med?.city || "",
          kontrakttimer: ktValid,
          kontraktTotal,
          ansaettelsestype: med?.ansaettelsestype || "",
          item_id: med?.item_id || "",
          erLaege: med ? isLaege(med) : false,
        };
      }
      return byMedarb[name];
    };

    // 1) Tilføj planlagte vagter — KUN for dage uden stempling, og kun
    //    hvis vagten er en fravær-vagttype (Syg/Ferie/Fri/…).
    periodVagter.forEach(v => {
      const name = v.medarbejder;
      const bucket = ensureBucket(name);
      const vt = v.vagttype || "";
      const harStempling = stempletKeys.has(`${name}|${v.dato}`);
      if (harStempling) return; // stempling overskriver
      if (!STATUS_VAGTTYPER.has(vt)) return; // åben vagt → 0 timer
      const t = parseFloat(v.timer) || 0;
      bucket.total = Math.round((bucket.total + t) * 100) / 100;
      const typeKey = kanonVagttype(vt);
      bucket.typer[typeKey] = Math.round(((bucket.typer[typeKey] || 0) + t) * 100) / 100;
    });

    // 2) Tilføj approved clock_records — klassificeret efter stemplingens
    //    egen vagttype. Lægers forberedelsestid (1/3 ekstra ved
    //    Lægeundervisning) gælder også her.
    (clockRecords || [])
      .filter(r => r.status === "approved")
      .filter(r => r.dato >= periode.fraDato && r.dato <= periode.tilDato)
      .filter(r => inclGodkendt) // respektér checkbox (stempling = godkendt vagt)
      .forEach(r => {
        const name = r.medarbejder;
        if (!name) return;
        const bucket = ensureBucket(name);
        const t = parseFloat(r.timer_actual) || 0;
        const vt = kanonVagttype(r.vagttype);
        bucket.total = Math.round((bucket.total + t) * 100) / 100;
        bucket.typer[vt] = Math.round(((bucket.typer[vt] || 0) + t) * 100) / 100;
        if (vt === "Lægeundervisning" && bucket.erLaege) {
          const forb = Math.round((t / 3) * 100) / 100;
          bucket.typer["Forberedelse (1/3)"] = Math.round(
            ((bucket.typer["Forberedelse (1/3)"] || 0) + forb) * 100,
          ) / 100;
          bucket.total = Math.round((bucket.total + forb) * 100) / 100;
        }
      });

    // 3) #42.5: planlagt tid for RESTERENDE dage (i dag og frem, til periodeslut)
    //    hvor der IKKE er en afsluttet stempling endnu. Lægges til forecastet.
    //    "Afsluttet dag" = dato < i dag ELLER stemplet ud → bruger stemplet tid
    //    (allerede i bucket.total). Resterende planlagte arbejdsdage forecastes.
    const _idag = isoFromDate(new Date());
    const restPlanlagt = {}; // name → planlagte timer for resterende dage
    periodVagter.forEach(v => {
      const name = v.medarbejder;
      const vt = v.vagttype || "";
      // Fravær (Syg/Ferie/Fri) er allerede talt med i total via pass 1.
      if (STATUS_VAGTTYPER.has(vt)) return;
      // Kun resterende dage (i dag og frem) tæller som forecast; afsluttede dage
      // bruger den faktiske stempling (eller 0 hvis intet stemplet).
      if (v.dato < _idag) return;
      // Hvis der allerede er en afsluttet (approved) stempling den dag, brug den.
      if (stempletKeys.has(`${name}|${v.dato}`)) return;
      const t = parseFloat(v.timer) || 0;
      restPlanlagt[name] = (restPlanlagt[name] || 0) + t;
    });

    // 4) Beregn diff som forecast: (afsluttet stemplet + fravær) + resterende
    //    planlagt − kontrakt. d.total rummer allerede afsluttet stemplet + fravær.
    Object.entries(byMedarb).forEach(([name, d]) => {
      const rest = restPlanlagt[name] || 0;
      d.restPlanlagt = Math.round(rest * 10) / 10;
      d.forecast = Math.round((d.total + rest) * 10) / 10;
      d.diff = d.kontraktTotal != null
        ? Math.round((d.total + rest - d.kontraktTotal) * 10) / 10
        : null;
    });
    // Medarbejdere der KUN har resterende planlagte vagter (ingen stempling/fravær
    // endnu) mangler en bucket — opret dem så de også får et forecast.
    Object.keys(restPlanlagt).forEach(name => {
      if (!byMedarb[name]) {
        const b = ensureBucket(name);
        const rest = restPlanlagt[name] || 0;
        b.restPlanlagt = Math.round(rest * 10) / 10;
        b.forecast = Math.round(rest * 10) / 10;
        b.diff = b.kontraktTotal != null ? Math.round((rest - b.kontraktTotal) * 10) / 10 : null;
      }
    });

    // #44.1: +/- timer "I alt år-til-dato" læses fra det autoritative backend-
    //    endpoint (aarSaldoMap), så tallet er IDENTISK med Arbejdsplan-boksen
    //    inkl. manuelle måneds-overrides. Sættes nedenfor på data-rækkerne.
    return Object.entries(byMedarb);
  }, [vagter, clockRecords, periode, medarbejdere, antalUgerIPeriode, inclGodkendt, inclIkkeGodkendt]);

  const alleTyper = useMemo(() => {
    const s = new Set();
    unsortedData.forEach(([, d]) => Object.keys(d.typer).forEach(t => s.add(t)));
    return [...s].sort();
  }, [unsortedData]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir(col === "navn" || col === "afdeling" ? "asc" : "desc"); }
  };

  const data = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    // #44.1: berig hver række med autoritativt +/- timer YTD fra backend-mappet.
    const beriget = unsortedData.map(([name, d]) => {
      const fraId = d.item_id ? aarSaldoMap.by_id?.[String(d.item_id)] : undefined;
      const fraNavn = aarSaldoMap.by_navn?.[name];
      const aarSaldo = fraId !== undefined ? fraId : (fraNavn !== undefined ? fraNavn : null);
      return [name, { ...d, aarSaldo }];
    });
    const filtered = q
      ? beriget.filter(([name]) => name.toLowerCase().includes(q))
      : beriget;
    return [...filtered].sort((a, b) => {
      let av, bv;
      if (sortCol === "navn") { av = a[0].toLowerCase(); bv = b[0].toLowerCase(); }
      else if (sortCol === "afdeling") { av = (a[1].afdeling || "").toLowerCase(); bv = (b[1].afdeling || "").toLowerCase(); }
      else if (sortCol === "total") { av = a[1].total; bv = b[1].total; }
      else if (sortCol === "kontrakt") { av = a[1].kontraktTotal ?? -Infinity; bv = b[1].kontraktTotal ?? -Infinity; }
      else if (sortCol === "diff") { av = a[1].diff ?? -Infinity; bv = b[1].diff ?? -Infinity; }
      else if (sortCol === "aarsaldo") { av = a[1].aarSaldo ?? -Infinity; bv = b[1].aarSaldo ?? -Infinity; }
      else { av = a[1].typer[sortCol] || 0; bv = b[1].typer[sortCol] || 0; }
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
  }, [unsortedData, sortCol, sortDir, searchQuery, aarSaldoMap]);

  const totalTimer = data.reduce((s, [, d]) => s + d.total, 0);

  const saveKontrakt = async (name, itemId) => {
    const val = parseFloat(kontraktInput);
    if (isNaN(val)) { setEditingKontrakt(null); return; }
    try {
      await axios.put(`${API}/hub/vagtplan/medarbejder-kontrakttimer`, { item_id: itemId, kontrakttimer: val }, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
    setEditingKontrakt(null);
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortDir === "asc" ? <ChevronDown className="w-3 h-3 rotate-180" /> : <ChevronDown className="w-3 h-3" />;
  };


  return (
    <div className="space-y-4">
      {/* Periode-nav */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center rounded-full border border-border bg-secondary p-0.5">
            {[{ id: "standard", label: "Fast" }, { id: "custom", label: "Tilpasset" }].map(m => (
              <button key={m.id} onClick={() => setPeriodeMode(m.id)}
                className={`px-3 py-1 rounded-full text-[13px] font-semibold transition-colors ${periodeMode === m.id ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`}>
                {m.label}
              </button>
            ))}
          </div>
          {periodeMode === "standard" ? (
            <div className="flex items-center gap-1">
              <button onClick={() => setPeriodeOffset(v => v+1)} className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-semibold min-w-[180px] text-center">{periode.label}</span>
              <button onClick={() => setPeriodeOffset(v => Math.max(0, v-1))} disabled={periodeOffset === 0} className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input type="date" value={customFra} onChange={e => setCustomFra(e.target.value)}
                className="px-3 py-1.5 rounded-xl border border-border bg-background text-[13px] focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              <span className="text-[13px] text-muted-foreground">–</span>
              <input type="date" value={customTil} onChange={e => setCustomTil(e.target.value)}
                className="px-3 py-1.5 rounded-xl border border-border bg-background text-[13px] focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[140px] sm:flex-none">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Søg medarbejder…"
              className="w-full pl-8 pr-3 py-1.5 rounded-xl border border-border bg-background text-[13px] focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30 sm:w-[200px]"
            />
          </div>
          <label className="flex items-center gap-1.5 text-[13px] text-muted-foreground cursor-pointer select-none">
            <input type="checkbox" checked={inclGodkendt}
              onChange={e => setInclGodkendt(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#FF2A6D]" />
            Inkludér godkendte vagter
          </label>
          <label className="flex items-center gap-1.5 text-[13px] text-muted-foreground cursor-pointer select-none">
            <input type="checkbox" checked={inclIkkeGodkendt}
              onChange={e => setInclIkkeGodkendt(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#FF2A6D]" />
            Inkludér ikke-godkendte vagter
          </label>
          <span className="text-[13px] text-muted-foreground">
            {data.length} medarbejdere · {Math.round(totalTimer*10)/10}t total
            {showLoen && ` · est. ${formatLoen(Math.round(totalTimer * loenSats))}`}
          </span>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground rounded-xl border border-border">
          <BarChart2 className="w-10 h-10 mb-3 opacity-30" />
          <p className="text-sm">Ingen vagter i denne periode</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-x-auto">
          <table className="w-full min-w-[760px] text-[13px]">
            <thead>
              <tr className="bg-secondary/50 border-b border-border">
                <th className="text-left px-4 py-3 font-semibold text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort("navn")}>
                  <div className="flex items-center gap-1">Medarbejder <SortIcon col="navn" /></div>
                </th>
                <th className="text-left px-3 py-3 font-semibold text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort("afdeling")}>
                  <div className="flex items-center gap-1">Afdeling <SortIcon col="afdeling" /></div>
                </th>
                {alleTyper.map(t => (
                  <th key={t} className="px-3 py-3 text-right font-semibold text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort(t)}>
                    <div className="flex items-center justify-end gap-1">
                      <span className={`w-2 h-2 rounded-full ${VAGTTYPE_META[t === "Normal" ? "" : t]?.dot || "bg-[#FF2A6D]"}`} />
                      {t}
                      <SortIcon col={t} />
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 text-right font-bold text-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort("total")}>
                  <div className="flex items-center justify-end gap-1">Total <SortIcon col="total" /></div>
                </th>
                {isSuperAdmin && (
                  <th
                    className="px-4 py-3 text-right font-semibold text-muted-foreground cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("kontrakt")}
                    title={`Kontrakttimer/uge × ${Math.round(antalUgerIPeriode * 100) / 100} uger`}
                  >
                    <div className="flex items-center justify-end gap-1">Kontrakt <SortIcon col="kontrakt" /></div>
                  </th>
                )}
                {isSuperAdmin && (
                  <th
                    className="px-4 py-3 text-right font-semibold text-muted-foreground cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("diff")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Saldo denne periode
                      {/* #42.6: forklarings-tooltip */}
                      <InfoIcon className="w-3 h-3 cursor-help text-muted-foreground/60" title="Forecast-saldo for den valgte lønperiode. Bruger stemplede timer for afsluttede dage og planlagte timer for resterende dage." />
                      <SortIcon col="diff" />
                    </div>
                  </th>
                )}
                {/* #42.4: +/- timer (år) — kun superadmin (kontrakt-baseret). */}
                {isSuperAdmin && (
                  <th
                    className="px-4 py-3 text-right font-semibold text-muted-foreground cursor-pointer hover:text-foreground"
                    onClick={() => handleSort("aarsaldo")}
                    title="Samme tal som 'I alt år-til-dato' fra +/- timer på medarbejderens Arbejdsplan (inkl. manuelle måneds-justeringer). Syg, ferie, fri og læge-fravær tæller som opfyldt kontrakt. Kun månedsløn."
                  >
                    <div className="flex items-center justify-end gap-1">+/- timer ({new Date().getFullYear()}) <SortIcon col="aarsaldo" /></div>
                  </th>
                )}
                {showLoen && <th className="px-4 py-3 text-right font-semibold text-muted-foreground">Løn (est.)</th>}
              </tr>
            </thead>
            <tbody>
              {data.map(([name, d], ri) => (
                <tr key={name} className={`border-b border-border last:border-0 ${ri % 2 === 0 ? "" : "bg-secondary/10"}`}>
                  <td className="px-4 py-2.5 font-semibold">
                    {/* #42.3: klikbart navn → stamkort (kun med Medarbejdere-adgang). */}
                    {kanAabneStamkort && d.item_id ? (
                      <button onClick={() => navigate(`/admin/medarbejdere/${d.item_id}`)}
                        className="text-left hover:text-[#FF2A6D] transition-colors hover:underline underline-offset-2">
                        {name}
                      </button>
                    ) : name}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">{d.afdeling || "—"}</td>
                  {alleTyper.map(t => (
                    <td key={t} className="px-3 py-2.5 text-right text-muted-foreground">
                      {d.typer[t] ? `${d.typer[t]}t` : "—"}
                    </td>
                  ))}
                  <td className="px-4 py-2.5 text-right font-bold text-[#FF2A6D]">
                    {d.total}t
                  </td>
                  {isSuperAdmin && (
                    <td className="px-4 py-2.5 text-right">
                      {editingKontrakt === name ? (
                        <input
                          autoFocus
                          type="number"
                          value={kontraktInput}
                          onChange={e => setKontraktInput(e.target.value)}
                          onBlur={() => saveKontrakt(name, d.item_id)}
                          onKeyDown={e => { if (e.key === "Enter") saveKontrakt(name, d.item_id); if (e.key === "Escape") setEditingKontrakt(null); }}
                          className="w-16 px-1.5 py-0.5 rounded border border-[#FF2A6D]/40 text-[13px] text-right focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]/30 bg-background"
                        />
                      ) : (
                        <button
                          onClick={() => { setEditingKontrakt(name); setKontraktInput(d.kontrakttimer != null ? String(d.kontrakttimer) : ""); }}
                          className="text-[13px] text-muted-foreground hover:text-foreground transition-colors"
                          title={d.kontrakttimer != null ? `${d.kontrakttimer}t/uge × ${Math.round(antalUgerIPeriode * 100) / 100} uger` : "Klik for at sætte kontrakttimer/uge"}
                        >
                          {d.kontraktTotal != null
                            ? `${d.kontraktTotal}t`
                            : <span className="opacity-30">—</span>}
                        </button>
                      )}
                    </td>
                  )}
                  {isSuperAdmin && (
                    <td className="px-4 py-2.5 text-right">
                      {d.diff == null ? (
                        <span className="text-[13px] text-muted-foreground/40">—</span>
                      ) : d.diff === 0 ? (
                        <span className="text-[13px] font-semibold text-muted-foreground">0t</span>
                      ) : (
                        <span className={`text-[13px] font-semibold ${d.diff > 0 ? "text-emerald-600" : "text-red-500"}`}>
                          {d.diff > 0 ? "+" : ""}{d.diff}t
                        </span>
                      )}
                    </td>
                  )}
                  {/* #42.4: +/- timer (år-til-dato). "—" for timeløn/vikar. */}
                  {isSuperAdmin && (
                    <td className="px-4 py-2.5 text-right">
                      {d.aarSaldo == null ? (
                        <span className="text-[13px] text-muted-foreground/40">—</span>
                      ) : d.aarSaldo === 0 ? (
                        <span className="text-[13px] font-semibold text-muted-foreground">0t</span>
                      ) : (
                        <span className={`text-[13px] font-semibold ${d.aarSaldo > 0 ? "text-emerald-600" : "text-red-500"}`}>
                          {d.aarSaldo > 0 ? "+" : ""}{d.aarSaldo}t
                        </span>
                      )}
                    </td>
                  )}
                  {showLoen && (
                    <td className="px-4 py-2.5 text-right text-muted-foreground">
                      {formatLoen(Math.round(d.total * loenSats))}
                    </td>
                  )}
                </tr>
              ))}
              <tr className="bg-secondary/30 border-t-2 border-border">
                <td className="px-4 py-2.5 font-bold" colSpan={2}>Total</td>
                {alleTyper.map(t => {
                  const s = data.reduce((acc, [, d]) => acc + (d.typer[t] || 0), 0);
                  return <td key={t} className="px-3 py-2.5 text-right font-semibold">{Math.round(s*10)/10}t</td>;
                })}
                <td className="px-4 py-2.5 text-right font-bold text-[#FF2A6D]">{Math.round(totalTimer*10)/10}t</td>
                {isSuperAdmin && (() => {
                  const totalKontrakt = data.reduce((acc, [, d]) => acc + (d.kontraktTotal || 0), 0);
                  return <td className="px-4 py-2.5 text-right font-bold text-muted-foreground">{Math.round(totalKontrakt*10)/10}t</td>;
                })()}
                {isSuperAdmin && (() => {
                  const totalDiff = data.reduce((acc, [, d]) => acc + (d.diff || 0), 0);
                  const rounded = Math.round(totalDiff * 10) / 10;
                  if (rounded === 0) return <td className="px-4 py-2.5 text-right font-bold text-muted-foreground">0t</td>;
                  return (
                    <td className={`px-4 py-2.5 text-right font-bold ${rounded > 0 ? "text-emerald-600" : "text-red-500"}`}>
                      {rounded > 0 ? "+" : ""}{rounded}t
                    </td>
                  );
                })()}
                {/* #42.4: +/- timer total (kun månedsløn-medarbejdere). */}
                {isSuperAdmin && (() => {
                  const totalAar = data.reduce((acc, [, d]) => acc + (d.aarSaldo || 0), 0);
                  const rounded = Math.round(totalAar * 10) / 10;
                  if (rounded === 0) return <td className="px-4 py-2.5 text-right font-bold text-muted-foreground">0t</td>;
                  return (
                    <td className={`px-4 py-2.5 text-right font-bold ${rounded > 0 ? "text-emerald-600" : "text-red-500"}`}>
                      {rounded > 0 ? "+" : ""}{rounded}t
                    </td>
                  );
                })()}
                {showLoen && (
                  <td className="px-4 py-2.5 text-right font-bold">{formatLoen(Math.round(totalTimer * loenSats))}</td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Lån-modal ─────────────────────────────────────────────────────────────────
const LAAN_BYER = ["København", "Aarhus", "Odense", "Aalborg", "Kolding", "TNBC"];

function LaanModal({ medarbejdere, laan, onOpret, onSlet, onClose }) {
  const [q, setQ] = useState("");
  const [valgtMed, setValgtMed] = useState("");
  const [valgtCity, setValgtCity] = useState("");
  const [fraDato, setFraDato] = useState("");
  const [tilDato, setTilDato] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const filtered = useMemo(() =>
    medarbejdere.filter(m => m.name.toLowerCase().includes(q.toLowerCase())),
    [medarbejdere, q]
  );

  const todayIso = isoFromDate(new Date());

  const handleOpret = async () => {
    if (!valgtMed || !valgtCity || !fraDato || !tilDato) { setError("Udfyld alle felter"); return; }
    setSaving(true); setError("");
    const ok = await onOpret({ medarbejder: valgtMed, city: valgtCity, fra_dato: fraDato, til_dato: tilDato });
    setSaving(false);
    if (ok) { setValgtMed(""); setValgtCity(""); setFraDato(""); setTilDato(""); setQ(""); }
    else setError("Kunne ikke oprette lån");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-md shadow-2xl space-y-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">Lån medarbejder</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors"><X className="w-4 h-4" /></button>
        </div>

        {/* Nyt lån */}
        <div className="space-y-3 rounded-xl border border-border p-4 bg-secondary/20">
          <p className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Nyt lån</p>

          {/* Medarbejder søgefelt */}
          <div>
            <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Medarbejder</label>
            <div className="relative">
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border bg-background">
                <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <input
                  value={valgtMed || q}
                  onChange={e => { setQ(e.target.value); setValgtMed(""); }}
                  placeholder="Søg navn..."
                  className="flex-1 bg-transparent text-sm focus:outline-none"
                />
                {valgtMed && <button onClick={() => { setValgtMed(""); setQ(""); }} className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>}
              </div>
              {q && !valgtMed && (
                <div className="absolute left-0 top-full mt-1 w-full bg-background border border-border rounded-xl shadow-xl z-50 max-h-44 overflow-y-auto">
                  {filtered.length === 0
                    ? <p className="px-4 py-3 text-sm text-muted-foreground">Ingen resultater</p>
                    : filtered.map(m => (
                      <button key={m.name} type="button"
                        onClick={() => { setValgtMed(m.name); setQ(""); }}
                        className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-secondary transition-colors text-left">
                        <span className="font-medium">{m.name}</span>
                        <span className="text-[13px] text-muted-foreground">{m.city}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* By */}
          <div>
            <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Lån til by</label>
            <div className="flex items-center gap-1 flex-wrap">
              {LAAN_BYER.map(b => (
                <button key={b} type="button" onClick={() => setValgtCity(b)}
                  className={`px-3 py-1.5 rounded-full text-[13px] font-semibold border transition-all ${valgtCity === b ? "bg-[#FF2A6D] text-white border-[#FF2A6D]" : "border-border text-muted-foreground hover:border-[#FF2A6D]/40"}`}>
                  {b}
                </button>
              ))}
            </div>
          </div>

          {/* Datoer */}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Fra</label>
              <input type="date" value={fraDato} onChange={e => setFraDato(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
            </div>
            <div className="flex-1">
              <label className="block text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Til</label>
              <input type="date" value={tilDato} onChange={e => setTilDato(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
            </div>
          </div>

          {error && <p className="text-[13px] text-red-500">{error}</p>}

          <button onClick={handleOpret} disabled={saving}
            className="w-full py-2 rounded-xl text-sm bg-[#FF2A6D] text-white font-semibold hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Opret lån
          </button>
        </div>

        {/* Eksisterende lån */}
        {laan.length > 0 && (
          <div className="space-y-2">
            <p className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Aktive lån</p>
            {laan.map(l => (
              <div key={l.laan_id} className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-border bg-background gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{l.medarbejder}</p>
                  <p className="text-[13px] text-muted-foreground">
                    → {l.city} · {l.fra_dato} – {l.til_dato}
                    {l.til_dato < todayIso ? " · udløbet" : ""}
                  </p>
                </div>
                <button onClick={() => onSlet(l.laan_id)}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Hoved-komponent ──────────────────────────────────────────────────────────
const VIEWS = [
  { id: "uge",      label: "Uge",          icon: LayoutGrid },
  { id: "maaned",   label: "Periode",      icon: Calendar },
  { id: "ferie",    label: "Ferie",        icon: Sun },
  { id: "oversigt", label: "Timeoversigt", icon: BarChart2 },
  { id: "stempel",  label: "Stempling",    icon: Clock },
  { id: "koersel",  label: "Kørsel",       icon: Car },
];

const BLANK_DRAFT = { dato: "", fra: "09:00", til: "17:00", vagttype: "Normal timer", city: "", medarbejder: "", note: "", andet_navn: "" };

const isLaege = (m) => m.stilling?.toLowerCase().includes("læge");

// Modal: opret manuel stempling for en åben vagt.
// Pre-udfylder fra/til-tid og default lokation fra den planlagte vagt.
function ManuelStemplingModal({ vagt, lokationer, onClose, onOpret, busy }) {
  const defaultFra = (vagt?.fra || "").slice(0, 5) || "09:00";
  const defaultTil = (vagt?.til || "").slice(0, 5) || "17:00";
  const [fra, setFra] = useState(defaultFra);
  const [til, setTil] = useState(defaultTil);
  const [note, setNote] = useState("");
  // Tildelte lokationer for vagtens by (eller alle, hvis intet match).
  // Bemærk: backend forventer lokations-navn, ikke ID.
  const tildelte = useMemo(() => {
    const aktive = (lokationer || []).filter(l => l.aktiv !== false);
    if (!vagt?.city) return aktive;
    const matching = aktive.filter(l => (l.by || "") === vagt.city);
    return matching.length ? matching : aktive;
  }, [lokationer, vagt]);
  const [lokation, setLokation] = useState(() => {
    if (vagt?.city) {
      const m = (lokationer || []).find(l => (l.by || "") === vagt.city);
      if (m) return m.navn;
    }
    return tildelte[0]?.navn || "";
  });

  const kanGemme = !!fra && !!til && !!lokation && !busy;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-2xl shadow-2xl border border-border p-5 w-full max-w-sm space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-sm">Manuel stempling</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[13px] text-muted-foreground">
          {vagt.medarbejder} · {vagt.dato}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[13px] text-muted-foreground mb-1">Fra-tid</label>
            <input type="time" value={fra} onChange={e => setFra(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
          </div>
          <div>
            <label className="block text-[13px] text-muted-foreground mb-1">Til-tid</label>
            <input type="time" value={til} onChange={e => setTil(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
          </div>
        </div>
        <div>
          <label className="block text-[13px] text-muted-foreground mb-1">Lokation</label>
          <select value={lokation} onChange={e => setLokation(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30">
            {tildelte.length === 0 && <option value="">Ingen lokationer fundet</option>}
            {tildelte.map(l => (
              <option key={l.lok_id || l.navn} value={l.navn}>
                {l.navn}{l.by ? ` · ${l.by}` : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[13px] text-muted-foreground mb-1">Note (valgfrit)</label>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
            className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
            placeholder="Hvorfor oprettes denne manuelt?" />
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-border text-sm hover:bg-secondary transition-colors">Annuller</button>
          <button
            onClick={() => onOpret({ clock_in_time: fra, clock_out_time: til, lokation, note: note.trim() })}
            disabled={!kanGemme}
            className="px-4 py-2 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-2"
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Opret stempling
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal: slet vagt med arkiv (kræver årsag).
function SletVagtArkivModal({ vagt, onClose, onSlet, busy }) {
  const [aarsag, setAarsag] = useState("");
  const kanSlette = aarsag.trim().length >= 3 && !busy;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-2xl shadow-2xl border border-border p-5 w-full max-w-sm space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-sm">Slet vagt (arkiveres)</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[13px] text-muted-foreground">
          {vagt.medarbejder} · {vagt.dato} · {vagt.fra}–{vagt.til}
        </p>
        <p className="text-[12px] text-amber-600 bg-amber-500/10 rounded-lg px-3 py-2">
          Vagten arkiveres med din note. Sletningen kan ikke fortrydes herfra.
        </p>
        <div>
          <label className="block text-[13px] text-muted-foreground mb-1">Årsag (påkrævet)</label>
          <textarea value={aarsag} onChange={e => setAarsag(e.target.value)} rows={2}
            className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
            placeholder="Hvorfor slettes vagten?" />
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-border text-sm hover:bg-secondary transition-colors">Annuller</button>
          <button
            onClick={() => onSlet(aarsag.trim())}
            disabled={!kanSlette}
            className="px-4 py-2 rounded-xl bg-red-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-2"
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Slet og arkivér
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal til soft-delete af stempling (markér som ikke godkendt)
function AfvisStemplingModal({ record, onClose, onConfirm, busy }) {
  const [note, setNote] = useState("");
  const kanAfvis = note.trim().length >= 1 && !busy;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-2xl shadow-2xl border border-border p-5 w-full max-w-sm space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-sm">Markér som ikke godkendt</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[13px] text-muted-foreground">
          {record.medarbejder} · {record.dato}
        </p>
        <p className="text-[12px] text-amber-600 bg-amber-500/10 rounded-lg px-3 py-2">
          Vagten bevares, men ekskluderes fra timetællere og kan fortrydes senere.
        </p>
        <div>
          <label className="block text-[13px] text-muted-foreground mb-1">Note / årsag</label>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
            className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
            placeholder="Hvorfor godkendes vagten ikke?" />
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-border text-sm hover:bg-secondary transition-colors">Annuller</button>
          <button
            onClick={() => onConfirm(note.trim())}
            disabled={!kanAfvis}
            className="px-4 py-2 rounded-xl bg-red-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-2"
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Markér som ikke godkendt
          </button>
        </div>
      </div>
    </div>
  );
}

// Opfølgning #59 (DEL B / B3): Eksport-dialog for kørsel.
// Lønperiode (YYYY-MM, blank = foregående afsluttede), status (godkendt/alle).
// Henter Excel-blob fra backend og trigger browser-download. Filnavn fra
// Content-Disposition hvis muligt, ellers "Kørsel.xlsx".
function KoerselEksportModal({ onClose, token }) {
  const [periode, setPeriode] = useState(""); // "" = foregående afsluttede (backend-default)
  const [status, setStatus] = useState("godkendt"); // "godkendt" | "alle"
  const [busy, setBusy] = useState(false);

  // Parse filnavn fra Content-Disposition (filename="..." eller filename*=UTF-8''...).
  const filnavnFraHeader = (cd) => {
    if (!cd) return null;
    const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cd);
    if (star) { try { return decodeURIComponent(star[1].replace(/^"|"$/g, "")); } catch { /* ignore */ } }
    const plain = /filename="?([^";]+)"?/i.exec(cd);
    return plain ? plain[1] : null;
  };

  const handleEksport = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (periode.trim()) params.set("periode", periode.trim());
      // status=godkendt sendes altid; "alle" sendes ikke som status-filter.
      if (status === "godkendt") params.set("status", "godkendt");
      const res = await axios.get(`${API}/admin/koersel/eksport?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
      const filnavn = filnavnFraHeader(cd) || "Kørsel.xlsx";
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filnavn;
      a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch (err) {
      alert("Kunne ikke eksportere kørsel: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-sm shadow-2xl space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">Eksportér kørsel</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-1.5">
          <label className="block text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Lønperiode</label>
          <input
            type="month"
            value={periode}
            onChange={e => setPeriode(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
          />
          <p className="text-[12px] text-muted-foreground">Tom = foregående afsluttede lønperiode.</p>
        </div>

        <div className="space-y-1.5">
          <label className="block text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Status</label>
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
          >
            <option value="godkendt">Godkendt</option>
            <option value="alle">Alle</option>
          </select>
        </div>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-xl border border-border text-sm font-semibold hover:bg-secondary transition-colors">
            Annullér
          </button>
          <button onClick={handleEksport} disabled={busy}
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Eksportér
          </button>
        </div>
      </div>
    </div>
  );
}

// Opfølgning #62/#66: Kørsel-fanen med fuld historik, periodenavigation og accordion pr. medarbejder.
function koerselPeriodeForDato(dato) {
  const d = dato ? new Date(dato + "T00:00:00") : new Date();
  const day = d.getDate(), month = d.getMonth() + 1, year = d.getFullYear();
  let fra, til, key;
  if (day >= 20) {
    fra = new Date(year, month - 1, 20);
    const nextM = month === 12 ? 1 : month + 1;
    const nextY = month === 12 ? year + 1 : year;
    til = new Date(nextY, nextM - 1, 19);
    key = `${year}-${String(month).padStart(2,"0")}`;
  } else {
    const prevM = month === 1 ? 12 : month - 1;
    const prevY = month === 1 ? year - 1 : year;
    fra = new Date(prevY, prevM - 1, 20);
    til = new Date(year, month - 1, 19);
    key = `${prevY}-${String(prevM).padStart(2,"0")}`;
  }
  return { fra, til, key };
}

function koerselPeriodeLabel(fra, til) {
  const fmtD = (d) => `${d.getDate()}.${(d.getMonth()+1).toString().padStart(2,"0")}`;
  return `${fmtD(fra)} – ${fmtD(til)}.${til.getFullYear()}`;
}

function KoerselStatusChip({ status }) {
  if (status === "godkendt") return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-500/10 px-2 py-0.5 rounded-full"><Check className="w-3 h-3" /> Godkendt</span>;
  if (status === "afvist")   return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 bg-red-500/10 px-2 py-0.5 rounded-full"><X className="w-3 h-3" /> Afvist</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-500/10 px-2 py-0.5 rounded-full"><Clock className="w-3 h-3" /> Afventer</span>;
}

function KoerselGodkendelse({ token }) {
  const hdrs = { Authorization: `Bearer ${token}` };
  const [periodeOffset, setPeriodeOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState("alle"); // "alle" | "afventer" | "godkendt" | "afvist" | "papirkurv"
  const [koersler, setKoersler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [afvisId, setAfvisId] = useState(null);
  const [afvisAarsag, setAfvisAarsag] = useState("");
  const [eksportOpen, setEksportOpen] = useState(false);
  const [aabneAccordions, setAabneAccordions] = useState(new Set());
  const [sletBekraeft, setSletBekraeft] = useState(null); // kørsel-objekt | null
  const [sletAarsag, setSletAarsag] = useState("");
  const [papirkurvData, setPapirkurvData] = useState([]);
  const [papirkurvLoading, setPapirkurvLoading] = useState(false);
  const [selectedKoersel, setSelectedKoersel] = useState(new Set());
  const [bulkKoerselBusy, setBulkKoerselBusy] = useState(false);

  // Beregn aktiv periode baseret på offset
  const aktivPeriode = useMemo(() => {
    const now = new Date();
    // Flyt offset måneder fra nutiden, så vi kan navigere
    const ref = new Date(now.getFullYear(), now.getMonth() + periodeOffset, now.getDate());
    const refIso = `${ref.getFullYear()}-${String(ref.getMonth()+1).padStart(2,"0")}-${String(ref.getDate()).padStart(2,"0")}`;
    return koerselPeriodeForDato(refIso);
  }, [periodeOffset]);

  const periodeKey = aktivPeriode.key;
  const periodeLabel = koerselPeriodeLabel(aktivPeriode.fra, aktivPeriode.til);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/koersel?periode=${periodeKey}`, { headers: hdrs })
      .then(r => {
        const data = r.data.koersler || [];
        setKoersler(data);
        // Default: åbn alle medarbejdere med afventende kørsler
        const navneMedAfventer = new Set(data.filter(k => k.status === "afventer").map(k => k.medarbejder_navn));
        setAabneAccordions(navneMedAfventer.size > 0 ? navneMedAfventer : new Set(data.map(k => k.medarbejder_navn)));
      })
      .catch(() => setKoersler([]))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodeKey, token]);

  useEffect(() => { load(); }, [load]);

  const godkend = async (k) => {
    const id = k.id || k._id;
    if (!id) { alert("Mangler kørsels-id"); return; }
    setBusy(id);
    try {
      await axios.post(`${API}/admin/koersel/${id}/godkend`, {}, { headers: hdrs });
      load();
    } catch (err) {
      alert("Kunne ikke godkende: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(null); }
  };

  const handleBulkKoerselGodkend = async () => {
    if (!selectedKoersel.size) return;
    setBulkKoerselBusy(true);
    let fejl = 0;
    for (const id of selectedKoersel) {
      try {
        await axios.post(`${API}/admin/koersel/${id}/godkend`, {}, { headers: hdrs });
      } catch {
        fejl++;
      }
    }
    setBulkKoerselBusy(false);
    setSelectedKoersel(new Set());
    load();
    if (fejl > 0) alert(`${fejl} kørsel(er) kunne ikke godkendes`);
  };

  const toggleKoerselSelect = (id) => setSelectedKoersel(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const afvis = async (k) => {
    const id = k.id || k._id;
    if (!id) { alert("Mangler kørsels-id"); return; }
    if (!afvisAarsag.trim()) { alert("Angiv en årsag"); return; }
    setBusy(id);
    try {
      await axios.post(`${API}/admin/koersel/${id}/afvis`, { aarsag: afvisAarsag.trim() }, { headers: hdrs });
      setAfvisId(null); setAfvisAarsag(""); load();
    } catch (err) {
      alert("Kunne ikke afvise: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(null); }
  };

  const toggleAccordion = (navn) => setAabneAccordions(s => {
    const n = new Set(s);
    if (n.has(navn)) n.delete(navn); else n.add(navn);
    return n;
  });

  const loadPapirkurv = useCallback(() => {
    setPapirkurvLoading(true);
    axios.get(`${API}/admin/koersel?papirkurv=true&periode=${periodeKey}`, { headers: hdrs })
      .then(r => setPapirkurvData(r.data.koersler || []))
      .catch(() => setPapirkurvData([]))
      .finally(() => setPapirkurvLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodeKey, token]);

  useEffect(() => {
    if (statusFilter === "papirkurv") loadPapirkurv();
  }, [statusFilter, loadPapirkurv]);

  const sletKoersel = async (k) => {
    const id = k.id || k._id;
    setSletBekraeft(null);
    setBusy(id);
    try {
      await axios.delete(`${API}/admin/koersel/${id}`, {
        headers: hdrs,
        data: { aarsag: sletAarsag.trim() }
      });
      setSletAarsag("");
      load();
    } catch (err) {
      alert("Kunne ikke slette: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(null); }
  };

  const gendanKoersel = async (k) => {
    const id = k.id || k._id;
    setBusy(id);
    try {
      await axios.post(`${API}/admin/koersel/${id}/gendan`, {}, { headers: hdrs });
      loadPapirkurv();
    } catch (err) {
      alert("Kunne ikke gendanne: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(null); }
  };

  const filteredKoersler = useMemo(() => {
    if (statusFilter === "papirkurv") return []; // håndteres separat
    if (statusFilter === "alle") return koersler;
    return koersler.filter(k => k.status === statusFilter);
  }, [koersler, statusFilter]);

  // Gruppér pr. medarbejder
  const prMedarbejder = useMemo(() => {
    const groups = {};
    filteredKoersler.forEach(k => {
      const navn = k.medarbejder_navn || "Ukendt";
      if (!groups[navn]) groups[navn] = [];
      groups[navn].push(k);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0], "da"));
  }, [filteredKoersler]);

  const totalGodkendtKm = koersler.filter(k => k.status === "godkendt").reduce((s, k) => s + (k.km || 0), 0);
  const antalAfventer = koersler.filter(k => k.status === "afventer").length;
  const antalGodkendt = koersler.filter(k => k.status === "godkendt").length;
  const antalAfvist  = koersler.filter(k => k.status === "afvist").length;

  return (
    <div className="flex flex-col gap-4">
      {/* Header: titel + periode-navigation + eksport */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Car className="w-4 h-4 text-[#FF2A6D]" />
            <h2 className="text-sm font-bold">Kørsel · Lønperiode {periodeLabel}</h2>
          </div>
          {totalGodkendtKm > 0 && (
            <p className="text-[13px] text-muted-foreground mt-0.5 ml-6">
              {Math.round(totalGodkendtKm * 10) / 10} km godkendt i perioden
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Periode-navigation */}
          <div className="flex items-center gap-1 bg-secondary border border-border rounded-full px-2 py-1">
            <button onClick={() => setPeriodeOffset(o => o - 1)} className="p-1 rounded-full hover:bg-background transition-colors text-muted-foreground" title="Forrige periode">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-[13px] font-semibold px-1 min-w-[130px] text-center">{periodeLabel}</span>
            <button onClick={() => setPeriodeOffset(o => o + 1)} className="p-1 rounded-full hover:bg-background transition-colors text-muted-foreground" title="Næste periode">
              <ChevronRight className="w-4 h-4" />
            </button>
            {periodeOffset !== 0 && (
              <button onClick={() => setPeriodeOffset(0)} className="ml-1 text-[13px] px-2 py-0.5 rounded-full bg-background text-muted-foreground hover:text-foreground transition-colors font-semibold">nu</button>
            )}
          </div>
          <button onClick={() => setEksportOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border bg-background text-[13px] font-semibold hover:bg-secondary transition-colors">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Eksportér kørsel
          </button>
        </div>
      </div>

      {/* Status-filter tabs */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {[
          { id: "alle",      label: `Alle (${koersler.length})` },
          { id: "afventer",  label: `Afventer (${antalAfventer})` },
          { id: "godkendt",  label: `Godkendt (${antalGodkendt})` },
          { id: "afvist",    label: `Afvist (${antalAfvist})` },
          { id: "papirkurv", label: `🗑 Papirkurv (${papirkurvData.length})` },
        ].map(f => (
          <button key={f.id} onClick={() => setStatusFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-[13px] font-semibold border transition-colors ${
              statusFilter === f.id
                ? "bg-[#FF2A6D] text-white border-[#FF2A6D]"
                : "border-border bg-background hover:bg-secondary"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Bulk-actions bar: vises når mindst 1 kørsel er valgt */}
      {selectedKoersel.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-green-500/30 bg-green-500/5">
          <span className="text-[13px] font-semibold text-green-700">{selectedKoersel.size} valgt</span>
          <button onClick={handleBulkKoerselGodkend} disabled={bulkKoerselBusy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500 text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
            {bulkKoerselBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Godkend valgte
          </button>
          <button onClick={() => setSelectedKoersel(new Set())}
            className="ml-auto p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Accordion pr. medarbejder */}
      {statusFilter !== "papirkurv" && (loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : prMedarbejder.length === 0 ? (
        <div className="rounded-xl border border-border py-10 text-center">
          <p className="text-sm text-muted-foreground italic">
            {statusFilter === "alle" ? "Ingen kørsler i denne periode" : `Ingen ${statusFilter === "afventer" ? "afventende" : statusFilter} kørsler`}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {prMedarbejder.map(([navn, rows]) => {
            const erAaben = aabneAccordions.has(navn);
            const godkendtKm = rows.filter(k => k.status === "godkendt").reduce((s, k) => s + (k.km || 0), 0);
            const afventerCount = rows.filter(k => k.status === "afventer").length;
            return (
              <div key={navn} className="rounded-xl border border-border overflow-hidden">
                {/* Accordion-header */}
                <button
                  onClick={() => toggleAccordion(navn)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-secondary/20 hover:bg-secondary/40 transition-colors text-left"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${erAaben ? "rotate-180" : ""}`} />
                    <span className="font-semibold text-sm truncate">{navn}</span>
                    {afventerCount > 0 && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-500/10 px-2 py-0.5 rounded-full shrink-0">
                        <Clock className="w-3 h-3" /> {afventerCount} afventer
                      </span>
                    )}
                  </div>
                  <span className="text-[13px] text-muted-foreground shrink-0 ml-3">
                    {godkendtKm > 0 ? `${Math.round(godkendtKm * 10) / 10} km godkendt` : "0 km godkendt"}
                  </span>
                </button>

                {/* Accordion-indhold */}
                {erAaben && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                      <thead>
                        <tr className="bg-secondary/10 border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                          <th className="px-3 py-2 w-8">
                            {rows.some(r => r.status === "afventer") && (
                              <input type="checkbox"
                                className="rounded border-border cursor-pointer"
                                checked={rows.filter(r => r.status === "afventer").every(r => selectedKoersel.has(r.id || r._id))}
                                onChange={e => {
                                  const ids = rows.filter(r => r.status === "afventer").map(r => r.id || r._id);
                                  setSelectedKoersel(prev => {
                                    const next = new Set(prev);
                                    if (e.target.checked) ids.forEach(i => next.add(i));
                                    else ids.forEach(i => next.delete(i));
                                    return next;
                                  });
                                }}
                              />
                            )}
                          </th>
                          <th className="px-3 py-2 font-semibold">Dato</th>
                          <th className="px-3 py-2 font-semibold">Fra</th>
                          <th className="px-3 py-2 font-semibold">Til</th>
                          <th className="px-3 py-2 font-semibold">Formål</th>
                          <th className="px-3 py-2 font-semibold text-right">Km</th>
                          <th className="px-3 py-2 font-semibold text-right">Pris</th>
                          <th className="px-3 py-2 font-semibold">Note</th>
                          <th className="px-3 py-2 font-semibold">Status</th>
                          <th className="px-3 py-2 font-semibold text-right">Aktioner</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.sort((a, b) => (a.dato || "").localeCompare(b.dato || "")).map((k) => {
                          const id = k.id || k._id;
                          const erAfvisAaben = afvisId === id;
                          return (
                            <Fragment key={id || k.dato}>
                              <tr className="border-b border-border last:border-0 align-middle">
                                <td className="px-3 py-2 w-8">
                                  {k.status === "afventer" && (
                                    <input type="checkbox"
                                      className="rounded border-border cursor-pointer"
                                      checked={selectedKoersel.has(id)}
                                      onChange={() => toggleKoerselSelect(id)}
                                    />
                                  )}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">{k.dato || "—"}</td>
                                <td className="px-3 py-2">{k.fra_adresse || "—"}</td>
                                <td className="px-3 py-2">{k.til_adresse || "—"}</td>
                                <td className="px-3 py-2 text-muted-foreground">{k.formaal || <span className="text-muted-foreground/50 italic">—</span>}</td>
                                <td className="px-3 py-2 text-right whitespace-nowrap font-semibold">{k.km != null ? `${k.km} km` : "—"}</td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">{k.km != null ? `${(k.km * 3.94).toFixed(2).replace(".", ",")} kr` : "—"}</td>
                                <td className="px-3 py-2 text-muted-foreground max-w-[160px] truncate">{k.note || ""}</td>
                                <td className="px-3 py-2"><KoerselStatusChip status={k.status} /></td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center justify-end gap-1.5">
                                    {k.status === "afventer" && (
                                      <>
                                        <button onClick={() => godkend(k)} disabled={busy === id}
                                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-green-500 text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
                                          {busy === id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Godkend
                                        </button>
                                        <button onClick={() => { setAfvisId(id); setAfvisAarsag(""); }} disabled={busy === id}
                                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-red-500/40 text-red-500 text-[12px] font-semibold hover:bg-red-500/10 disabled:opacity-50 transition-colors">
                                          <X className="w-3 h-3" /> Afvis
                                        </button>
                                      </>
                                    )}
                                    {k.status === "afvist" && k.afvisning_aarsag && (
                                      <span className="text-[12px] text-muted-foreground italic mr-1">{k.afvisning_aarsag}</span>
                                    )}
                                    <button
                                      onClick={() => { setSletBekraeft(k); setSletAarsag(""); }}
                                      disabled={busy === id}
                                      title="Slet kørsel"
                                      className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 disabled:opacity-50 transition-colors">
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              {erAfvisAaben && (
                                <tr className="border-b border-border bg-red-500/5">
                                  <td colSpan={10} className="px-3 py-2">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                      <input type="text" autoFocus value={afvisAarsag} onChange={e => setAfvisAarsag(e.target.value)}
                                        placeholder="Årsag til afvisning"
                                        className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-background text-[13px] focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
                                      <div className="flex items-center gap-1.5">
                                        <button onClick={() => afvis(k)} disabled={busy === id || !afvisAarsag.trim()}
                                          className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
                                          {busy === id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Afvis"}
                                        </button>
                                        <button onClick={() => { setAfvisId(null); setAfvisAarsag(""); }}
                                          className="px-3 py-1.5 rounded-lg border border-border text-[12px] font-semibold hover:bg-secondary transition-colors">Annullér</button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                              {sletBekraeft && (sletBekraeft.id || sletBekraeft._id) === id && (
                                <tr className="border-b border-border bg-red-500/5">
                                  <td colSpan={10} className="px-3 py-2">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                      <span className="text-[13px] font-semibold text-red-600 flex items-center gap-1.5">
                                        <Trash2 className="w-3.5 h-3.5" /> Slet denne kørsel?
                                      </span>
                                      <input type="text" value={sletAarsag} onChange={e => setSletAarsag(e.target.value)}
                                        placeholder="Årsag (valgfri)"
                                        className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-background text-[13px] focus:outline-none focus:ring-2 focus:ring-red-500/30" />
                                      <div className="flex items-center gap-1.5">
                                        <button onClick={() => sletKoersel(sletBekraeft)} disabled={busy === id}
                                          className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-1">
                                          {busy === id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Slet
                                        </button>
                                        <button onClick={() => { setSletBekraeft(null); setSletAarsag(""); }}
                                          className="px-3 py-1.5 rounded-lg border border-border text-[12px] font-semibold hover:bg-secondary transition-colors">Annullér</button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Papirkurv-visning */}
      {statusFilter === "papirkurv" && (
        <div className="flex flex-col gap-2">
          {papirkurvLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : papirkurvData.length === 0 ? (
            <div className="rounded-xl border border-border py-10 text-center">
              <p className="text-sm text-muted-foreground italic">Papirkurven er tom for denne periode</p>
            </div>
          ) : (
            <div className="rounded-xl border border-border overflow-hidden">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-secondary/10 border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 font-semibold">Dato</th>
                    <th className="px-3 py-2 font-semibold">Medarbejder</th>
                    <th className="px-3 py-2 font-semibold">Fra</th>
                    <th className="px-3 py-2 font-semibold">Til</th>
                    <th className="px-3 py-2 font-semibold">Formål</th>
                    <th className="px-3 py-2 font-semibold text-right">Km</th>
                    <th className="px-3 py-2 font-semibold">Status ved sletning</th>
                    <th className="px-3 py-2 font-semibold">Slettet af</th>
                    <th className="px-3 py-2 font-semibold">Årsag</th>
                    <th className="px-3 py-2 font-semibold text-right">Gendan</th>
                  </tr>
                </thead>
                <tbody>
                  {papirkurvData.map(k => {
                    const id = k.id || k._id;
                    return (
                      <tr key={id} className="border-b border-border last:border-0 align-middle">
                        <td className="px-3 py-2 whitespace-nowrap">{k.dato || "—"}</td>
                        <td className="px-3 py-2">{k.medarbejder_navn || "—"}</td>
                        <td className="px-3 py-2">{k.fra_adresse || "—"}</td>
                        <td className="px-3 py-2">{k.til_adresse || "—"}</td>
                        <td className="px-3 py-2 text-muted-foreground">{k.formaal || "—"}</td>
                        <td className="px-3 py-2 text-right font-semibold">{k.km != null ? `${k.km} km` : "—"}</td>
                        <td className="px-3 py-2"><KoerselStatusChip status={k.status} /></td>
                        <td className="px-3 py-2 text-muted-foreground text-[12px]">{k.arkiveret_af || "—"}{k.arkiveret_at ? ` · ${k.arkiveret_at.slice(0,10)}` : ""}</td>
                        <td className="px-3 py-2 text-muted-foreground text-[12px] italic">{k.slettet_aarsag || ""}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => gendanKoersel(k)} disabled={busy === id}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-border text-[12px] font-semibold hover:bg-secondary disabled:opacity-50 transition-colors ml-auto">
                            {busy === id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Gendan
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {eksportOpen && (
        <KoerselEksportModal token={token} onClose={() => setEksportOpen(false)} />
      )}
    </div>
  );
}

// ── Opfølgning #60 (DEL 6): "ANMODNING OM ÆNDRING"-sektion ──────────────────
// Godkender-visning under "Stempling til godkendelse"-fanen. Henter alle
// afventende vagt-ændringsanmodninger (mine=false) og lader godkenderen
// godkende/afvise dem.
function aendringTekst(a) {
  const navn = VAGTTYPE_META[a?.ny_vagttype]?.label || a?.ny_vagttype || "Ændring";
  if (a?.omfang === "delvis" && (a?.fra_tid || a?.til_tid)) {
    const fra = (a.fra_tid || "").slice(0, 5);
    const til = (a.til_tid || "").slice(0, 5);
    return `${navn}, kl. ${fra}${til ? `-${til}` : ""}`;
  }
  return `${navn} (hele vagten)`;
}

function AnmodningerGodkendelse({ token }) {
  const hdrs = { Authorization: `Bearer ${token}` };
  const [anmodninger, setAnmodninger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // id under behandling
  const [afvisId, setAfvisId] = useState(null); // id i afvis-modus
  const [afvisAarsag, setAfvisAarsag] = useState("");
  const [open, setOpen] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/vagt-aendring-anmodninger?status=afventer&mine=false`, { headers: hdrs })
      .then(r => setAnmodninger(r.data.anmodninger || []))
      .catch(() => setAnmodninger([]))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const godkend = async (id) => {
    setBusy(id);
    try {
      await axios.post(`${API}/admin/vagt-aendring-anmodning/${id}/godkend`, {}, { headers: hdrs });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunne ikke godkende anmodningen");
    }
    setBusy(null);
  };

  const afvis = async (id) => {
    setBusy(id);
    try {
      await axios.post(`${API}/admin/vagt-aendring-anmodning/${id}/afvis`, { aarsag: afvisAarsag.trim() }, { headers: hdrs });
      setAfvisId(null);
      setAfvisAarsag("");
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunne ikke afvise anmodningen");
    }
    setBusy(null);
  };

  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-3 bg-amber-500/5 border-b border-border text-left">
        <div className="flex items-center gap-2">
          <ChevronDown className={`w-4 h-4 text-amber-500 transition-transform ${open ? "rotate-180" : ""}`} />
          <div>
            <h2 className="text-sm font-bold">Anmodning om ændring</h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">{anmodninger.length} afventer</p>
          </div>
        </div>
      </button>
      {open && (
        loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : anmodninger.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-8 text-center">Ingen anmodninger afventer godkendelse</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-[13px]">
              <thead>
                <tr className="bg-secondary/50 border-b border-border">
                  {["Medarbejder", "Dato", "Ændring", "Anmodt af", "Status", ""].map(h => (
                    <th key={h} className="text-left px-3 py-2.5 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {anmodninger.map(a => (
                  <Fragment key={a.id}>
                    <tr className="border-b border-border last:border-0">
                      <td className="px-3 py-2.5 font-semibold">{a.medarbejder_navn || "—"}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{a.dato || "—"}</td>
                      <td className="px-3 py-2.5">{aendringTekst(a)}</td>
                      <td className="px-3 py-2.5">{a.anmodt_af_navn || "—"}</td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-amber-700 bg-amber-500/10 px-2 py-0.5 rounded-full">
                          <Clock className="w-3 h-3" /> Afventer
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5 justify-end">
                          <button
                            onClick={() => godkend(a.id)}
                            disabled={busy === a.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-500 text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
                          >
                            {busy === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            Godkend
                          </button>
                          <button
                            onClick={() => { setAfvisId(afvisId === a.id ? null : a.id); setAfvisAarsag(""); }}
                            disabled={busy === a.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-500/40 text-red-500 text-[13px] font-semibold hover:bg-red-500/10 disabled:opacity-50 transition-colors"
                          >
                            <X className="w-3.5 h-3.5" /> Afvis
                          </button>
                        </div>
                      </td>
                    </tr>
                    {afvisId === a.id && (
                      <tr className="bg-red-500/5 border-b border-border">
                        <td colSpan={6} className="px-3 py-3">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <input
                              value={afvisAarsag}
                              onChange={e => setAfvisAarsag(e.target.value)}
                              placeholder="Årsag til afvisning (valgfri)"
                              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-[13px] focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
                            />
                            <button
                              onClick={() => afvis(a.id)}
                              disabled={busy === a.id}
                              className="px-3 py-2 rounded-lg bg-red-500 text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
                            >
                              Bekræft afvisning
                            </button>
                            <button
                              onClick={() => { setAfvisId(null); setAfvisAarsag(""); }}
                              className="px-3 py-2 rounded-lg border border-border text-[13px] font-semibold hover:bg-secondary transition-colors"
                            >
                              Annullér
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

// ── Opfølgning #60 (DEL 6): "DINE ANMODNINGER"-boks ─────────────────────────
// Vises på brugerens egen vagtplan. Henter brugerens egne afventende
// vagt-ændringsanmodninger (mine=true). Skjules helt når listen er tom.
// `version` bumpes af forælderen efter en ny anmodning sendes → re-fetch.
function DineAnmodningerBox({ token, version }) {
  const hdrs = { Authorization: `Bearer ${token}` };
  const [anmodninger, setAnmodninger] = useState([]);

  useEffect(() => {
    let aktiv = true;
    axios.get(`${API}/admin/vagt-aendring-anmodninger?status=afventer&mine=true`, { headers: hdrs })
      .then(r => { if (aktiv) setAnmodninger(r.data.anmodninger || []); })
      .catch(() => { if (aktiv) setAnmodninger([]); });
    return () => { aktiv = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, version]);

  if (anmodninger.length === 0) return null;

  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 mb-3">
      <div className="flex items-center gap-2 mb-1.5">
        <Clock className="w-4 h-4 text-amber-600" />
        <h3 className="text-[13px] font-bold uppercase tracking-wide text-amber-700">Dine anmodninger</h3>
      </div>
      <ul className="space-y-1">
        {anmodninger.map(a => (
          <li key={a.id} className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[13px]">
            <span className="text-amber-600">·</span>
            <span className="font-semibold">{a.medarbejder_navn}</span>
            <span className="text-muted-foreground">, {a.dato} – {a.ny_vagttype}</span>
            {a.fra_tid && <span className="text-muted-foreground">fra kl. {(a.fra_tid || "").slice(0, 5)}</span>}
            <span className="ml-1 inline-flex items-center gap-1 text-[12px] font-semibold text-amber-700 bg-amber-500/15 px-2 py-0.5 rounded-full">
              Afventer godkendelse
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SygefraværBoks({ token, userName }) {
  const hdrs = { Authorization: `Bearer ${token}` };
  const [sygedage, setSygedage] = useState(null);

  useEffect(() => {
    if (!userName) return;
    const til = new Date();
    const fra = new Date(til);
    fra.setFullYear(fra.getFullYear() - 1);
    const fraIso = fra.toISOString().slice(0, 10);
    const tilIso = til.toISOString().slice(0, 10);
    axios.get(`${API}/hub/vagtplan/alle?fra_dato=${fraIso}&til_dato=${tilIso}`, { headers: hdrs })
      .then(r => {
        const vagter = r.data.vagter || [];
        const SYG_TYPER = new Set(["Syg", "Syg (delvis)", "Barnets første sygedag"]);
        const count = vagter.filter(v => v.medarbejder === userName && SYG_TYPER.has(v.vagttype)).length;
        setSygedage(count);
      })
      .catch(() => setSygedage(0));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userName]);

  if (sygedage === null) return null;

  return (
    <div className="rounded-xl border border-border bg-secondary/10 px-4 py-3 mb-3 inline-flex flex-col gap-0.5 max-w-xs">
      <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Sygefravær (12 mdr.)</span>
      <span className="text-lg font-bold tabular-nums">{sygedage} {sygedage === 1 ? "dag" : "dage"}</span>
    </div>
  );
}

function StempelGodkendelse({ token }) {
  const hdrs = { Authorization: `Bearer ${token}` };
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [filterDato, setFilterDato] = useState("");
  const [filterStatus, setFilterStatus] = useState("pending"); // "pending" | "approved" | "ikke_godkendt" | "alle"
  const [afvisModal, setAfvisModal] = useState(null); // {clock_id, medarbejder, dato} ved soft-delete
  const [editRecord, setEditRecord] = useState(null); // record der redigeres
  const [editInd, setEditInd] = useState("");
  const [editUd, setEditUd] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editDato, setEditDato] = useState("");
  const [editVagttype, setEditVagttype] = useState("");
  const [editLokation, setEditLokation] = useState("");
  const [editOverarbejdeNote, setEditOverarbejdeNote] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Åbne vagter (planlagte vagter i fortiden uden stempling).
  // Opfølgning #14: Listen vises som separat sektion fra stemplinger.
  // Dage med stempling viser KUN clock_record-rækker — den planlagte vagt
  // dukker ikke op som "Åben vagt" (filtreres serverside i
  // /admin/aabne-vagter, der ekskluderer (medarbejder, dato)-par hvor
  // der findes en non-rejected clock_record). Stemplingen er sandheden;
  // vagtens timer-felt må aldrig bruges som proxy.
  const [aabneVagter, setAabneVagter] = useState([]);
  const [aabneLoading, setAabneLoading] = useState(true);
  const [aabneBusy, setAabneBusy] = useState(null);
  const [manuelModal, setManuelModal] = useState(null); // {vagt} eller null
  const [sletModal, setSletModal] = useState(null); // {vagt} eller null
  const [lokationer, setLokationer] = useState([]); // tildelte clock-lokationer

  // #69: Accepteret foreslået udstempling — kun i hukommelsen indtil godkend klikkes.
  // { [clock_id]: "HH:MM" } — sættes af accepterForeslaaet, ryddes ved godkend/reload.
  const [acceptedForeslaaet, setAcceptedForeslaaet] = useState({});

  // Søgefelter
  const [searchQuery, setSearchQuery] = useState("");
  const [searchQueryAaben, setSearchQueryAaben] = useState("");

  // Multi-select state for bulk-handlinger
  const [selectedOpen, setSelectedOpen] = useState(new Set()); // vagt_id'er
  const [selectedStempling, setSelectedStempling] = useState(new Set()); // clock_id'er
  const [bulkBusy, setBulkBusy] = useState(false);
  // Opfølgning #32 (#2): foldbare sektioner. Stempling-godkendelse er det vigtigste
  // daglige arbejde → øverst, åben som default. Åbne vagter er undtagelser → nederst,
  // foldet sammen som default.
  const [stemplingOpen, setStemplingOpen] = useState(true);
  const [aabneOpen, setAabneOpen] = useState(false);
  // Opfølgning #32 (#4): auto-udstempel-popup før bulk-godkend.
  // autoUdModal = { records: [...], values: { clock_id: {clock_out, note} }, alleIds: [...] }
  const [autoUdModal, setAutoUdModal] = useState(null);
  const [autoUdBusy, setAutoUdBusy] = useState(false);
  // Opfølgning #35 (#1): bulk-godkend åbne vagter via tid+note-popup.
  // aabneGodkendModal = { vagter: [...], values: { vagt_id: {clock_in, clock_out, note} } }
  const [aabneGodkendModal, setAabneGodkendModal] = useState(null);
  const [aabneGodkendBusy, setAabneGodkendBusy] = useState(false);

  const load = () => {
    setLoading(true);
    const fraDefault = (() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); })();
    // Hent afventende (completed), godkendte og afviste (ikke godkendte) — komma-separeret liste
    axios.get(`${API}/admin/clock-records?status=completed,approved,rejected&fra_dato=${fraDefault}`, { headers: hdrs })
      .then(r => setRecords(r.data.records || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const loadAabne = () => {
    setAabneLoading(true);
    axios.get(`${API}/admin/aabne-vagter`, { headers: hdrs })
      .then(r => setAabneVagter(r.data.aabne_vagter || []))
      .catch(() => {})
      .finally(() => setAabneLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadAabne(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Lokationer bruges i manuel-modal
    axios.get(`${API}/admin/clock-lokationer`, { headers: hdrs })
      .then(r => setLokationer(r.data.lokationer || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Nulstil selection når filtre ændres
  useEffect(() => { setSelectedStempling(new Set()); }, [filterStatus, filterDato]);
  useEffect(() => { setSelectedOpen(new Set()); }, [aabneVagter.length]); // også når listen reloades

  const markerSomVagttype = async (vagt, vagttype) => {
    setAabneBusy(vagt.vagt_id);
    try {
      await axios.put(
        `${API}/hub/vagtplan/${vagt.vagt_id}`,
        { vagttype, fra: "", til: "", timer: 0 },
        { headers: hdrs }
      );
      // Opfølgning #57 (DEL 4): re-fetch åbne vagter efter konvertering
      // (fjerner den konverterede vagt og opdaterer bemærkninger).
      await loadAabne();
    } catch (e) {
      alert(e?.response?.data?.detail || "Fejl ved opdatering");
    }
    setAabneBusy(null);
  };

  const sletMedArkiv = async (vagt, aarsag) => {
    setAabneBusy(vagt.vagt_id);
    try {
      await axios.delete(
        `${API}/hub/vagtplan/${vagt.vagt_id}/med-arkiv`,
        { headers: hdrs, data: { aarsag } }
      );
      setAabneVagter(prev => prev.filter(v => v.vagt_id !== vagt.vagt_id));
      setSletModal(null);
    } catch (e) {
      alert(e?.response?.data?.detail || "Fejl ved sletning");
    }
    setAabneBusy(null);
  };

  const opretManuelStempling = async (vagt, payload) => {
    setAabneBusy(vagt.vagt_id);
    try {
      await axios.post(
        `${API}/admin/clock-records/manuel`,
        {
          medarbejder: vagt.medarbejder,
          medarbejder_item_id: vagt.medarbejder_item_id,
          vagt_id: vagt.vagt_id,
          dato: vagt.dato,
          clock_in_time: payload.clock_in_time,
          clock_out_time: payload.clock_out_time,
          lokation: payload.lokation,
          note: payload.note,
        },
        { headers: hdrs }
      );
      // Refresh både records og åbne vagter
      await Promise.all([
        new Promise(res => { load(); res(); }),
        new Promise(res => { loadAabne(); res(); }),
      ]);
      setManuelModal(null);
    } catch (e) {
      alert(e?.response?.data?.detail || "Fejl ved oprettelse af manuel stempling");
    }
    setAabneBusy(null);
  };

  const godkend = async (clock_id) => {
    setBusy(clock_id);
    try {
      // #69: hvis der er en accepteret foreslået tid i hukommelsen, gem den først
      const pendingUd = acceptedForeslaaet[clock_id];
      if (pendingUd) {
        await axios.post(`${API}/admin/clock-records/${clock_id}/accepter-foreslaaet`, {}, { headers: hdrs });
        setAcceptedForeslaaet(prev => { const n = { ...prev }; delete n[clock_id]; return n; });
      }
      await axios.post(`${API}/admin/clock-records/${clock_id}/godkend`, {}, { headers: hdrs });
      setRecords(prev => prev.map(r => r.clock_id === clock_id ? {
        ...r,
        status: "approved",
        ...(pendingUd ? { clock_out: pendingUd, foreslaaet_clock_out: null, glemt_udstempling: false } : {}),
      } : r));
    } catch { alert("Fejl ved godkendelse"); }
    setBusy(null);
  };

  // Korrektion af auto-udstemplet record (Bug 5)
  const accepterKorrektion = async (clock_id) => {
    setBusy(clock_id);
    try {
      await axios.post(`${API}/admin/clock-records/${clock_id}/accepter-korrektion`, {}, { headers: hdrs });
      await load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 404) {
        alert("Ingen korrektion at acceptere — opdater siden og prøv igen.");
      } else {
        alert(detail || "Fejl ved accept af korrektion");
      }
      await load();
    }
    setBusy(null);
  };

  // #69: "Accepter foreslået" opdaterer KUN in-memory — ingen backend-kald, ingen reload.
  // Gemmes til serveren når godkend-knappen klikkes (se godkend() ovenfor).
  const accepterForeslaaet = (clock_id) => {
    const r = records.find(x => x.clock_id === clock_id);
    if (!r) return;
    const tid = r.foreslaaet_clock_out;
    if (!tid) return;
    // Beregn HH:MM fra ISO-tid eller rå HH:MM
    let hhmm = tid;
    try {
      const m = String(tid).match(/T(\d{2}:\d{2})/);
      if (m) hhmm = m[1];
    } catch {}
    setAcceptedForeslaaet(prev => ({ ...prev, [clock_id]: hhmm }));
  };

  const saetKorrektion = async (clock_id) => {
    const tid = window.prompt("Indtast korrekt udstemplingstidspunkt (HH:MM):", "");
    if (tid === null) return;
    const hhmm = (tid || "").trim().slice(0, 5);
    if (!/^\d{2}:\d{2}$/.test(hhmm)) { alert("Format skal være HH:MM"); return; }
    setBusy(clock_id);
    try {
      await axios.post(`${API}/admin/clock-records/${clock_id}/saet-korrektion`, { clock_out: hhmm }, { headers: hdrs });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Fejl ved opdatering"); }
    setBusy(null);
  };

  const afvisKorrektion = async (clock_id) => {
    if (!window.confirm("Afvis medarbejderens korrektion? Den oprindelige auto-udstempling kl. 23:59 bliver gældende.")) return;
    setBusy(clock_id);
    try {
      await axios.post(`${API}/admin/clock-records/${clock_id}/afvis-korrektion`, {}, { headers: hdrs });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Fejl ved afvisning"); }
    setBusy(null);
  };

  const openEdit = (r) => {
    setEditRecord(r);
    setEditInd(fmtTid(r.clock_in));
    setEditUd(fmtTid(r.clock_out));
    setEditNote(r.note || "");
    setEditDato(r.dato || "");
    setEditVagttype(r.vagttype || "");
    // Opfølgning #57 (1a): vis backend-forenklet by-lokation hvis tilgængelig.
    setEditLokation(r.clock_in_visning || r.clock_in_lokation || "");
    setEditOverarbejdeNote("");
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      const payload = {
        clock_in: editInd,
        clock_out: editUd,
        note: editOverarbejdeNote.trim() ? `${editOverarbejdeNote.trim()}${editNote.trim() ? ` · ${editNote.trim()}` : ""}` : editNote,
      };
      // Backend understøtter primært clock_in/clock_out/note — ekstra felter sendes
      // best-effort og ignoreres hvis ikke implementeret server-side.
      if (editDato) payload.dato = editDato;
      if (editVagttype !== editRecord.vagttype) payload.vagttype = editVagttype;
      // Sammenlign mod den værdi vi seedede (clock_in_visning > clock_in_lokation),
      // så en uændret forenklet by-visning ikke fejlagtigt sendes som ny lokation.
      const seededLokation = editRecord.clock_in_visning || editRecord.clock_in_lokation || "";
      if (editLokation !== seededLokation) payload.clock_in_lokation = editLokation;
      await axios.put(`${API}/admin/clock-records/${editRecord.clock_id}`,
        payload, { headers: hdrs });
      await load();
      setEditRecord(null);
    } catch { alert("Fejl ved gem"); }
    setEditSaving(false);
  };

  const openAfvisModal = (r) => {
    setAfvisModal({
      clock_id: r.clock_id,
      medarbejder: r.medarbejder,
      dato: r.dato,
    });
  };

  const handleAfvisConfirm = async (rejection_reason) => {
    if (!afvisModal) return;
    const clock_id = afvisModal.clock_id;
    setBusy(clock_id);
    try {
      await axios.patch(
        `${API}/admin/clock-records/${clock_id}/afvis`,
        { rejection_reason },
        { headers: hdrs },
      );
      setRecords(prev => prev.map(r => r.clock_id === clock_id
        ? { ...r, status: "rejected", ikke_godkendt: true, rejection_reason }
        : r,
      ));
      setAfvisModal(null);
    } catch { alert("Fejl ved markering"); }
    setBusy(null);
  };

  // ─── Bulk-handlinger ───────────────────────────────────────────────
  // Godkend en liste af clock_ids direkte (uden auto-udstempel-tjek).
  const godkendIds = async (ids) => {
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await axios.post(`${API}/admin/clock-records/${id}/godkend`, {}, { headers: hdrs });
        ok++;
      } catch { fail++; }
    }
    return { ok, fail };
  };

  // Stempling: godkend valgte. Opfølgning #32 (#4): hvis nogen af de valgte er
  // auto-udstemplede (glemt at stemple ud), vises en popup FØR godkendelse, hvor
  // superadmin skal angive korrekt udstemplingstid + note pr. record.
  const handleBulkGodkendStempling = async () => {
    if (!selectedStempling.size) return;
    setBulkBusy(true);
    const alleIds = [...selectedStempling];
    let autoRecords = [];
    try {
      const r = await axios.post(`${API}/admin/clock-records/auto-udstemplet-info`,
        { clock_ids: alleIds }, { headers: hdrs });
      autoRecords = r.data?.auto_records || [];
    } catch { /* hvis info-kald fejler, godkend som normalt */ }

    if (autoRecords.length > 0) {
      // Åbn popup: udfyld tid+note pr. auto-record. De øvrige (ikke-auto) godkendes
      // sammen når popup'en bekræftes.
      const autoSet = new Set(autoRecords.map(a => a.clock_id));
      const ovrigeIds = alleIds.filter(id => !autoSet.has(id));
      const values = {};
      autoRecords.forEach(a => {
        values[a.clock_id] = {
          clock_out: a.foreslaaet_clock_out || "",  // planlagt sluttid, tom for ad-hoc
          note: "Glemt at stemple ud",
        };
      });
      setAutoUdModal({ records: autoRecords, values, ovrigeIds });
      setBulkBusy(false);
      return;
    }

    // Ingen auto-udstemplede → godkend direkte.
    const { ok, fail } = await godkendIds(alleIds);
    await load();
    setSelectedStempling(new Set());
    setBulkBusy(false);
    alert(`${ok} stempling${ok !== 1 ? "er" : ""} godkendt${fail ? ` · ${fail} fejlede` : ""}`);
  };

  // Bekræft auto-udstempel-popup: sæt clock_out + note pr. auto-record (PUT), godkend
  // dem + de øvrige valgte. Genbruger eksisterende korrektion-/godkend-endpoints.
  const handleAutoUdGodkendAlle = async () => {
    if (!autoUdModal) return;
    const { records, values, ovrigeIds } = autoUdModal;
    // Valider: alle skal have tid + note.
    for (const a of records) {
      const v = values[a.clock_id] || {};
      if (!v.clock_out || !v.clock_out.trim() || !v.note || !v.note.trim()) {
        alert("Udfyld korrekt udstemplingstid og note for alle vagter.");
        return;
      }
    }
    setAutoUdBusy(true);
    let ok = 0, fail = 0;
    try {
      for (const a of records) {
        const v = values[a.clock_id];
        try {
          // 1) sæt korrekt clock_out + note
          await axios.put(`${API}/admin/clock-records/${a.clock_id}`,
            { clock_out: v.clock_out.trim(), note: v.note.trim() }, { headers: hdrs });
          // 2) godkend
          await axios.post(`${API}/admin/clock-records/${a.clock_id}/godkend`, {}, { headers: hdrs });
          ok++;
        } catch { fail++; }
      }
      // Godkend de øvrige (ikke-auto) valgte.
      const r2 = await godkendIds(ovrigeIds);
      ok += r2.ok; fail += r2.fail;
    } finally {
      await load();
      setSelectedStempling(new Set());
      setAutoUdModal(null);
      setAutoUdBusy(false);
      alert(`${ok} stempling${ok !== 1 ? "er" : ""} godkendt${fail ? ` · ${fail} fejlede` : ""}`);
    }
  };

  // Stempling: afvis valgte (fælles note via prompt)
  const handleBulkAfvisStempling = async () => {
    if (!selectedStempling.size) return;
    const reason = window.prompt(`Angiv fælles årsag for at afvise ${selectedStempling.size} stempling${selectedStempling.size !== 1 ? "er" : ""}:`, "");
    if (reason === null) return; // annullér
    const rejection_reason = (reason || "").trim();
    if (!rejection_reason) { alert("Årsag kræves"); return; }
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of selectedStempling) {
      try {
        await axios.patch(`${API}/admin/clock-records/${id}/afvis`, { rejection_reason }, { headers: hdrs });
        ok++;
      } catch { fail++; }
    }
    await load();
    setSelectedStempling(new Set());
    setBulkBusy(false);
    alert(`${ok} stempling${ok !== 1 ? "er" : ""} afvist${fail ? ` · ${fail} fejlede` : ""}`);
  };

  // Stempling: slet valgte
  const handleBulkSletStempling = async () => {
    if (!selectedStempling.size) return;
    if (!window.confirm(`Slet ${selectedStempling.size} stempling${selectedStempling.size !== 1 ? "er" : ""}?`)) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of selectedStempling) {
      try {
        await axios.delete(`${API}/admin/clock-records/${id}`, { headers: hdrs });
        ok++;
      } catch { fail++; }
    }
    await load();
    setSelectedStempling(new Set());
    setBulkBusy(false);
    alert(`${ok} stempling${ok !== 1 ? "er" : ""} slettet${fail ? ` · ${fail} fejlede` : ""}`);
  };

  // Åbne vagter: markér valgte som syg/ferie
  const handleBulkMarkerVagttype = async (vagttype) => {
    if (!selectedOpen.size) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of selectedOpen) {
      try {
        await axios.put(`${API}/hub/vagtplan/${id}`, { vagttype, fra: "", til: "", timer: 0 }, { headers: hdrs });
        ok++;
      } catch { fail++; }
    }
    await loadAabne();
    setSelectedOpen(new Set());
    setBulkBusy(false);
    alert(`${ok} vagt${ok !== 1 ? "er" : ""} markeret som ${vagttype.toLowerCase()}${fail ? ` · ${fail} fejlede` : ""}`);
  };

  // Åbne vagter: slet valgte (med arkiv-årsag via prompt)
  const handleBulkSletAabne = async () => {
    if (!selectedOpen.size) return;
    const reason = window.prompt(`Angiv fælles årsag for at slette ${selectedOpen.size} vagt${selectedOpen.size !== 1 ? "er" : ""} (gemmes i arkiv):`, "");
    if (reason === null) return;
    const aarsag = (reason || "").trim();
    if (!aarsag) { alert("Årsag kræves"); return; }
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of selectedOpen) {
      try {
        await axios.delete(`${API}/hub/vagtplan/${id}/med-arkiv`, { headers: hdrs, data: { aarsag } });
        ok++;
      } catch { fail++; }
    }
    await loadAabne();
    setSelectedOpen(new Set());
    setBulkBusy(false);
    alert(`${ok} vagt${ok !== 1 ? "er" : ""} slettet${fail ? ` · ${fail} fejlede` : ""}`);
  };

  // Opfølgning #35 (#1): bulk-godkend åbne vagter. Åbner popup hvor superadmin
  // udfylder ind/ud-tid + note pr. vagt (foreslået = vagtens planlagte fra/til,
  // note default "Glemt at stemple ud"). Ved bekræftelse oprettes en manuel
  // stempling pr. vagt + godkendes — så de 72 åbne vagter kan klares i ét hug.
  const lokationForVagt = (vagt) => {
    const aktive = (lokationer || []).filter(l => l.aktiv !== false);
    const match = aktive.find(l => (l.by || "") === vagt.city);
    return (match?.navn) || vagt.city || "";
  };
  const handleBulkGodkendAabne = () => {
    if (!selectedOpen.size) return;
    const valgte = aabneVagter.filter(v => selectedOpen.has(v.vagt_id));
    if (!valgte.length) return;
    const values = {};
    valgte.forEach(v => {
      values[v.vagt_id] = {
        clock_in: v.fra || "",       // planlagt start (blank for ad-hoc/ukendt)
        clock_out: v.til || "",      // planlagt sluttid
        note: "Glemt at stemple ud",
        lokation: lokationForVagt(v),
      };
    });
    setAabneGodkendModal({ vagter: valgte, values });
  };

  const handleAabneGodkendAlle = async () => {
    if (!aabneGodkendModal) return;
    const { vagter, values } = aabneGodkendModal;
    // Valider: alle skal have ind+ud-tid + note.
    for (const v of vagter) {
      const x = values[v.vagt_id] || {};
      if (!x.clock_in?.trim() || !x.clock_out?.trim() || !x.note?.trim()) {
        alert("Udfyld ind-tid, ud-tid og note for alle vagter.");
        return;
      }
    }
    setAabneGodkendBusy(true);
    let ok = 0, fail = 0;
    try {
      for (const v of vagter) {
        const x = values[v.vagt_id];
        try {
          // 1) opret manuel stempling for vagten
          const r = await axios.post(`${API}/admin/clock-records/manuel`, {
            medarbejder: v.medarbejder,
            medarbejder_item_id: v.medarbejder_item_id,
            vagt_id: v.vagt_id,
            dato: v.dato,
            clock_in_time: x.clock_in.trim(),
            clock_out_time: x.clock_out.trim(),
            lokation: x.lokation || "",
            note: x.note.trim(),
          }, { headers: hdrs });
          // 2) godkend den oprettede record
          const cid = r.data?.record?.clock_id;
          if (cid) {
            await axios.post(`${API}/admin/clock-records/${cid}/godkend`, {}, { headers: hdrs });
          }
          ok++;
        } catch { fail++; }
      }
    } finally {
      await Promise.all([load(), loadAabne()]);
      setSelectedOpen(new Set());
      setAabneGodkendModal(null);
      setAabneGodkendBusy(false);
      alert(`${ok} vagt${ok !== 1 ? "er" : ""} godkendt${fail ? ` · ${fail} fejlede` : ""}`);
    }
  };

  const fmtTid = (iso) => {
    if (iso === null || iso === undefined || iso === "") return "—";
    const m = String(iso).match(/T(\d{2}:\d{2})/);
    return m ? m[1] : "—";
  };

  // Opfølgning #57 (1b): foreslaaet_clock_out kan være "HH:MM" eller ISO-datetime.
  const fmtForeslaaet = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    const s = typeof v === "string" ? v.trim() : String(v);
    if (/^\d{2}:\d{2}/.test(s)) return s.slice(0, 5); // allerede HH:MM
    return fmtTid(s); // ISO → HH:MM
  };

  const fmtTimer = (t) => {
    if (t == null) return "—";
    const num = typeof t === "number" ? t : parseFloat(t);
    if (!isFinite(num)) return "—";
    const h = Math.floor(num);
    const m = Math.round((num - h) * 60);
    if (h > 0 && m > 0) return `${h}t ${m}m`;
    if (h > 0) return `${h}t`;
    return `${m}m`;
  };

  // #69: beregn vagt-start/slut fra stempling + planlagte tider.
  // Medarbejder der stempler ind FØR vagt-start → vagt-start vises som planlagt_fra.
  // Udstempling rundes til nærmeste 5 min.
  const rund5 = (hhmm) => {
    if (!hhmm) return hhmm;
    const m = String(hhmm).match(/T?(\d{2}):(\d{2})/);
    if (!m) return hhmm;
    const total = Number(m[1]) * 60 + Math.round(Number(m[2]) / 5) * 5;
    return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  };

  const calcVagtTid = (r) => {
    const rawInd = fmtTid(r.clock_in);   // "HH:MM" eller "—"
    const rawUd  = fmtTid(r.clock_out);  // "HH:MM" eller "—"
    if (rawInd === "—") return { vagStart: "—", vagSlut: rawUd === "—" ? "—" : rund5(rawUd) };
    const planlagtFra = r.planlagt_fra || "";
    const erAdhoc = !!r.er_adhoc;
    let vagStart;
    if (!erAdhoc && planlagtFra && rawInd <= planlagtFra) {
      vagStart = planlagtFra;
    } else {
      vagStart = rund5(rawInd) || rawInd;
    }
    const vagSlut = rawUd === "—" ? "—" : (rund5(rawUd) || rawUd);
    return { vagStart, vagSlut };
  };

  const fmtDato = (iso) => {
    // Defensiv: tom/null/undefined → "—". Håndtér både "YYYY-MM-DD" og ISO-datetime.
    if (iso === null || iso === undefined || iso === "") return "—";
    const s = typeof iso === "string" ? iso : String(iso);
    try {
      // Hvis allerede en fuld ISO-datetime, brug som-er; ellers tilføj T00:00:00.
      const input = s.includes("T") ? s : `${s}T00:00:00`;
      const d = new Date(input);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("da-DK", { weekday: "short", day: "numeric", month: "short" });
    } catch { return "—"; }
  };

  const fmtAdresse = (r, type) => {
    if (!r) return "—";
    // Opfølgning #57 (1a): backend leverer en forenklet by-lokation i
    // clock_in_visning / clock_out_visning (kun bynavn ved predefineret afdeling,
    // ellers gade-adresse). Brug den direkte; fald tilbage til de gamle felter.
    const visning = type === "ind" ? r.clock_in_visning : r.clock_out_visning;
    const visningTrim = (visning || "").trim();
    if (visningTrim) return visningTrim;
    // Fald tilbage til den fulde adresse (clock_in_adresse_fuld) som backend kun
    // sender til superadmins — så godkenderen kan verificere den eksakte position.
    // Ikke-superadmins får kun den maskerede by/navn (privacy).
    const adresse = type === "ind"
      ? (r.clock_in_adresse_fuld || r.clock_in_adresse)
      : (r.clock_out_adresse_fuld || r.clock_out_adresse);
    const lokation = type === "ind" ? r.clock_in_lokation : r.clock_out_lokation;
    const adresseTrim = (adresse || "").trim();
    const lokationTrim = (lokation || "").trim();
    if (adresseTrim && lokationTrim && adresseTrim !== lokationTrim) return `${lokationTrim} · ${adresseTrim}`;
    return adresseTrim || lokationTrim || "—";
  };

  const datoFiltered = filterDato ? records.filter(r => r.dato === filterDato) : records;
  const isIkkeGodkendt = (r) => r.status === "rejected" || r.ikke_godkendt === true;
  const pending = datoFiltered.filter(r => r.status === "completed" && !isIkkeGodkendt(r));
  const approved = datoFiltered.filter(r => r.status === "approved" && !isIkkeGodkendt(r));
  const ikkeGodkendt = datoFiltered.filter(r => isIkkeGodkendt(r));
  const statusFiltered = filterStatus === "pending" ? pending
    : filterStatus === "approved" ? approved
    : filterStatus === "ikke_godkendt" ? ikkeGodkendt
    : [...pending, ...approved, ...ikkeGodkendt];

  // Søgefilter på medarbejder-navn (startsWith øverst, includes derefter)
  const visible = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return statusFiltered;
    const starter = statusFiltered.filter(r => (r.medarbejder_navn || r.medarbejder || "").toLowerCase().startsWith(q));
    const resten = statusFiltered.filter(r => {
      const navn = (r.medarbejder_navn || r.medarbejder || "").toLowerCase();
      return !navn.startsWith(q) && navn.includes(q);
    });
    return [...starter, ...resten];
  })();

  // Søgefilter på åbne vagter
  const filtredeAabneVagter = (() => {
    const q = searchQueryAaben.trim().toLowerCase();
    if (!q) return aabneVagter;
    const starter = aabneVagter.filter(v => (v.medarbejder || "").toLowerCase().startsWith(q));
    const resten = aabneVagter.filter(v => {
      const navn = (v.medarbejder || "").toLowerCase();
      return !navn.startsWith(q) && navn.includes(q);
    });
    return [...starter, ...resten];
  })();

  const fmtGodkendt = (iso) => {
    if (iso === null || iso === undefined || iso === "") return "";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
    } catch { return ""; }
  };

  return (
    // Opfølgning #32 (#2): flex-col + order — Stempling-godkendelse øverst (order-1/2),
    // Åbne vagter nederst (order-3). Modaler er fixed-overlays og påvirkes ikke af order.
    <div className="flex flex-col gap-4">
      {/* Opfølgning #60 (DEL 6): Anmodning om ændring — øverst (godkender-visning) */}
      <div className="order-0">
        <AnmodningerGodkendelse token={token} />
      </div>
      <div className="order-1 flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => setStemplingOpen(o => !o)} className="flex items-center gap-2 text-left">
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${stemplingOpen ? "rotate-180" : ""}`} />
          <div>
            <h2 className="text-sm font-bold">Stempling til godkendelse</h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">{pending.length} afventer · {approved.length} godkendt · {ikkeGodkendt.length} ikke godkendt</p>
          </div>
        </button>
        <div className={`w-full sm:w-auto flex flex-col gap-2 ${stemplingOpen ? "" : "hidden"}`}>
          {/* Søgefelt */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Søg medarbejder..."
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-background text-[13px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]/40" />
          </div>
          {/* Status-filter: hele segmentet på én linje. Dato-feltet lægger sig på
              egen linje under på mobil, så der er plads til alle 4 statusser. */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex rounded-lg border border-border overflow-hidden">
            {[
              { key: "pending", label: "Afventer" },
              { key: "approved", label: "Godkendt" },
              { key: "ikke_godkendt", label: "Ikke godkendte" },
              { key: "alle", label: "Alle" },
            ].map(opt => (
              <button key={opt.key} onClick={() => setFilterStatus(opt.key)}
                className={`flex-1 sm:flex-none px-2.5 sm:px-3 h-8 text-[13px] font-semibold whitespace-nowrap transition-colors ${filterStatus === opt.key ? "bg-[#FF2A6D] text-white" : "bg-background text-muted-foreground hover:bg-secondary"}`}>
                {opt.label}
              </button>
            ))}
          </div>
          <input type="date" value={filterDato} onChange={e => setFilterDato(e.target.value)}
            className="w-full sm:w-auto px-3 h-8 rounded-lg border border-border bg-background text-[13px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
          </div>
        </div>
      </div>

      {/* Åbne vagter (planlagte vagter i fortiden uden stempling) — nederst + foldbar */}
      <div className="order-3 rounded-xl border border-border overflow-hidden">
        <button onClick={() => setAabneOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-3 bg-amber-500/5 border-b border-border text-left">
          <div className="flex items-center gap-2">
            <ChevronDown className={`w-4 h-4 text-amber-500 transition-transform ${aabneOpen ? "rotate-180" : ""}`} />
            <div>
              <h3 className="text-sm font-bold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Åbne vagter
              </h3>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                Planlagte vagter i fortiden uden tilhørende stempling · sidste 30 dage
              </p>
            </div>
          </div>
          <span className="text-[13px] font-semibold text-amber-600 bg-amber-500/10 px-2 py-1 rounded-full">
            {aabneVagter.length} {aabneVagter.length === 1 ? "vagt" : "vagter"}
          </span>
        </button>
        {!aabneOpen ? null : aabneLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : aabneVagter.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-6 text-center">Ingen åbne vagter</p>
        ) : (
          <div className="overflow-x-auto">
            {/* Søgefelt åbne vagter */}
            <div className="relative px-3 pt-3 pb-2 border-b border-border">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input type="text" value={searchQueryAaben} onChange={e => setSearchQueryAaben(e.target.value)}
                placeholder="Søg medarbejder..."
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-background text-[13px] focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]/40" />
            </div>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-secondary/30 border-b border-border">
                  <th className="w-8 px-3 py-2">
                    {(() => {
                      const ids = filtredeAabneVagter.map(v => v.vagt_id);
                      const valgteSyn = ids.filter(id => selectedOpen.has(id));
                      const alleValgt = ids.length > 0 && valgteSyn.length === ids.length;
                      const nogenValgt = valgteSyn.length > 0 && !alleValgt;
                      const toggleAlle = () => {
                        if (alleValgt) setSelectedOpen(new Set());
                        else setSelectedOpen(new Set(ids));
                      };
                      return (
                        <button onClick={toggleAlle} title={alleValgt ? "Fjern alle" : "Vælg alle"}
                          className="flex items-center justify-center text-muted-foreground hover:text-foreground">
                          {alleValgt ? <CheckSquare className="w-3.5 h-3.5 text-blue-500" />
                            : nogenValgt ? <CheckSquare className="w-3.5 h-3.5 text-blue-500 opacity-60" />
                            : <Square className="w-3.5 h-3.5" />}
                        </button>
                      );
                    })()}
                  </th>
                  {["Medarbejder", "Dato", "Vagt", "Bemærkning", "Aktioner"].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtredeAabneVagter.map((v, i) => {
                  const isSelected = selectedOpen.has(v.vagt_id);
                  const toggleRow = () => {
                    setSelectedOpen(prev => {
                      const next = new Set(prev);
                      if (next.has(v.vagt_id)) next.delete(v.vagt_id);
                      else next.add(v.vagt_id);
                      return next;
                    });
                  };
                  return (
                  <tr key={v.vagt_id} className={`border-b border-border last:border-0 ${i % 2 === 1 ? "bg-secondary/10" : ""} ${isSelected ? "bg-blue-500/5" : ""}`}>
                    <td className="px-3 py-2.5 w-8">
                      <button onClick={toggleRow} className="text-muted-foreground hover:text-foreground">
                        {isSelected ? <CheckSquare className="w-3.5 h-3.5 text-blue-500" /> : <Square className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 font-semibold whitespace-nowrap">{v.medarbejder}</td>
                    <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{fmtDato(v.dato)}</td>
                    <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                      {v.fra && v.til ? `${v.fra}–${v.til}` : "—"}
                      {v.city ? <span className="ml-2 text-[13px] text-muted-foreground/70">· {v.city}</span> : null}
                      {v.note ? <span className="ml-2 italic text-[13px] text-muted-foreground/70">· {v.note}</span> : null}
                    </td>
                    {/* Opfølgning #57 (DEL 4): bemærkning (helligdag/ferie/lukket) */}
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {v.bemaerkning?.tekst
                        ? <span className="text-[13px] font-medium text-foreground">{v.bemaerkning.tekst}</span>
                        : <span className="text-muted-foreground">–</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        <button
                          onClick={() => markerSomVagttype(v, "Syg")}
                          disabled={aabneBusy === v.vagt_id}
                          className="px-2 py-1 rounded-lg border border-border text-[13px] font-semibold text-red-500 hover:bg-red-500/10 disabled:opacity-50 transition-colors whitespace-nowrap"
                          title="Markér vagten som syg"
                        >
                          Markér som syg
                        </button>
                        <button
                          onClick={() => markerSomVagttype(v, "Ferie/Fri")}
                          disabled={aabneBusy === v.vagt_id}
                          className="px-2 py-1 rounded-lg border border-border text-[13px] font-semibold text-emerald-600 hover:bg-emerald-500/10 disabled:opacity-50 transition-colors whitespace-nowrap"
                          title="Markér vagten som ferie/fri"
                        >
                          Markér som ferie/fri
                        </button>
                        <button
                          onClick={() => setManuelModal({ vagt: v })}
                          disabled={aabneBusy === v.vagt_id}
                          className="px-2 py-1 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity whitespace-nowrap"
                        >
                          Manuel stempling
                        </button>
                        <button
                          onClick={() => setSletModal({ vagt: v })}
                          disabled={aabneBusy === v.vagt_id}
                          title="Slet vagt (arkiveres)"
                          className="p-1 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-500 disabled:opacity-50 transition-colors"
                        >
                          {aabneBusy === v.vagt_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Manuel stempling-modal */}
      {manuelModal && (
        <ManuelStemplingModal
          vagt={manuelModal.vagt}
          lokationer={lokationer}
          onClose={() => setManuelModal(null)}
          onOpret={(payload) => opretManuelStempling(manuelModal.vagt, payload)}
          busy={aabneBusy === manuelModal.vagt.vagt_id}
        />
      )}

      {/* Slet-vagt-modal (med arkiv) */}
      {sletModal && (
        <SletVagtArkivModal
          vagt={sletModal.vagt}
          onClose={() => setSletModal(null)}
          onSlet={(aarsag) => sletMedArkiv(sletModal.vagt, aarsag)}
          busy={aabneBusy === sletModal.vagt.vagt_id}
        />
      )}

      {/* Afvis-modal (soft-delete: marker som ikke godkendt) */}
      {afvisModal && (
        <AfvisStemplingModal
          record={afvisModal}
          onClose={() => setAfvisModal(null)}
          onConfirm={handleAfvisConfirm}
          busy={busy === afvisModal.clock_id}
        />
      )}

      {/* Rediger-modal (Krav 6: udvidet med dato, vagttype, lokation) */}
      {editRecord && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-2xl shadow-2xl border border-border p-5 w-full max-w-sm space-y-4 max-h-[92vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-sm">Rediger stempling</h3>
                <p className="text-[13px] text-muted-foreground">{editRecord.medarbejder}{editRecord.dato ? ` · ${editRecord.dato}` : ""}</p>
              </div>
              <button onClick={() => setEditRecord(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>

            {/* SEKTION 1: Planlagt vagt */}
            <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-1">
              <p className="text-[11px] uppercase font-semibold tracking-wide text-muted-foreground">Planlagt vagt</p>
              {editRecord.planlagt_fra ? (
                <>
                  <p className="font-semibold text-sm">{editRecord.planlagt_fra} – {editRecord.planlagt_til}
                    {editRecord.city ? <span className="font-normal text-muted-foreground"> · {editRecord.city}</span> : null}
                  </p>
                  <p className="text-[13px] text-muted-foreground">
                    {editRecord.planlagt_vagttype || editRecord.vagttype || "Normal timer"}
                    {editRecord.planlagt_timer != null ? ` · ${editRecord.planlagt_timer}t planlagt` : ""}
                  </p>
                </>
              ) : (
                <p className="text-[13px] text-muted-foreground italic">
                  {editRecord.er_adhoc ? "Ad-hoc stempling — ingen planlagt vagt" : "Ingen planlagt vagt (hjælpe-vagt)"}
                </p>
              )}
            </div>

            {/* SEKTION 2: Faktisk stempling — redigerbar */}
            <div className="space-y-3">
              <p className="text-[11px] uppercase font-semibold tracking-wide text-muted-foreground">Faktisk stempling</p>
              <div>
                <label className="block text-[13px] text-muted-foreground mb-1">Dato</label>
                <input type="date" value={editDato} onChange={e => setEditDato(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[13px] text-muted-foreground mb-1">Check ind</label>
                  <input type="time" value={editInd} onChange={e => setEditInd(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
                  {editRecord.clock_in_visning && <p className="text-[12px] text-muted-foreground mt-0.5">{editRecord.clock_in_visning}</p>}
                </div>
                <div>
                  <label className="block text-[13px] text-muted-foreground mb-1">Check ud</label>
                  <input type="time" value={editUd} onChange={e => setEditUd(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
                  {editRecord.auto_udstemplet && <p className="text-[12px] text-amber-600 mt-0.5">⚠ Auto-udstemplet</p>}
                  {editRecord.clock_out_visning && !editRecord.auto_udstemplet && <p className="text-[12px] text-muted-foreground mt-0.5">{editRecord.clock_out_visning}</p>}
                </div>
              </div>
              {/* Foreslået tid */}
              {(editRecord.glemt_udstempling || (editRecord.foreslaaet_clock_out != null && editRecord.foreslaaet_clock_out !== "")) && (
                <div>
                  <label className="block text-[13px] text-muted-foreground mb-1">Foreslået tid (fra medarbejder)</label>
                  <div className="flex items-center gap-2">
                    <input type="text" readOnly value={fmtForeslaaet(editRecord.foreslaaet_clock_out)}
                      className="flex-1 px-3 py-2 rounded-xl border border-border bg-secondary/40 text-sm text-muted-foreground focus:outline-none" />
                    <button type="button"
                      onClick={async () => { await accepterForeslaaet(editRecord.clock_id); setEditRecord(null); }}
                      disabled={busy === editRecord.clock_id}
                      className="px-3 py-2 rounded-xl bg-green-500/10 text-green-600 text-sm font-semibold hover:bg-green-500/20 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5 whitespace-nowrap">
                      {busy === editRecord.clock_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      Acceptér
                    </button>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-[13px] text-muted-foreground mb-1">Vagttype</label>
                <select value={editVagttype} onChange={e => setEditVagttype(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30">
                  {VAGTTYPER.map(vt => (
                    <option key={vt || "normal"} value={vt}>{VAGTTYPE_META[vt]?.label || "Normal"}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[13px] text-muted-foreground mb-1">Lokation</label>
                <input value={editLokation} onChange={e => setEditLokation(e.target.value)}
                  placeholder="Fx Skarre Strandvej 11"
                  className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
              <div>
                <label className="block text-[13px] text-muted-foreground mb-1">Note</label>
                <textarea value={editNote} onChange={e => setEditNote(e.target.value)} rows={2}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
                  placeholder="Evt. note..." />
              </div>
            </div>

            {/* SEKTION 3: Beregnet vagt-tid (live preview) */}
            {editInd && editUd && (() => {
              const planlagtFra = editRecord?.planlagt_fra;
              const erAdhoc = editRecord?.er_adhoc;
              const rund5 = (hhmm) => {
                if (!hhmm) return hhmm;
                const [h, m] = hhmm.split(":").map(Number);
                const total = h * 60 + Math.round(m / 5) * 5;
                return `${String(Math.floor(total / 60) % 24).padStart(2,"0")}:${String(total % 60).padStart(2,"0")}`;
              };
              let vagStart, vagSlut;
              if (!erAdhoc && planlagtFra && editInd <= planlagtFra) {
                vagStart = planlagtFra;
              } else {
                vagStart = rund5(editInd);
              }
              vagSlut = rund5(editUd);
              const [sh, sm] = vagStart.split(":").map(Number);
              const [eh, em] = vagSlut.split(":").map(Number);
              const minutterI = sh * 60 + sm;
              const minutterU = eh * 60 + em;
              const diffMin = minutterU - minutterI;
              const timerStr = diffMin > 0 ? `${Math.floor(diffMin / 60)}t ${diffMin % 60}m` : "—";
              const planlagtTil = editRecord?.planlagt_til;
              let overMin = 0;
              if (planlagtTil && vagSlut > planlagtTil) {
                const [pt_h, pt_m] = planlagtTil.split(":").map(Number);
                overMin = minutterU - (pt_h * 60 + pt_m);
              }
              return (
                <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-2">
                  <p className="text-[11px] uppercase font-semibold tracking-wide text-muted-foreground">Beregnet vagt-tid</p>
                  <div className="grid grid-cols-2 gap-x-3 text-[13px]">
                    <span className="text-muted-foreground">Vagt-start:</span><span className="font-semibold">{vagStart}</span>
                    <span className="text-muted-foreground">Vagt-slut:</span><span className="font-semibold">{vagSlut}</span>
                    <span className="text-muted-foreground">Timer:</span><span className="font-semibold">{timerStr}</span>
                  </div>
                  {overMin > 30 && (
                    <div className="pt-2 border-t border-border">
                      <p className="text-amber-600 text-[12px] font-semibold flex items-center gap-1 mb-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" /> Vagten overskrider planlagt slut med {overMin} min. Begrundelse påkrævet.
                      </p>
                      <textarea value={editOverarbejdeNote} onChange={e => setEditOverarbejdeNote(e.target.value)} rows={2}
                        placeholder="Skriv hvorfor vagten varede længere..."
                        className="w-full px-3 py-2 rounded-lg border border-amber-500/40 bg-background text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Gem/Annullér */}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditRecord(null)} className="px-4 py-2 rounded-xl border border-border text-sm hover:bg-secondary transition-colors">Annuller</button>
              <button onClick={handleEditSave} disabled={editSaving || (() => {
                if (!editInd || !editUd || !editRecord?.planlagt_til || editRecord?.er_adhoc) return false;
                const rund5 = (hhmm) => { const [h,m] = hhmm.split(":").map(Number); const t = h*60+Math.round(m/5)*5; return `${String(Math.floor(t/60)%24).padStart(2,"0")}:${String(t%60).padStart(2,"0")}`; };
                const vagSlut = rund5(editUd);
                if (vagSlut <= editRecord.planlagt_til) return false;
                const [ph,pm] = editRecord.planlagt_til.split(":").map(Number);
                const [vh,vm] = vagSlut.split(":").map(Number);
                const overMin = (vh*60+vm) - (ph*60+pm);
                return overMin > 30 && !editOverarbejdeNote.trim();
              })()}
                className="px-4 py-2 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
                {editSaving ? "Gemmer..." : "Gem"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Opfølgning #32 (#4): auto-udstempel-popup før bulk-godkend */}
      {autoUdModal && (() => {
        const recs = autoUdModal.records;
        const alleUdfyldt = recs.every(a => {
          const v = autoUdModal.values[a.clock_id] || {};
          return v.clock_out && v.clock_out.trim() && v.note && v.note.trim();
        });
        const setVal = (cid, felt, val) => setAutoUdModal(m => ({
          ...m, values: { ...m.values, [cid]: { ...m.values[cid], [felt]: val } },
        }));
        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 pt-20">
            <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-2xl max-h-[82vh] overflow-hidden flex flex-col">
              <div className="px-5 py-4 border-b border-border">
                <h3 className="text-base font-bold flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" /> Nogle vagter blev automatisk stemplet ud
                </h3>
                <p className="text-[13px] text-muted-foreground mt-1">
                  {recs.length} medarbejder{recs.length !== 1 ? "e" : ""} blev auto-stemplet ud kl. 23:59 fordi de glemte at stemple ud.
                  Indtast korrekt udstemplingstid og en note for hver, før vagten godkendes.
                </p>
              </div>
              <div className="overflow-y-auto px-5 py-3 space-y-3">
                {recs.map(a => {
                  const v = autoUdModal.values[a.clock_id] || {};
                  return (
                    <div key={a.clock_id} className="rounded-xl border border-border p-3">
                      <p className="text-sm font-semibold">
                        {a.medarbejder}
                        <span className="text-muted-foreground font-normal">
                          {" · "}{a.dato}{" · "}{a.er_adhoc ? "ad-hoc" : `planlagt ${a.planlagt_fra || "?"}–${a.planlagt_til || "?"}`}
                          {a.lokation ? ` · ${a.lokation}` : ""}
                        </span>
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                        <div>
                          <label className="block text-[13px] text-muted-foreground mb-1">
                            Foreslået udstempling {a.er_adhoc ? "(intet planlagt, skriv selv)" : "(vagtens planlagte sluttid)"}
                          </label>
                          <input type="time" value={v.clock_out || ""}
                            onChange={e => setVal(a.clock_id, "clock_out", e.target.value)}
                            className="w-full px-3 h-9 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                        </div>
                        <div>
                          <label className="block text-[13px] text-muted-foreground mb-1">Note (påkrævet)</label>
                          <input type="text" value={v.note || ""}
                            onChange={e => setVal(a.clock_id, "note", e.target.value)}
                            placeholder="Glemt at stemple ud"
                            className="w-full px-3 h-9 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
                <button onClick={() => setAutoUdModal(null)} disabled={autoUdBusy}
                  className="px-4 py-2 rounded-xl border border-border text-sm hover:bg-secondary disabled:opacity-50 transition-colors">
                  Annullér
                </button>
                <button onClick={handleAutoUdGodkendAlle} disabled={autoUdBusy || !alleUdfyldt}
                  className="px-4 py-2 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-2">
                  {autoUdBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Godkend alle
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Opfølgning #35 (#1): bulk-godkend åbne vagter — tid+note pr. vagt */}
      {aabneGodkendModal && (() => {
        const vagter = aabneGodkendModal.vagter;
        const alleUdfyldt = vagter.every(v => {
          const x = aabneGodkendModal.values[v.vagt_id] || {};
          return x.clock_in?.trim() && x.clock_out?.trim() && x.note?.trim();
        });
        const setVal = (vid, felt, val) => setAabneGodkendModal(m => ({
          ...m, values: { ...m.values, [vid]: { ...m.values[vid], [felt]: val } },
        }));
        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 pt-20">
            <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-2xl max-h-[82vh] overflow-hidden flex flex-col">
              <div className="px-5 py-4 border-b border-border">
                <h3 className="text-base font-bold flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" /> Godkend åbne vagter
                </h3>
                <p className="text-[13px] text-muted-foreground mt-1">
                  {vagter.length} åbne vagt{vagter.length !== 1 ? "er" : ""} godkendes. Bekræft ind-/udstemplingstid
                  (foreslået = vagtens planlagte tider) og en note for hver.
                </p>
              </div>
              <div className="overflow-y-auto px-5 py-3 space-y-3">
                {vagter.map(v => {
                  const x = aabneGodkendModal.values[v.vagt_id] || {};
                  return (
                    <div key={v.vagt_id} className="rounded-xl border border-border p-3">
                      <p className="text-sm font-semibold">
                        {v.medarbejder}
                        <span className="text-muted-foreground font-normal">
                          {" · "}{v.dato}{" · "}{v.fra && v.til ? `planlagt ${v.fra}–${v.til}` : "ad-hoc"}
                          {x.lokation ? ` · ${x.lokation}` : ""}
                        </span>
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                        <div>
                          <label className="block text-[13px] text-muted-foreground mb-1">Stemplet ind</label>
                          <input type="time" value={x.clock_in || ""}
                            onChange={e => setVal(v.vagt_id, "clock_in", e.target.value)}
                            className="w-full px-3 h-9 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                        </div>
                        <div>
                          <label className="block text-[13px] text-muted-foreground mb-1">Stemplet ud</label>
                          <input type="time" value={x.clock_out || ""}
                            onChange={e => setVal(v.vagt_id, "clock_out", e.target.value)}
                            className="w-full px-3 h-9 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                          <label className="block text-[13px] text-muted-foreground mb-1">Note (påkrævet)</label>
                          <input type="text" value={x.note || ""}
                            onChange={e => setVal(v.vagt_id, "note", e.target.value)}
                            placeholder="Glemt at stemple ud"
                            className="w-full px-3 h-9 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
                <button onClick={() => setAabneGodkendModal(null)} disabled={aabneGodkendBusy}
                  className="px-4 py-2 rounded-xl border border-border text-sm hover:bg-secondary disabled:opacity-50 transition-colors">
                  Annullér
                </button>
                <button onClick={handleAabneGodkendAlle} disabled={aabneGodkendBusy || !alleUdfyldt}
                  className="px-4 py-2 rounded-xl bg-green-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-2">
                  {aabneGodkendBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Godkend alle
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <div className={`order-2 ${stemplingOpen ? "" : "hidden"}`}>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : visible.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-8 text-center">Ingen stempleringer</p>
      ) : (
        <div className="rounded-xl border border-border overflow-x-auto">
          <table className="w-full min-w-[900px] text-[13px]">
            <thead>
              <tr className="bg-secondary/50 border-b border-border">
                <th className="w-8 px-3 py-2.5">
                  {(() => {
                    const ids = visible.map(r => r.clock_id);
                    const valgteSyn = ids.filter(id => selectedStempling.has(id));
                    const alleValgt = ids.length > 0 && valgteSyn.length === ids.length;
                    const nogenValgt = valgteSyn.length > 0 && !alleValgt;
                    const toggleAlle = () => {
                      if (alleValgt) setSelectedStempling(new Set());
                      else setSelectedStempling(new Set(ids));
                    };
                    return (
                      <button onClick={toggleAlle} title={alleValgt ? "Fjern alle" : "Vælg alle"}
                        className="flex items-center justify-center text-muted-foreground hover:text-foreground">
                        {alleValgt ? <CheckSquare className="w-3.5 h-3.5 text-blue-500" />
                          : nogenValgt ? <CheckSquare className="w-3.5 h-3.5 text-blue-500 opacity-60" />
                          : <Square className="w-3.5 h-3.5" />}
                      </button>
                    );
                  })()}
                </th>
                {["Medarbejder","Dato","Ind","Ud","Foreslået ud","Timer","Ind fra","Ud fra","Note","Status",""].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => {
                const isApproved = r.status === "approved";
                const isIkkeOk = isIkkeGodkendt(r);
                const redigeretEfter = !!r.redigeret_efter_godkendelse;
                const isManuel = r.manuel === true || r.clock_in_via === "manuel" || r.clock_out_via === "manuel";
                const isAutoUdstemplet = !!r.auto_udstemplet;
                const harAfventerKorrektion = isAutoUdstemplet && r.korrektion_status === "afventer";
                // Opfølgning #57 (1b): glemt/auto-udstemplet + evt. foreslået tid.
                const erGlemtEllerAuto = !!r.glemt_udstempling || isAutoUdstemplet;
                const harForeslaaet = !!r.glemt_udstempling || (r.foreslaaet_clock_out != null && r.foreslaaet_clock_out !== "");
                // #69: er foreslået tid accepteret i hukommelsen (ikke gemt endnu)?
                const erAendretIkkeGemt = !!acceptedForeslaaet[r.clock_id];
                const isSelected = selectedStempling.has(r.clock_id);
                const toggleRow = (e) => {
                  e.stopPropagation();
                  setSelectedStempling(prev => {
                    const next = new Set(prev);
                    if (next.has(r.clock_id)) next.delete(r.clock_id);
                    else next.add(r.clock_id);
                    return next;
                  });
                };
                return (
                  <Fragment key={r.clock_id}>
                  <tr
                    onClick={() => openEdit(r)}
                    className={`border-b border-border last:border-0 cursor-pointer hover:bg-secondary/30 transition-colors ${i % 2 === 1 ? "bg-secondary/10" : ""} ${isIkkeOk ? "opacity-60" : ""} ${isSelected ? "bg-blue-500/5" : ""}`}>
                    <td className="px-3 py-2.5 w-8" onClick={e => e.stopPropagation()}>
                      <button onClick={toggleRow} className="text-muted-foreground hover:text-foreground">
                        {isSelected ? <CheckSquare className="w-3.5 h-3.5 text-blue-500" /> : <Square className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 font-semibold whitespace-nowrap">{r.medarbejder}</td>
                    <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{fmtDato(r.dato)}</td>
                    {/* #69: IND/UD viser beregnet vagt-tid (planlagt start hvis tidlig, rund5 ellers) */}
                    {(() => {
                      const { vagStart, vagSlut } = calcVagtTid(r);
                      return (
                        <>
                          <td className="px-3 py-2.5 tabular-nums whitespace-nowrap" title={`Råt stempel: ${fmtTid(r.clock_in)}`}>{vagStart}</td>
                          <td className="px-3 py-2.5 tabular-nums whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">
                              <span title={`Råt stempel: ${fmtTid(r.clock_out)}`}>{erAendretIkkeGemt ? (acceptedForeslaaet[r.clock_id] || vagSlut) : vagSlut}</span>
                              {erGlemtEllerAuto && !erAendretIkkeGemt && (
                                <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0"
                                  title="Auto-/glemt udstempling — tjek foreslået tid" />
                              )}
                            </span>
                          </td>
                        </>
                      );
                    })()}
                    {/* Opfølgning #57 (1b): FORESLÅET UD */}
                    <td className="px-3 py-2.5 tabular-nums whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      {harForeslaaet ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="font-semibold text-foreground">{fmtForeslaaet(r.foreslaaet_clock_out)}</span>
                          {erAendretIkkeGemt ? (
                            <span className="px-2 py-0.5 rounded-lg bg-orange-500/15 text-orange-600 text-[12px] font-semibold inline-flex items-center gap-1">
                              <Check className="w-3 h-3" /> Accepteret
                            </span>
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); accepterForeslaaet(r.clock_id); }}
                              title="Acceptér foreslået udstemplingstid — gemmes når du trykker Godkend"
                              className="px-2 py-0.5 rounded-lg bg-green-500/10 text-green-600 text-[12px] font-semibold hover:bg-green-500/20 transition-colors inline-flex items-center gap-1"
                            >
                              <Check className="w-3 h-3" /> Acceptér
                            </button>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">–</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 tabular-nums font-semibold text-blue-500 whitespace-nowrap">{fmtTimer(r.timer_actual)}</td>
                    <td className="px-3 py-2.5 text-muted-foreground max-w-[150px] truncate" title={fmtAdresse(r,"ind")}>{fmtAdresse(r,"ind")}</td>
                    <td className="px-3 py-2.5 text-muted-foreground max-w-[150px] truncate" title={fmtAdresse(r,"ud")}>{fmtAdresse(r,"ud")}</td>
                    <td className="px-3 py-2.5 text-muted-foreground max-w-[120px] truncate italic">{r.note || "—"}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className="flex flex-col gap-1 items-start">
                        {isIkkeOk ? (
                          <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-red-500 bg-red-500/10 px-2 py-0.5 rounded-full"
                            title={r.rejection_reason ? `Ikke godkendt: ${r.rejection_reason}` : "Ikke godkendt"}>
                            <X className="w-2.5 h-2.5" /> Ikke godkendt
                          </span>
                        ) : isApproved ? (
                          <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-green-500 bg-green-500/10 px-2 py-0.5 rounded-full"
                            title={r.godkendt_af ? `Godkendt af ${r.godkendt_af}${r.godkendt_at ? " · " + fmtGodkendt(r.godkendt_at) : ""}` : ""}>
                            <Check className="w-2.5 h-2.5" /> Godkendt
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full">
                            Afventer
                          </span>
                        )}
                        {isApproved && !isIkkeOk && r.godkendt_at && (
                          <span className="text-[13px] text-muted-foreground whitespace-nowrap">
                            {fmtGodkendt(r.godkendt_at)}{r.godkendt_af ? ` · ${r.godkendt_af}` : ""}
                          </span>
                        )}
                        {isIkkeOk && r.rejection_reason && (
                          <span className="text-[13px] text-muted-foreground italic max-w-[160px] truncate" title={r.rejection_reason}>
                            {r.rejection_reason}
                          </span>
                        )}
                        {redigeretEfter && (
                          <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-amber-600 bg-amber-500/10 px-2 py-0.5 rounded-full"
                            title={r.redigeret_at ? `Redigeret ${fmtGodkendt(r.redigeret_at)}${r.redigeret_af ? " af " + r.redigeret_af : ""}` : ""}>
                            <AlertTriangle className="w-2.5 h-2.5" /> Redigeret efter godkendelse
                          </span>
                        )}
                        {isManuel && (
                          <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full"
                            title={r.manuel_oprettet_af ? `Manuelt oprettet af ${r.manuel_oprettet_af}` : "Manuelt oprettet stempling"}>
                            Manuel
                          </span>
                        )}
                        {erAendretIkkeGemt && (
                          <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-orange-600 bg-orange-500/10 px-2 py-0.5 rounded-full"
                            title="Foreslået udstempling accepteret — trykkes Godkend for at gemme">
                            Ændret - ikke gemt
                          </span>
                        )}
                        {isAutoUdstemplet && (
                          <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-amber-700 bg-amber-500/10 px-2 py-0.5 rounded-full"
                            title="Auto-udstemplet kl. 23:59 fordi medarbejder glemte at stemple ud">
                            <AlertTriangle className="w-2.5 h-2.5" /> Auto-udstemplet
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5 justify-end">
                        {!isApproved && (
                          <button onClick={(e) => { e.stopPropagation(); godkend(r.clock_id); }} disabled={busy === r.clock_id}
                            title={erAendretIkkeGemt ? "Godkend og gem accepteret udstempling" : "Godkend stempling"}
                            className={`p-1 rounded-lg disabled:opacity-50 transition-colors ${erAendretIkkeGemt ? "bg-orange-500 text-white hover:bg-orange-600 animate-pulse" : "bg-green-500/10 text-green-600 hover:bg-green-500/20"}`}>
                            {busy === r.clock_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                          </button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); openEdit(r); }} title="Rediger"
                          className="p-1 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground">
                          <FileSpreadsheet className="w-3.5 h-3.5" />
                        </button>
                        {!isIkkeOk && (
                          <button onClick={(e) => { e.stopPropagation(); openAfvisModal(r); }} disabled={busy === r.clock_id} title="Markér som ikke godkendt"
                            className="p-1 rounded-lg hover:bg-red-500/10 transition-colors text-muted-foreground hover:text-red-500">
                            {busy === r.clock_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {harAfventerKorrektion && (
                    <tr className="border-b border-amber-500/30 bg-amber-500/5">
                      <td className="px-3 py-3" colSpan={12}>
                        <div className="flex items-start gap-3 flex-wrap">
                          <div className="flex items-start gap-2 flex-1 min-w-[260px]">
                            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                            <div className="text-[13px]">
                              <p className="font-semibold text-amber-700">Medarbejder har angivet faktisk udstemplingstidspunkt</p>
                              <p className="text-muted-foreground mt-0.5">
                                Auto-udstemplet kl. <span className="tabular-nums">23:59</span>
                                {" · "}foreslået:{" "}
                                <span className="tabular-nums font-semibold text-foreground">
                                  {fmtTid(r.korrektion_foreslaaet_clock_out)}
                                </span>
                              </p>
                              {r.korrektion_note && (
                                <p className="italic mt-1 text-muted-foreground">"{r.korrektion_note}"</p>
                              )}
                              {r.korrektion_indsendt_at && (
                                <p className="text-[13px] text-muted-foreground mt-0.5">
                                  Indsendt {fmtGodkendt(r.korrektion_indsendt_at)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={(e) => { e.stopPropagation(); accepterKorrektion(r.clock_id); }}
                              disabled={busy === r.clock_id}
                              className="px-3 py-1.5 rounded-lg bg-green-500 text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-1.5"
                            >
                              {busy === r.clock_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                              Accepter foreslået
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); saetKorrektion(r.clock_id); }}
                              disabled={busy === r.clock_id}
                              className="px-3 py-1.5 rounded-lg border border-border text-[13px] font-semibold hover:bg-secondary disabled:opacity-50 transition-colors"
                            >
                              Indtast andet tidspunkt
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); afvisKorrektion(r.clock_id); }}
                              disabled={busy === r.clock_id}
                              className="px-3 py-1.5 rounded-lg border border-border text-[13px] font-semibold text-red-500 hover:bg-red-500/10 disabled:opacity-50 transition-colors"
                            >
                              Afvis
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {/* Sticky bulk-handlings-bar */}
      {(selectedOpen.size > 0 || selectedStempling.size > 0) && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-card border border-border rounded-2xl shadow-2xl px-4 py-3 flex-wrap max-w-[95vw]">
          {selectedStempling.size > 0 && (
            <>
              <span className="text-[13px] font-semibold text-muted-foreground mr-1">
                {selectedStempling.size} stempling{selectedStempling.size !== 1 ? "er" : ""} valgt
              </span>
              <div className="flex flex-col items-start">
                {selectedOpen.size > 0 && (
                  <span className="text-[13px] text-muted-foreground/80 mb-0.5 leading-tight">
                    Godkendelse anvendes kun på rækker med stempling. {selectedOpen.size} åbne vagter springes over.
                  </span>
                )}
                <button onClick={handleBulkGodkendStempling} disabled={bulkBusy || selectedStempling.size === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-green-500 text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
                  {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Godkend valgte
                </button>
              </div>
              <button onClick={handleBulkAfvisStempling} disabled={bulkBusy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-[13px] font-semibold text-red-500 hover:bg-red-500/10 disabled:opacity-50 transition-colors">
                Afvis valgte
              </button>
              <button onClick={handleBulkSletStempling} disabled={bulkBusy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-500 text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
                <Trash2 className="w-3.5 h-3.5" /> Slet valgte
              </button>
              <button onClick={() => setSelectedStempling(new Set())} disabled={bulkBusy}
                title="Ryd valg"
                className="ml-1 p-1.5 rounded-full hover:bg-secondary transition-colors text-muted-foreground disabled:opacity-50">
                <X className="w-4 h-4" />
              </button>
            </>
          )}
          {selectedOpen.size > 0 && selectedStempling.size > 0 && (
            <span className="h-6 w-px bg-border mx-1" />
          )}
          {selectedOpen.size > 0 && (
            <>
              <span className="text-[13px] font-semibold text-muted-foreground mr-1">
                {selectedOpen.size} åbne vagt{selectedOpen.size !== 1 ? "er" : ""} valgt
              </span>
              <button onClick={() => handleBulkMarkerVagttype("Syg")} disabled={bulkBusy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-[13px] font-semibold text-red-500 hover:bg-red-500/10 disabled:opacity-50 transition-colors">
                {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Markér som syg
              </button>
              <button onClick={() => handleBulkMarkerVagttype("Ferie/Fri")} disabled={bulkBusy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-[13px] font-semibold text-emerald-600 hover:bg-emerald-500/10 disabled:opacity-50 transition-colors">
                Markér som ferie/fri
              </button>
              {/* Opfølgning #35 (#1): bulk-godkend åbne vagter (opretter manuel
                  stempling + godkender) via samme tid+note-popup som #32. */}
              <button onClick={handleBulkGodkendAabne} disabled={bulkBusy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-green-500 text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
                Godkend valgte
              </button>
              <button onClick={handleBulkSletAabne} disabled={bulkBusy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-500 text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
                <Trash2 className="w-3.5 h-3.5" /> Slet valgte
              </button>
              <button onClick={() => setSelectedOpen(new Set())} disabled={bulkBusy}
                title="Ryd valg"
                className="ml-1 p-1.5 rounded-full hover:bg-secondary transition-colors text-muted-foreground disabled:opacity-50">
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// #48.2: "Læger" fjernet (læger hører under Administration), "Administration"
// tilføjet før TNBC.
const FILTER_OPTIONS = ["Alle", "København", "Aarhus", "Odense", "Aalborg", "Kolding", "Administration", "TNBC"];

export default function VagtplanPage() {
  const { user, token, logout } = useAuth();
  const isSuperAdmin = ["superadmin", "owner", "api_admin"].includes(user?.role);
  const isAdminRole = ["admin", "superadmin", "owner", "api_admin"].includes(user?.role);
  // Stempling-fanen er en SEPARAT rettighed fra vagtplan: kun admin+ og
  // medarbejdere med kan_godkende_stempling må se den. En medarbejder med kun
  // kan_se_vagtplan får IKKE adgang.
  const kanGodkendeStempling = isAdminRole || (user?.rettigheder?.kan_godkende_stempling === true);
  // Opfølgning #59 (DEL B): Kørsel-fanen er en SEPARAT rettighed — kun admin+ og
  // medarbejdere med kan_godkende_koersel må se "Kørsel til godkendelse" + eksport.
  const kanGodkendeKoersel = isAdminRole || (user?.rettigheder?.kan_godkende_koersel === true);
  // Vagtplan-adgang: admin+ eller medarbejder med kan_se_vagtplan. Bruges til
  // godkend/afvis af ferieanmodninger (samme som backend _kraev_vagtplan_adgang).
  const kanGodkendeFerie = isAdminRole || (user?.rettigheder?.kan_se_vagtplan === true);
  // Opfølgning #34 (#2): canManage må IKKE kun være superadmin — medarbejdere med
  // kan_se_vagtplan-rettigheden skal også kunne oprette/ændre/flytte vagter.
  // Backend (_kraev_vagtplan_adgang) accepterer dem allerede; det var kun frontend
  // der gatede handlinger til superadmin (samme bug som tidligere med Farina).
  const canManageVagt = isSuperAdmin || (user?.rettigheder?.kan_se_vagtplan === true);

  // #48.1: simplificeret, skrivebeskyttet visning for medarbejdere UDEN
  // vagtplan-rettighed. De ser kun deres egen afdeling — medmindre afdelingen er
  // Administration eller TNBC (så ser de alle byer som admin). Alle skrive-
  // handlinger er allerede skjult (canManageVagt = false for dem).
  const harVagtplanRet = isAdminRole || (user?.rettigheder?.kan_se_vagtplan === true);
  const erSimplificeret = !harVagtplanRet; // almindelig medarbejder uden ret

  const VISIBLE_VIEWS = VIEWS.filter(v => {
    if (v.id === "stempel") return kanGodkendeStempling;
    if (v.id === "koersel") return kanGodkendeKoersel;
    if (erSimplificeret && v.id === "maaned") return false;   // ingen Periode
    if (erSimplificeret && v.id === "oversigt") return false; // ingen Timeoversigt
    return true;
  });
  // Normalisér egen afdeling til samme display-format som filter/medarbejder.city.
  const _egenAfdRaw = (user?.primaer_afdeling || user?.city || "").trim();
  const _egenAfd = /^koebenhavn$|^kobenhavn$|^københavn$/i.test(_egenAfdRaw) ? "København" : _egenAfdRaw;
  const _erAdmTNBC = /administration/i.test(_egenAfd) || /^tnbc$/i.test(_egenAfd);

  // #37 by-filter: en medarbejder med kan_se_vagtplan kan være begrænset til
  // bestemte byer (kan_se_vagtplan__byer). Admin+ er aldrig begrænset (tom = alle).
  const tilladteByer = useMemo(() => {
    if (isAdminRole) return null; // ingen begrænsning
    // #48.1: simplificeret medarbejder → kun egen afdeling (Adm/TNBC = alle byer).
    if (erSimplificeret) {
      if (_erAdmTNBC) return null; // alle byer
      return _egenAfd ? [_egenAfd] : null;
    }
    const b = user?.rettigheder?.kan_se_vagtplan__byer;
    return Array.isArray(b) && b.length ? b : null;
  }, [isAdminRole, erSimplificeret, _erAdmTNBC, _egenAfd, user]);
  // By-valgmuligheder i filteret: begræns til de tilladte byer (uden "Alle"/"Læger"
  // hvis begrænset, så man kun ser sine egne byer).
  const filterOptions = useMemo(() => {
    if (!tilladteByer) return FILTER_OPTIONS;
    return FILTER_OPTIONS.filter(b => b === "Alle" || tilladteByer.includes(b));
  }, [tilladteByer]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(() => {
    const VALID = new Set(["uge", "maaned", "ferie", "oversigt", "stempel", "koersel"]);
    const t = searchParams.get("tab");
    return VALID.has(t) ? t : "uge";
  });
  // By-begrænsede brugere starter med deres byer valgt (ikke "Alle", som for dem
  // ville betyde alle deres tilladte byer alligevel — men eksplicit er tydeligere).
  const [cityFilters, setCityFilters] = useState(() =>
    new Set(["Alle"]));
  const cityFilter = cityFilters.size === 1 && cityFilters.has("Alle") ? "Alle" : [...cityFilters].join(",");

  // Uge-navigation
  const todayIso = isoFromDate(new Date());
  const [currentMonday, setCurrentMonday] = useState(() => mondayOfWeek(todayIso));

  // Periode-navigation (20.–19.).
  // Startoffset: er vi >= 20. i måneden er vi i perioden der slutter NÆSTE måned (+1), ellers denne (+0).
  const [maanedOffset, setMaanedOffset] = useState(() => new Date().getDate() >= 20 ? 1 : 0);
  // Tilpasset periode-mode ("standard" = 20.-19., "custom" = bruger-defineret fra/til)
  const [periodeMode, setPeriodeMode] = useState("standard"); // "standard" | "custom"
  const [periodeCustomFra, setPeriodeCustomFra] = useState("");
  const [periodeCustomTil, setPeriodeCustomTil] = useState("");
  const activePeriode = useMemo(() => {
    if (periodeMode === "custom" && periodeCustomFra && periodeCustomTil) {
      const fraD = new Date(periodeCustomFra + "T00:00:00");
      const tilD = new Date(periodeCustomTil + "T00:00:00");
      // Brug slut-datoens måned/år som "periode-anker" (til header-labels, MaanedGrid/FerieView)
      return {
        year: tilD.getFullYear(),
        month: tilD.getMonth(),
        fra: periodeCustomFra,
        til: periodeCustomTil,
      };
    }
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + maanedOffset);
    const year = d.getFullYear();
    const month = d.getMonth(); // 0-indexed, slutmåned (perioden slutter d. 19. her)
    const fraDate = new Date(year, month - 1, 20);
    const tilDate = new Date(year, month, 19);
    const fra = `${fraDate.getFullYear()}-${String(fraDate.getMonth()+1).padStart(2,"0")}-20`;
    const til = `${tilDate.getFullYear()}-${String(tilDate.getMonth()+1).padStart(2,"0")}-19`;
    return { year, month, fra, til };
  }, [maanedOffset, periodeMode, periodeCustomFra, periodeCustomTil]);
  const activeMaaned = activePeriode; // bagudkompatibilitet

  const [vagter, setVagter] = useState([]);
  const [medarbejdere, setMedarbejdere] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVagtModal, setShowVagtModal] = useState(false);
  // Opfølgning #60 (DEL 6): "Anmod om ændring"-dialog. Holder den vagt der
  // anmodes om (eller null). Åbnes fra VagtModal-knappen.
  const [anmodAendringVagt, setAnmodAendringVagt] = useState(null);
  // Bumpes når en ny anmodning er sendt → "Dine anmodninger"-boks re-fetcher.
  const [anmodningVersion, setAnmodningVersion] = useState(0);
  const [draft, setDraft] = useState(BLANK_DRAFT);
  const [editVagt, setEditVagt] = useState(null);
  // Matchede stemplinger for den vagt der redigeres — bruges til at vise
  // STEMPLET-sektionen i "Ret vagt"-popup'en (Krav #4).
  const [editStemplinger, setEditStemplinger] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showKopierModal, setShowKopierModal] = useState(null); // null | "uge_eller_maaned" | "enkelt" | "medarb"
  const [showExportModal, setShowExportModal] = useState(false);
  const [eksportDropdown, setEksportDropdown] = useState(false);
  const eksportDropdownRef = useRef(null);
  const [showFerieModal, setShowFerieModal] = useState(false);
  // Ret/slet-ferie-modal: holder den periode-doc brugeren har klikket på
  const [retFeriePeriode, setRetFeriePeriode] = useState(null);
  // Anmodning-popup: vises når en planlægger klikker på en stiplet "afventer"-bjælke.
  const [anmodningPopup, setAnmodningPopup] = useState(null);
  const [anmodningAfvisModus, setAnmodningAfvisModus] = useState(false);
  const [anmodningAfvisGrund, setAnmodningAfvisGrund] = useState("");
  const [anmodningBusy, setAnmodningBusy] = useState(false);
  // #68 DEL 1d: "Marker vagt"-dialog for simplificerede brugere
  const [markerVagtModal, setMarkerVagtModal] = useState(null); // { vagt } | null
  const [markerBusy, setMarkerBusy] = useState(false);
  const toggleCity = (city) => {
    setCityFilters(prev => {
      const next = new Set(prev);
      if (city === "Alle") return new Set(["Alle"]);
      next.delete("Alle");
      if (next.has(city)) {
        next.delete(city);
        if (next.size === 0) return new Set(["Alle"]);
      } else {
        next.add(city);
      }
      return next;
    });
  };

  const [selectedVagter, setSelectedVagter] = useState(new Set()); // vagt_id'er
  const [selectMode, setSelectMode] = useState(false);
  // #49.5: "Stemplet ind nu" — vis kun medarbejdere der lige nu er stemplet ind
  // (åbent clock_record i dag: status open/paused og clock_out ikke sat).
  // Persisteres IKKE — defaulter altid til OFF (alle medarbejdere vises).
  const [kunStempletInd, setKunStempletInd] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showBulkKopierModal, setShowBulkKopierModal] = useState(false);
  const [bulkKopierTil, setBulkKopierTil] = useState(""); // medarbejder-navn
  const [bulkKopierMode, setBulkKopierMode] = useState("naeste_uge"); // "naeste_uge" | "resten_maaned" | "frem_til_uge"
  const [bulkKopierTilUge, setBulkKopierTilUge] = useState(""); // ISO uge-værdi fx "2026-W22"
  const [bulkBeholdMedarb, setBulkBeholdMedarb] = useState(true); // hvis true: bevar vagtens egen medarbejder

  const [showMenu] = useState(false);
  const menuRef = useRef(null);
  const [cityDropOpen, setCityDropOpen] = useState(false);
  const cityDropRef = useRef(null);
  const [undoStack, setUndoStack] = useState([]);
  const [undoToast, setUndoToast] = useState(null);
  const undoTimerRef = useRef(null);
  // skjulte medarbejdere (Set af navne) — persisteret i localStorage
  const [skjulte, setSkjulte] = useState(() => {
    try {
      const raw = localStorage.getItem("vagtplan_skjulte");
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });

  // Lønvisning (superadmin)
  const [showLoen, setShowLoen] = useState(false);
  const loenSats = 250; // kr/t

  const hdrs = { Authorization: `Bearer ${token}` };

  const handleLoenEksporter = async () => {
    const fra = fetchRange?.fra;
    const til = fetchRange?.til;
    if (!fra || !til) {
      alert("Vælg en lønperiode før eksport");
      return;
    }
    try {
      const params = new URLSearchParams({
        fra_dato: fra,
        til_dato: til,
        by: cityFilter !== "Alle" ? cityFilter : "",
      });
      const res = await axios.get(`${API}/hub/vagtplan/loen-eksport/excel?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Lønkørsel-${fra}_${til}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Eksport fejlede: " + (e?.response?.data?.detail || e.message));
    }
  };

  // Udregn dato-interval der skal hentes baseret på view
  const fetchRange = useMemo(() => {
    if (view === "uge") {
      return { fra: currentMonday, til: addDays(currentMonday, 6) };
    }
    if (view === "maaned" || view === "ferie") {
      return { fra: activePeriode.fra, til: activePeriode.til };
    }
    // stempel/koersel/oversigt: vagtplan-data bruges ikke — returner null for at springe fetch over
    if (view === "stempel" || view === "koersel") {
      return null;
    }
    // Timeoversigt: hent 3 måneder
    const now = new Date();
    const fra = `${now.getFullYear()-1}-${String(now.getMonth()+1).padStart(2,"0")}-20`;
    const next = new Date(now.getFullYear(), now.getMonth()+3, 19);
    const til = isoFromDate(next);
    return { fra, til };
  }, [view, currentMonday, activePeriode]);

  const [clockRecords, setClockRecords] = useState([]);
  const [feriePerioder, setFeriePerioder] = useState([]); // sammenhængende ferie-perioder fra hub_ferie_perioder

  // Hent stempel-records: admin-endpoint hvis bruger kan godkende, ellers fall
  // back til read-only /hub/vagtplan/stemplinger for brugere med kun
  // kan_se_vagtplan-rettighed (403 fra admin-endpoint).
  const fetchClockRecords = useCallback(async (params) => {
    try {
      const r = await axios.get(`${API}/admin/clock-records?${params}`, { headers: hdrs });
      return r.data.records || [];
    } catch (err) {
      if (err?.response?.status === 403) {
        try {
          const r2 = await axios.get(`${API}/hub/vagtplan/stemplinger?${params}`, { headers: hdrs });
          return r2.data.records || [];
        } catch {
          return [];
        }
      }
      return [];
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadVagter = useCallback(async () => {
    if (!fetchRange) return;
    try {
      const params = new URLSearchParams({ fra_dato: fetchRange.fra, til_dato: fetchRange.til });
      const paramsStr = params.toString();
      // Hent ALLE stempel-statusser (open, paused, completed, approved, rejected)
      // så vagtplanen viser både planlagte og ad-hoc stemplinger uanset godkendelse.
      const [vRes, records, fRes] = await Promise.all([
        axios.get(`${API}/hub/vagtplan/alle?${params}`, { headers: hdrs })
          .catch(() => ({ data: { vagter: [] } })),
        fetchClockRecords(paramsStr),
        axios.get(`${API}/hub/ferie-perioder?fra=${fetchRange.fra}&til=${fetchRange.til}`, { headers: hdrs })
          .catch(() => ({ data: { perioder: [] } })),
      ]);
      setVagter(vRes.data.vagter || []);
      setClockRecords(records);
      setFeriePerioder(fRes.data.perioder || []);
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, fetchRange, fetchClockRecords]);

  // Godkend en afventer-ferieanmodning. Konverterer vagter til Ferie + traekker
  // dage fra feriebank + opretter notifikation til medarbejderen (backend).
  const godkendAnmodning = async () => {
    if (!anmodningPopup?.periode_id) return;
    setAnmodningBusy(true);
    try {
      await axios.post(
        `${API}/hub/ferie-perioder/${anmodningPopup.periode_id}/godkend`,
        {},
        { headers: hdrs }
      );
      await loadVagter();
      setAnmodningPopup(null);
      setAnmodningAfvisModus(false);
      setAnmodningAfvisGrund("");
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunne ikke godkende anmodningen");
    }
    setAnmodningBusy(false);
  };

  const afvisAnmodning = async () => {
    if (!anmodningPopup?.periode_id) return;
    setAnmodningBusy(true);
    try {
      await axios.post(
        `${API}/hub/ferie-perioder/${anmodningPopup.periode_id}/afvis`,
        { grund: anmodningAfvisGrund },
        { headers: hdrs }
      );
      await loadVagter();
      setAnmodningPopup(null);
      setAnmodningAfvisModus(false);
      setAnmodningAfvisGrund("");
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunne ikke afvise anmodningen");
    }
    setAnmodningBusy(false);
  };

  useEffect(() => {
    const init = async () => {
      if (!fetchRange) { setLoading(false); return; }
      setLoading(true);
      try {
        const paramsStr = `fra_dato=${fetchRange.fra}&til_dato=${fetchRange.til}`;
        // Hent ALLE stempel-statusser — se kommentar i loadVagter ovenfor.
        const [vRes, mRes, records, fRes] = await Promise.all([
          axios.get(`${API}/hub/vagtplan/alle?${paramsStr}`, { headers: hdrs })
            .catch(() => ({ data: { vagter: [] } })),
          axios.get(`${API}/hub/vagtplan/medarbejdere-liste`, { headers: hdrs })
            .catch(() => ({ data: { medarbejdere: [] } })),
          fetchClockRecords(paramsStr),
          axios.get(`${API}/hub/ferie-perioder?fra=${fetchRange.fra}&til=${fetchRange.til}`, { headers: hdrs })
            .catch(() => ({ data: { perioder: [] } })),
        ]);
        setVagter(vRes.data.vagter || []);
        setMedarbejdere(mRes.data.medarbejdere || []);
        setClockRecords(records);
        setFeriePerioder(fRes.data.perioder || []);
      } catch {}
      setLoading(false);
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!loading) loadVagter();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadVagter]);

  // #49.5: navne på medarbejdere der lige nu er stemplet ind. Et åbent
  // clock_record i DAG (status open/paused, clock_out ikke sat) = stemplet ind.
  const stempletIndNuNavne = useMemo(() => {
    const s = new Set();
    (clockRecords || []).forEach(r => {
      const aaben = (r.status === "open" || r.status === "paused")
        && r.dato === todayIso
        && !r.clock_out;
      if (aaben && r.medarbejder) s.add(r.medarbejder);
    });
    return s;
  }, [clockRecords, todayIso]);

  const filteredMedarbejdere = useMemo(() => {
    // #48.2: ingen separat "Læger"-spand længere — læger optræder under deres
    // afdeling (Administration) på lige fod med øvrige administrative medarbejdere.
    const base = medarbejdere;
    // #49.5: additivt stemplet-ind-filter — anvendes oven på by-filteret.
    const stempletOk = (name) => !kunStempletInd || stempletIndNuNavne.has(name);
    // #37: "Alle" betyder for en by-begrænset bruger KUN hendes tilladte byer.
    if (cityFilters.has("Alle")) {
      if (!tilladteByer) return base.filter(m => !skjulte.has(m.name) && stempletOk(m.name));
      return base.filter(m => tilladteByer.includes(m.city) && !skjulte.has(m.name) && stempletOk(m.name));
    }
    const selectedCities = [...cityFilters];
    const byNavn = new Set(base.filter(m =>
      selectedCities.some(c => m.city === c || m.city === CITY_DB[c])
    ).map(m => m.name));
    return base.filter(m => byNavn.has(m.name) && !skjulte.has(m.name) && stempletOk(m.name));
  }, [medarbejdere, cityFilters, skjulte, tilladteByer, kunStempletInd, stempletIndNuNavne]);

  // #49.5: antal distinkte medarbejdere der lige nu er stemplet ind OG er
  // synlige for brugeren (respekterer by-filter/by-begrænsning, men IKKE selve
  // stemplet-ind-toggle — bruges som tæller i knap-labelen).
  const stempletIndNuCount = useMemo(() => {
    const base = medarbejdere;
    let synlige;
    if (cityFilters.has("Alle")) {
      synlige = tilladteByer
        ? base.filter(m => tilladteByer.includes(m.city) && !skjulte.has(m.name))
        : base.filter(m => !skjulte.has(m.name));
    } else {
      const selectedCities = [...cityFilters];
      const byNavn = new Set(base.filter(m =>
        selectedCities.some(c => m.city === c || m.city === CITY_DB[c])
      ).map(m => m.name));
      synlige = base.filter(m => byNavn.has(m.name) && !skjulte.has(m.name));
    }
    return synlige.filter(m => stempletIndNuNavne.has(m.name)).length;
  }, [medarbejdere, cityFilters, skjulte, tilladteByer, stempletIndNuNavne]);

  // Dage i aktiv uge
  const ugeDays = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => {
      const iso = addDays(currentMonday, i);
      const d = new Date(iso + "T00:00:00");
      return { iso, date: d, isWeekend: d.getDay() === 0 || d.getDay() === 6 };
    }),
    [currentMonday]
  );

  const openNyVagt = (dato = "", medarbejder = "") => {
    const m = medarbejdere.find(x => x.name === medarbejder);
    setDraft({ ...BLANK_DRAFT, dato, medarbejder, city: m?.city || "" });
    setEditVagt(null);
    setShowVagtModal(true);
  };

  const openEditVagt = (v) => {
    // Normaliser "Ferie"/"Fri" (DB-intern) → "Ferie/Fri" (frontend-label)
    const normVagttype = (vt) => (vt === "Ferie" || vt === "Fri") ? "Ferie/Fri" : (vt || "");
    setDraft({
      dato: v.dato,
      fra: v.fra || "09:00",
      til: v.til || "17:00",
      vagttype: normVagttype(v.vagttype),
      city: v.city || "",
      medarbejder: v.medarbejder || "",
      note: v.note || "",
      andet_navn: v.andet_navn || "",
    });
    // Krav #4: find matchede stemplinger for vagten, så popup'en kan vise
    // en separat STEMPLET-sektion (kun for rigtige planlagte vagter — ikke
    // ad-hoc clock-kort, hvor der ikke er en planlagt vagt at adskille fra).
    const matchede = (!v._isAdhoc && v.vagt_id)
      ? clockRecords.filter(r => r.vagt_id === v.vagt_id)
      : [];
    setEditStemplinger(matchede);
    // Bevar _isAdhoc/_clockId så handleSave kan vælge korrekt endpoint (Krav 6 / Korrektion)
    setEditVagt(v);
    setShowVagtModal(true);
  };

  const showUndoToast = (msg, vagt, type, prevVagt) => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoToast({ message: msg, vagt, type, prevVagt });
    undoTimerRef.current = setTimeout(() => setUndoToast(null), 10000);
  };

  // Konfliktmodal-state: vises når backend returnerer 409 ved POST /hub/vagtplan
  const [conflictData, setConflictData] = useState(null); // {existing, newDraft}

  const postVagtMedKonflikt = async (draftToSave, action) => {
    const qs = action ? `?conflict_action=${encodeURIComponent(action)}` : "";
    await axios.post(`${API}/hub/vagtplan${qs}`, draftToSave, { headers: hdrs });
  };

  // #68 DEL 1d: marker vagt (Syg/Ferie/Fri/Normal timer) for simplificerede brugere
  const markerVagt = async (vagt, nyVagttype) => {
    setMarkerBusy(true);
    try {
      await axios.put(`${API}/hub/vagtplan/${vagt.vagt_id}`,
        { vagttype: nyVagttype, aendret_af: user?.name || "" },
        { headers: hdrs });
      setMarkerVagtModal(null);
      await loadVagter();
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunne ikke ændre vagt");
    } finally { setMarkerBusy(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editVagt && editVagt._isAdhoc && editVagt._clockId) {
        // Ad-hoc clock-record: gem via clock-records endpoint (Korrektion)
        const payload = {
          clock_in: draft.fra,
          clock_out: draft.til,
          note: draft.note || "",
          vagttype: draft.vagttype || "",
          clock_in_lokation: draft.city || "",
        };
        await axios.put(`${API}/admin/clock-records/${editVagt._clockId}`, payload, { headers: hdrs });
      } else if (editVagt) {
        const previousVagt = { ...editVagt };
        await axios.put(`${API}/hub/vagtplan/${editVagt.vagt_id}`, draft, { headers: hdrs });
        setUndoStack(prev => [...prev, { type: "edit", vagt: { ...draft, vagt_id: editVagt.vagt_id }, previousVagt }]);
        showUndoToast("Vagt opdateret", { ...draft, vagt_id: editVagt.vagt_id }, "edit", previousVagt);
      } else {
        try {
          await postVagtMedKonflikt(draft, undefined);
        } catch (e) {
          // 409 = konflikt — vis modal
          if (e?.response?.status === 409 && e?.response?.data?.detail?.conflict) {
            const det = e.response.data.detail;
            setConflictData({ existing: det.existing, newDraft: det.new || draft });
            setSaving(false);
            return; // afvent brugerens valg
          }
          throw e;
        }
      }
      await loadVagter();
      setShowVagtModal(false);
      setEditVagt(null);
      setDraft(BLANK_DRAFT);
    } catch (e) {
      alert(e?.response?.data?.detail || "Gem fejlede");
    }
    setSaving(false);
  };

  const handleConflictResolve = async (action) => {
    if (!conflictData) return;
    if (action === "cancel") { setConflictData(null); return; }
    setSaving(true);
    try {
      await postVagtMedKonflikt(conflictData.newDraft, action);
      await loadVagter();
      setConflictData(null);
      setShowVagtModal(false);
      setEditVagt(null);
      setDraft(BLANK_DRAFT);
    } catch (e) {
      alert(e?.response?.data?.detail || "Gem fejlede");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!editVagt || !window.confirm("Slet vagten?")) return;
    const deletedVagt = { ...editVagt };
    if (editVagt._isAdhoc && editVagt._clockId) {
      // Ad-hoc clock-record: slet via clock-records endpoint
      try {
        await axios.delete(`${API}/admin/clock-records/${editVagt._clockId}`, { headers: hdrs });
        await loadVagter();
      } catch (e) { alert(e?.response?.data?.detail || "Slet fejlede"); }
      setShowVagtModal(false);
      setEditVagt(null);
      setDraft(BLANK_DRAFT);
      return;
    }
    await axios.delete(`${API}/hub/vagtplan/${editVagt.vagt_id}`, { headers: hdrs });
    setVagter(prev => prev.filter(v => v.vagt_id !== editVagt.vagt_id));
    setShowVagtModal(false);
    setEditVagt(null);
    setDraft(BLANK_DRAFT);
    setUndoStack(prev => [...prev, { type: "slet", vagt: deletedVagt }]);
    showUndoToast(`${deletedVagt.medarbejder} – ${deletedVagt.dato} slettet`, deletedVagt, "slet");
  };

  const handleUndo = async () => {
    if (!undoToast) return;
    const { type, vagt, prevVagt } = undoToast;
    setUndoToast(null);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    try {
      if (type === "slet") {
        const { vagt_id, _id, ...body } = vagt;
        await axios.post(`${API}/hub/vagtplan`, body, { headers: hdrs });
        await loadVagter();
      } else if (type === "edit" && prevVagt) {
        await axios.put(`${API}/hub/vagtplan/${prevVagt.vagt_id}`, prevVagt, { headers: hdrs });
        await loadVagter();
      }
    } catch (e) {
      alert("Fortryd fejlede");
    }
  };

  const handleKopierVagt = (v) => {
    setDraft({
      dato: "",
      fra: v.fra || "09:00",
      til: v.til || "17:00",
      vagttype: v.vagttype || "",
      city: v.city || "",
      medarbejder: v.medarbejder || "",
      note: v.note || "",
      andet_navn: v.andet_navn || "",
    });
    setEditVagt(null);
    setShowVagtModal(true);
  };

  // Stempling-godkendelse direkte fra VagtModal (for superadmin med afventende stempling)
  const handleGodkendStemplingFraModal = async (stempling) => {
    if (!stempling?.clock_id) return;
    try {
      await axios.post(`${API}/admin/clock-records/${stempling.clock_id}/godkend`, {}, { headers: hdrs });
      setEditStemplinger(prev => prev.map(r => r.clock_id === stempling.clock_id ? { ...r, status: "approved" } : r));
      await loadVagter();
    } catch (e) {
      alert(e?.response?.data?.detail || "Fejl ved godkendelse af stempling");
    }
  };

  const handleAfvisStemplingFraModal = async (stempling) => {
    if (!stempling?.clock_id) return;
    const aarsag = window.prompt("Årsag til afvisning (valgfri):", "");
    if (aarsag === null) return; // Annuller
    try {
      await axios.patch(`${API}/admin/clock-records/${stempling.clock_id}/afvis`, { rejection_reason: aarsag.trim() }, { headers: hdrs });
      setEditStemplinger(prev => prev.map(r => r.clock_id === stempling.clock_id ? { ...r, status: "rejected", ikke_godkendt: true } : r));
      await loadVagter();
    } catch (e) {
      alert(e?.response?.data?.detail || "Fejl ved afvisning af stempling");
    }
  };

  // Højreklik-actions fra UgeGrid kontekstmenu
  const handleKontekstAction = async (action, vagt) => {
    if (!vagt) return;
    if (action === "kopier") {
      handleKopierVagt(vagt);
      return;
    }
    const vagttypeMap = { syg: "Syg", ferie: "Ferie/Fri", fri: "Ferie/Fri" };
    if (vagttypeMap[action]) {
      try {
        await axios.put(`${API}/hub/vagtplan/${vagt.vagt_id}`, { ...vagt, vagttype: vagttypeMap[action] }, { headers: hdrs });
        await loadVagter();
      } catch (e) {
        alert(e?.response?.data?.detail || "Kunne ikke opdatere vagt");
      }
      return;
    }
    if (action === "slet") {
      if (!window.confirm(`Slet vagten for ${vagt.medarbejder} ${vagt.dato}?`)) return;
      try {
        await axios.delete(`${API}/hub/vagtplan/${vagt.vagt_id}`, { headers: hdrs });
        await loadVagter();
      } catch (e) {
        alert(e?.response?.data?.detail || "Kunne ikke slette vagt");
      }
    }
  };

  const handleKopierUge = async ({ fraMandag, tilMandag }) => {
    try {
      const r = await axios.post(`${API}/hub/vagtplan/kopier-uge`, { fra_mandag: fraMandag, til_mandag: tilMandag }, { headers: hdrs });
      await loadVagter();
      return { inserted: r.data.inserted, skipped: r.data.skipped };
    } catch (e) {
      return { error: e?.response?.data?.detail || "Fejl ved kopiering" };
    }
  };

  const handleKopierMaaned = async () => {
    try {
      const r = await axios.post(`${API}/hub/vagtplan/kopier-maaned`, {
        fra_aar: activeMaaned.year,
        fra_maaned: activeMaaned.month + 1,
      }, { headers: hdrs });
      await loadVagter();
      return { inserted: r.data.inserted, skipped: r.data.skipped };
    } catch (e) {
      return { error: e?.response?.data?.detail || "Fejl ved kopiering" };
    }
  };

  const toggleSkjul = (navn) => {
    setSkjulte(prev => {
      const next = new Set(prev);
      if (next.has(navn)) next.delete(navn); else next.add(navn);
      localStorage.setItem("vagtplan_skjulte", JSON.stringify([...next]));
      return next;
    });
  };

  const toggleSelectVagt = (vagt_id) => {
    setSelectedVagter(prev => {
      const next = new Set(prev);
      if (next.has(vagt_id)) next.delete(vagt_id); else next.add(vagt_id);
      return next;
    });
  };

  const handleBulkKopier = () => {
    if (!selectedVagter.size) return;
    setBulkKopierTil("");
    setBulkKopierMode("naeste_uge");
    setBulkBeholdMedarb(true);
    // Default: forslå én uge frem fra senest valgte vagt
    const valgte = vagter.filter(v => selectedVagter.has(v.vagt_id));
    if (valgte.length) {
      const senesteIso = valgte.map(v => v.dato).sort().slice(-1)[0];
      const senesteMandag = mondayOfWeek(senesteIso);
      setBulkKopierTilUge(mondayToWeekValue(addDays(senesteMandag, 7)));
    } else {
      setBulkKopierTilUge("");
    }
    setShowBulkKopierModal(true);
  };

  const execBulkKopier = async () => {
    const valgte = vagter.filter(v => selectedVagter.has(v.vagt_id));
    if (!valgte.length) return;
    // Hvis vi IKKE beholder oprindelig medarbejder, kræves en målmedarbejder
    if (!bulkBeholdMedarb && !bulkKopierTil) return;
    // Målmedarbejder for en given vagt
    const targetMedarb = (v) => bulkBeholdMedarb ? v.medarbejder : bulkKopierTil;

    // Byg liste af { vagt, dato } der skal oprettes
    const jobs = [];
    if (bulkKopierMode === "naeste_uge") {
      for (const v of valgte) {
        jobs.push({ v, dato: addDays(v.dato, 7) });
      }
    } else if (bulkKopierMode === "resten_maaned") {
      // Start: mandag EFTER den uge hvor den senest valgte vagt ligger.
      // Slut: sidste dag i den måned som startDato falder i (medmindre den
      // måned er den aktuelle måned — så bruges den aktuelle måned). Hver dato
      // får KUN den vagt der har samme ugedag (mapping: ugedag → vagter).
      const senesteIso = valgte.map(v => v.dato).sort().slice(-1)[0];
      const senesteMandagIso = mondayOfWeek(senesteIso);
      const startDatoIso = addDays(senesteMandagIso, 7);
      const startDato = new Date(startDatoIso + "T00:00:00");
      const today = new Date();
      const sameAsCurrentMonth = (
        startDato.getFullYear() === today.getFullYear() &&
        startDato.getMonth() === today.getMonth()
      );
      const year = sameAsCurrentMonth ? today.getFullYear() : startDato.getFullYear();
      const month = sameAsCurrentMonth ? today.getMonth() : startDato.getMonth();
      const lastDay = new Date(year, month + 1, 0).getDate();
      const vagterPrUgedag = {}; // 0=søn..6=lør → [vagter]
      for (const v of valgte) {
        const dow = new Date(v.dato + "T00:00:00").getDay();
        (vagterPrUgedag[dow] = vagterPrUgedag[dow] || []).push(v);
      }
      // Iterer fra startDato (kan ligge i sidste måned hvis startDato.month !== month —
      // i så fald begynder vi fra den 1. i target-måneden).
      const iterStart = (startDato.getFullYear() === year && startDato.getMonth() === month)
        ? startDato.getDate()
        : 1;
      for (let d = iterStart; d <= lastDay; d++) {
        const dato = new Date(year, month, d);
        const matchVagter = vagterPrUgedag[dato.getDay()] || [];
        for (const v of matchVagter) {
          // Spring over hvis det er den oprindelige dato (skal aldrig ske her, men sikkert)
          const isoDato = isoFromDate(dato);
          if (isoDato !== v.dato) jobs.push({ v, dato: isoDato });
        }
      }
    } else if (bulkKopierMode === "frem_til_uge") {
      if (!bulkKopierTilUge) return;
      // Parse "YYYY-Www" → mandag i den valgte slut-uge
      const m = bulkKopierTilUge.match(/^(\d{4})-W(\d{1,2})$/);
      if (!m) return;
      const year = parseInt(m[1], 10);
      const week = parseInt(m[2], 10);
      const jan4 = new Date(year, 0, 4);
      const jan4Dow = (jan4.getDay() + 6) % 7;
      const isoWeek1Mon = new Date(jan4);
      isoWeek1Mon.setDate(jan4.getDate() - jan4Dow);
      const slutMandag = new Date(isoWeek1Mon);
      slutMandag.setDate(isoWeek1Mon.getDate() + (week - 1) * 7);
      const slutMandagIso = isoFromDate(slutMandag);
      // Basis: mandagen i SENESTE valgte vagts uge
      const senesteIso = valgte.map(v => v.dato).sort().slice(-1)[0];
      const senesteMandag = mondayOfWeek(senesteIso);
      const diffDays = (new Date(slutMandagIso + "T00:00:00") - new Date(senesteMandag + "T00:00:00")) / 86400000;
      const antalUger = Math.floor(diffDays / 7);
      if (antalUger < 1) { alert("Vælg en uge efter sidste valgte vagt"); return; }
      // Hver vagt kopieres n=1..antalUger uger fremad — bevarer samme ugedag
      for (let n = 1; n <= antalUger; n++) {
        for (const v of valgte) {
          jobs.push({ v, dato: addDays(v.dato, n * 7) });
        }
      }
    }

    if (!jobs.length) { alert("Ingen datoer at kopiere til"); return; }

    // Ferie-aware: planlagte ferier i destinations-perioden BEVARES.
    // En kopieret vagt der lander på en ferie-dag konverteres til "Fri".
    const ferieDage = new Set(
      vagter
        .filter(v => (v.vagttype || "").toLowerCase() === "ferie")
        .map(v => `${v.medarbejder}|${v.dato}`)
    );

    // Optæl alle (target_medarb, dato)-par i destinations-perioden og tjek hvor
    // mange eksisterende vagter der vil blive ERSTATTET. Ferie tæller IKKE som
    // konflikt — den bevares. Tæl syg separat.
    const SYG_TYPER = new Set(["Syg", "Syg (delvis)", "Barnets første sygedag"]);
    const jobsPrPair = {}; // "medarb|dato" → true
    for (const { v, dato } of jobs) jobsPrPair[`${targetMedarb(v)}|${dato}`] = true;
    const eksisterendeIDestinations = vagter.filter(v => jobsPrPair[`${v.medarbejder}|${v.dato}`]);
    const eksisterendeKonflikter = eksisterendeIDestinations.filter(
      v => (v.vagttype || "").toLowerCase() !== "ferie"
    );
    const antalKonflikter = eksisterendeKonflikter.length;
    const antalSyg = eksisterendeKonflikter.filter(v => SYG_TYPER.has(v.vagttype)).length;
    const antalFerieBevaret = eksisterendeIDestinations.length - eksisterendeKonflikter.length;
    const antalKonverteresTilFri = jobs.filter(
      ({ v, dato }) => ferieDage.has(`${targetMedarb(v)}|${dato}`)
    ).length;

    // Bekræftelse — anderledes besked hvis vi overskriver
    const uniqueDatoer = [...new Set(jobs.map(j => j.dato))].sort();
    const uniqueMedarb = [...new Set(jobs.map(j => targetMedarb(j.v)))];
    const datoSpan = uniqueDatoer.length > 0
      ? (uniqueDatoer.length === 1 ? uniqueDatoer[0] : `${uniqueDatoer[0]} – ${uniqueDatoer.slice(-1)[0]}`)
      : "";
    let msg = `Opret ${jobs.length} vagt${jobs.length !== 1 ? "er" : ""} på ${uniqueMedarb.length} medarbejder${uniqueMedarb.length !== 1 ? "e" : ""}${datoSpan ? ` (${datoSpan})` : ""}?`;
    if (antalKonflikter > 0) {
      msg += `\n\nDer ligger allerede ${antalKonflikter} vagt${antalKonflikter !== 1 ? "er" : ""}`;
      if (antalSyg > 0) msg += ` (heraf ${antalSyg} markeret som syg)`;
      msg += ` i destinations-perioden. Disse erstattes.`;
    }
    if (antalFerieBevaret > 0) {
      msg += `\n\n${antalFerieBevaret} planlagt${antalFerieBevaret !== 1 ? "e" : ""} ferie-dag${antalFerieBevaret !== 1 ? "e" : ""} bevares.`;
    }
    if (antalKonverteresTilFri > 0) {
      msg += `\n\n${antalKonverteresTilFri} kopieret${antalKonverteresTilFri !== 1 ? "e" : ""} vagt${antalKonverteresTilFri !== 1 ? "er" : ""} lander på en ferie-dag og oprettes som "Fri".`;
    }
    msg += `\n\nFortsæt?`;
    if (!window.confirm(msg)) return;

    setBulkBusy(true);
    try {
      // Brug det nye atomiske kopier-med-erstat-endpoint.
      // force_vagttype: "Fri" for jobs der lander på en planlagt feriedag.
      const payload = {
        jobs: jobs.map(({ v, dato }) => {
          const isFerieDag = ferieDage.has(`${targetMedarb(v)}|${dato}`);
          return {
            vagt_id_kilde: v.vagt_id,
            ny_dato: dato,
            ny_medarbejder: targetMedarb(v),
            ...(isFerieDag ? { force_vagttype: "Fri" } : {}),
          };
        }),
      };
      await axios.post(`${API}/hub/vagtplan/kopier-med-erstat`, payload, { headers: hdrs });
      await loadVagter();
      setSelectedVagter(new Set());
      setSelectMode(false);
      setShowBulkKopierModal(false);
    } catch (e) { alert(e?.response?.data?.detail || "Fejl ved kopiering"); }
    setBulkBusy(false);
  };

  const handleBulkSlet = async () => {
    if (!selectedVagter.size) return;
    if (!window.confirm(`Slet ${selectedVagter.size} vagter?`)) return;
    setBulkBusy(true);
    try {
      for (const id of selectedVagter) {
        await axios.delete(`${API}/hub/vagtplan/${id}`, { headers: hdrs });
      }
      await loadVagter();
      setSelectedVagter(new Set());
      setSelectMode(false);
    } catch { alert("Fejl ved sletning"); }
    setBulkBusy(false);
  };

  const handleBulkGodkend = async () => {
    if (!selectedVagter.size) return;
    setBulkBusy(true);
    try {
      for (const id of selectedVagter) {
        await axios.patch(`${API}/hub/vagtplan/${id}/godkend`, {}, { headers: hdrs }).catch(() => {});
      }
      await loadVagter();
      setSelectedVagter(new Set());
      setSelectMode(false);
    } catch { alert("Fejl ved godkendelse"); }
    setBulkBusy(false);
  };

  // Opfølgning #32 (#1): markér valgte vagter som en bestemt vagttype (Syg/Ferie).
  // Genbruger PUT /hub/vagtplan/{id} (hele vagt + ny vagttype), samme som enkelt-
  // markering via kontekstmenu. Slår hver valgt vagt op i vagter-listen.
  const handleBulkVagttype = async (vagttype) => {
    if (!selectedVagter.size) return;
    if (!window.confirm(`Markér ${selectedVagter.size} vagter som "${vagttype}"?`)) return;
    setBulkBusy(true);
    const vagtById = {};
    vagter.forEach(v => { vagtById[v.vagt_id] = v; });
    try {
      for (const id of selectedVagter) {
        const v = vagtById[id];
        if (!v) continue;
        await axios.put(`${API}/hub/vagtplan/${id}`, { ...v, vagttype }, { headers: hdrs }).catch(() => {});
      }
      await loadVagter();
      setSelectedVagter(new Set());
      setSelectMode(false);
    } catch { alert("Fejl ved markering"); }
    setBulkBusy(false);
  };

  useEffect(() => { return () => { if (undoTimerRef.current) clearTimeout(undoTimerRef.current); }; }, []);

  useEffect(() => {
    if (!showMenu) return;
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [showMenu]);

  useEffect(() => {
    if (!cityDropOpen) return;
    const h = (e) => { if (cityDropRef.current && !cityDropRef.current.contains(e.target)) setCityDropOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [cityDropOpen]);

  useEffect(() => {
    if (!eksportDropdown) return;
    const h = (e) => { if (eksportDropdownRef.current && !eksportDropdownRef.current.contains(e.target)) setEksportDropdown(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [eksportDropdown]);

  const prevWeek = () => setCurrentMonday(m => addDays(m, -7));
  const nextWeek = () => setCurrentMonday(m => addDays(m, 7));
  const goToday = () => { setCurrentMonday(mondayOfWeek(todayIso)); setMaanedOffset(new Date().getDate() >= 20 ? 1 : 0); setPeriodeMode("standard"); };

  const isThisWeek = currentMonday === mondayOfWeek(todayIso);
  const isThisMonth = maanedOffset === (new Date().getDate() >= 20 ? 1 : 0);

  // Planlagte vagter filtreret efter by-valg. #48.2: ingen separat læge-spand —
  // læger indgår under deres afdeling (Administration) som alle andre.
  const sectionVagter = useMemo(() => {
    // #49.5: når stemplet-ind-toggle er aktiv begrænses vagterne til medarbejdere
    // der lige nu er stemplet ind, så grid-rækker (der unioneres fra vagter) ikke
    // genindfører ikke-stemplede medarbejdere.
    const stempletOk = (name) => !kunStempletInd || stempletIndNuNavne.has(name);
    if (cityFilters.has("Alle")) {
      // #37: by-begrænset bruger ser kun vagter i sine tilladte byer.
      if (tilladteByer) return vagter.filter(v => tilladteByer.includes(v.city) && stempletOk(v.medarbejder));
      return vagter.filter(v => stempletOk(v.medarbejder));
    }
    const filtNavn = new Set(filteredMedarbejdere.map(m => m.name));
    const selectedCities = [...cityFilters];
    return vagter.filter(v => (filtNavn.has(v.medarbejder) || selectedCities.includes(v.city)) && stempletOk(v.medarbejder));
  }, [vagter, cityFilters, filteredMedarbejdere, tilladteByer, kunStempletInd, stempletIndNuNavne]);

  // Godkendte stemplinger filtreret tilsvarende — vises som augmentation
  // i samme celle som planlagt vagt (ikke længere pseudo-vagter).
  const sectionClockRecords = useMemo(() => {
    // #49.5: samme stemplet-ind-begrænsning som sectionVagter.
    const stempletOk = (name) => !kunStempletInd || stempletIndNuNavne.has(name);
    if (cityFilters.has("Alle")) {
      if (tilladteByer) {
        const filtNavn = new Set(filteredMedarbejdere.map(m => m.name));
        return clockRecords.filter(r => (filtNavn.has(r.medarbejder) || tilladteByer.includes(r.by || "")) && stempletOk(r.medarbejder));
      }
      return clockRecords.filter(r => stempletOk(r.medarbejder));
    }
    const filtNavn = new Set(filteredMedarbejdere.map(m => m.name));
    const selectedCities = [...cityFilters];
    return clockRecords.filter(r => (filtNavn.has(r.medarbejder) || selectedCities.includes(r.by || "")) && stempletOk(r.medarbejder));
  }, [clockRecords, cityFilters, filteredMedarbejdere, tilladteByer, kunStempletInd, stempletIndNuNavne]);

  // Stats til header. Opf. #36: timer-totalen tæller ÉN gang pr. (medarbejder,
  // dato) via dagTimer() — stempling overtager planlagt vagt, ingen dobbeltælling.
  const visibleStats = useMemo(() => {
    const vByCell = {};
    sectionVagter.forEach(v => {
      const k = `${v.medarbejder || ""}|${v.dato || ""}`;
      (vByCell[k] = vByCell[k] || []).push(v);
    });
    const rByCell = {};
    sectionClockRecords.forEach(r => {
      const k = `${r.medarbejder || ""}|${r.dato || ""}`;
      (rByCell[k] = rByCell[k] || []).push(r);
    });
    let timer = 0;
    new Set([...Object.keys(vByCell), ...Object.keys(rByCell)]).forEach(k => {
      timer += dagTimer(vByCell[k] || [], rByCell[k] || []);
    });
    const medarbSet = new Set([
      ...sectionVagter.map(v => v.medarbejder),
      ...sectionClockRecords.map(r => r.medarbejder),
    ]);
    return {
      vagterCount: sectionVagter.length,
      timer: Math.round(timer * 10) / 10,
      medarbCount: medarbSet.size,
    };
  }, [sectionVagter, sectionClockRecords]);

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader user={user} onLogout={logout} />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

        {/* Sidetitel */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight font-[Manrope]">Vagtplan</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              <span className="font-semibold text-foreground capitalize">{(() => {
                if (view === "uge") {
                  // [mnd] [år] · Uge [N] · [aktuel dato]
                  const d = new Date(currentMonday + "T00:00:00");
                  const thu = new Date(d); thu.setDate(d.getDate() + 3);
                  const mnd = MND_FULL[thu.getMonth()];
                  const wk = isoUgenummer(currentMonday);
                  const now = new Date();
                  const aktuel = `${now.getDate()}. ${MND_SHORT[now.getMonth()]}`;
                  return `${mnd.charAt(0).toUpperCase() + mnd.slice(1)} ${thu.getFullYear()} · Uge ${wk} · ${aktuel}`;
                }
                if (view === "maaned" || view === "ferie") {
                  const fra = new Date(activePeriode.fra + "T00:00:00");
                  const til = new Date(activePeriode.til + "T00:00:00");
                  const fraLbl = `${MND_FULL[fra.getMonth()]} ${fra.getFullYear()}`;
                  const tilLbl = `${MND_FULL[til.getMonth()]} ${til.getFullYear()}`;
                  return fra.getMonth() === til.getMonth() && fra.getFullYear() === til.getFullYear()
                    ? `${fraLbl} (periode)`
                    : `${fraLbl} – ${tilLbl} (periode)`;
                }
                // stempel / oversigt: brug periode
                const fra = new Date(activePeriode.fra + "T00:00:00");
                const til = new Date(activePeriode.til + "T00:00:00");
                return `${fra.getDate()}. ${MND_SHORT[fra.getMonth()]} – ${til.getDate()}. ${MND_SHORT[til.getMonth()]} ${til.getFullYear()} (periode)`;
              })()}</span>
              <span className="mx-2 text-muted-foreground/50">·</span>
              {visibleStats.vagterCount} vagter · {visibleStats.timer}t · {visibleStats.medarbCount} medarbejdere
              {showLoen && ` · est. ${formatLoen(Math.round(visibleStats.timer * loenSats))}`}
            </p>
          </div>
          {canManageVagt && (
            <div className="flex items-center gap-2 flex-wrap">
              {/* Eksport er fortsat superadmin-only (Opf. #31) */}
              {isSuperAdmin && (
                <div className="relative" ref={eksportDropdownRef}>
                  <button
                    onClick={() => setEksportDropdown(v => !v)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-[13px] font-semibold text-muted-foreground hover:bg-secondary transition-colors"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5" />
                    Eksportér
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  {eksportDropdown && (
                    <div className="absolute right-0 top-full mt-1 z-50 bg-background border border-border rounded-xl shadow-lg py-1 min-w-[200px]">
                      <button onClick={() => { setEksportDropdown(false); setShowExportModal(true); }}
                        className="w-full text-left px-4 py-2 text-[13px] hover:bg-secondary transition-colors">
                        Detaljeret vagtplan
                      </button>
                      <button onClick={() => { setEksportDropdown(false); handleLoenEksporter(); }}
                        className="w-full text-left px-4 py-2 text-[13px] hover:bg-secondary transition-colors">
                        Lønkørsel (simplificeret)
                      </button>
                    </div>
                  )}
                </div>
              )}
              {(view === "uge" || view === "maaned") && (
                <button
                  onClick={() => { setSelectMode(v => !v); setSelectedVagter(new Set()); }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-xl border text-sm font-semibold transition-colors ${
                    selectMode
                      ? "bg-blue-500/10 text-blue-600 border-blue-500/30"
                      : "border-border hover:bg-secondary"
                  }`}
                >
                  <CheckSquare className="w-4 h-4" /> Vælg
                </button>
              )}
              {view === "ferie" && (
                <button
                  onClick={() => setShowFerieModal(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
                >
                  <Plus className="w-4 h-4" /> Opret ferie
                </button>
              )}
              {view === "uge" && (
                <button
                  onClick={() => openNyVagt(todayIso)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
                >
                  <Plus className="w-4 h-4" /> Opret vagt
                </button>
              )}
            </div>
          )}
        </div>

        {/* Toolbar: view-tabs + by-filter + nav */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          {/* View-selector — fælles SubTabs-skabelon (understreg-faner, ingen ikoner). */}
          <SubTabs
            tabs={VISIBLE_VIEWS.map(v => ({ key: v.id, label: v.label }))}
            value={view}
            onChange={v => { setView(v); setSearchParams(p => { const n = new URLSearchParams(p); n.set("tab", v); return n; }, { replace: true }); }}
            className="w-full sm:w-auto"
          />

          <div className="flex items-center gap-2 flex-wrap">
            {/* By-multi-filter */}
            <div className="relative" ref={cityDropRef}>
              <button
                onClick={() => setCityDropOpen(o => !o)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border bg-background text-[13px] font-semibold text-foreground hover:bg-secondary transition-colors"
              >
                <MapPin className="w-3.5 h-3.5" />
                <span className="max-w-[150px] truncate">
                  {cityFilters.has("Alle") ? "Alle byer" : [...cityFilters].join(", ")}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
              {cityDropOpen && (
                <div className="absolute left-0 top-full mt-1 bg-background border border-border rounded-xl shadow-xl z-50 py-1 min-w-[160px]">
                  {filterOptions.map(b => (
                    <button
                      key={b}
                      onClick={() => toggleCity(b)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] hover:bg-secondary transition-colors text-left"
                    >
                      <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                        cityFilters.has(b) || (b === "Alle" && cityFilters.has("Alle"))
                          ? "bg-[#FF2A6D] border-[#FF2A6D]" : "border-border"
                      }`}>
                        {(cityFilters.has(b) || (b === "Alle" && cityFilters.has("Alle"))) && (
                          <Check className="w-2.5 h-2.5 text-white" />
                        )}
                      </span>
                      {b}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* #49.5: Stemplet ind nu — kun på uge og periode (ikke ferie/oversigt/stempling/kørsel) */}
            {(view === "uge" || view === "maaned") && (
              <button
                onClick={() => setKunStempletInd(v => !v)}
                title="Vis kun medarbejdere der er stemplet ind lige nu"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-[13px] font-semibold transition-colors ${
                  kunStempletInd
                    ? "bg-[#FF2A6D]/10 text-[#FF2A6D] border-[#FF2A6D]/30"
                    : "border-border bg-background text-foreground hover:bg-secondary"
                }`}
              >
                <Clock className="w-3.5 h-3.5" />
                Stemplet ind nu{kunStempletInd ? ` · ${stempletIndNuCount}` : ""}
              </button>
            )}

            {/* Vis skjulte */}
            {isSuperAdmin && skjulte.size > 0 && (
              <button
                onClick={() => { setSkjulte(new Set()); localStorage.removeItem("vagtplan_skjulte"); }}
                className="px-3 py-1.5 rounded-xl border border-border text-[13px] font-semibold text-muted-foreground hover:text-foreground transition-colors"
              >
                Vis skjulte ({skjulte.size})
              </button>
            )}

            {/* Uge/måned navigation */}
            {view === "uge" && (
              <div className="flex items-center gap-1 bg-secondary border border-border rounded-full px-2 py-1">
                <button onClick={prevWeek} className="p-1 rounded-full hover:bg-background transition-colors text-muted-foreground">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-[13px] font-semibold px-1 min-w-[110px] text-center">{weekLabel(currentMonday)}</span>
                <button onClick={nextWeek} className="p-1 rounded-full hover:bg-background transition-colors text-muted-foreground">
                  <ChevronRight className="w-4 h-4" />
                </button>
                {!isThisWeek && (
                  <button onClick={goToday} className="ml-1 text-[13px] px-2 py-0.5 rounded-full bg-background text-muted-foreground hover:text-foreground transition-colors font-semibold">
                    i dag
                  </button>
                )}
              </div>
            )}
            {view === "maaned" && (
              <div className="flex items-center gap-2 flex-wrap">
                {/* Standard/Tilpasset toggle */}
                <div className="flex items-center rounded-full border border-border bg-secondary p-0.5">
                  {[{ id: "standard", label: "Fast 20.-19." }, { id: "custom", label: "Tilpasset" }].map(m => (
                    <button
                      key={m.id}
                      onClick={() => setPeriodeMode(m.id)}
                      className={`px-2.5 py-1 rounded-full text-[12px] font-semibold transition-colors ${
                        periodeMode === m.id ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                {periodeMode === "standard" ? (
                  <div className="flex items-center gap-1 bg-secondary border border-border rounded-full px-2 py-1">
                    <button onClick={() => setMaanedOffset(v => v-1)} className="p-1 rounded-full hover:bg-background transition-colors text-muted-foreground">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-[13px] font-semibold px-1 min-w-[120px] sm:min-w-[140px] text-center capitalize">
                      Periode {MND_FULL[activePeriode.month]} {activePeriode.year}
                    </span>
                    <button onClick={() => setMaanedOffset(v => v+1)} className="p-1 rounded-full hover:bg-background transition-colors text-muted-foreground">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <input
                      type="month"
                      value={`${activeMaaned.year}-${String(activeMaaned.month+1).padStart(2,"0")}`}
                      onChange={e => {
                        const v = e.target.value; // "YYYY-MM"
                        if (!v) return;
                        const [yr, mo] = v.split("-").map(Number);
                        const now = new Date();
                        const diff = (yr - now.getFullYear()) * 12 + (mo - 1 - now.getMonth());
                        setMaanedOffset(diff);
                      }}
                      className="opacity-0 w-0 h-0 absolute"
                      id="month-picker-input"
                    />
                    <button
                      onClick={() => document.getElementById("month-picker-input")?.showPicker?.()}
                      className="p-1 rounded-full hover:bg-background transition-colors text-muted-foreground"
                      title="Vælg måned"
                    >
                      <Calendar className="w-3.5 h-3.5" />
                    </button>
                    {!isThisMonth && (
                      <button onClick={goToday} className="ml-1 text-[13px] px-2 py-0.5 rounded-full bg-background text-muted-foreground hover:text-foreground transition-colors font-semibold">
                        i dag
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <input
                      type="date"
                      value={periodeCustomFra}
                      onChange={e => setPeriodeCustomFra(e.target.value)}
                      className="px-2 py-1 rounded-lg border border-border bg-background text-[13px] focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
                    />
                    <span className="text-[13px] text-muted-foreground">–</span>
                    <input
                      type="date"
                      value={periodeCustomTil}
                      onChange={e => setPeriodeCustomTil(e.target.value)}
                      className="px-2 py-1 rounded-lg border border-border bg-background text-[13px] focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Indhold */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-[#FF2A6D] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="animate-fade-in-up">
            {/* Opfølgning #60 (DEL 6): Dine anmodninger-boks på egen vagtplan (uge-visning) */}
            {view === "uge" && harVagtplanRet && (
              <DineAnmodningerBox token={token} version={anmodningVersion} />
            )}
            {view === "uge" && (
              <UgeGrid
                days={ugeDays}
                vagter={sectionVagter}
                clockRecords={sectionClockRecords}
                medarbejdere={filteredMedarbejdere}
                canManage={canManageVagt}
                onCellClick={(dato, name) => !selectMode && openNyVagt(dato, name)}
                onVagtClick={(v) => selectMode ? toggleSelectVagt(v.vagt_id) : openEditVagt(v)}
                cityFilter={cityFilter}
                showLoen={showLoen}
                loenSats={loenSats}
                skjulte={skjulte}
                onToggleSkjul={toggleSkjul}
                selectMode={selectMode}
                selectedVagter={selectedVagter}
                onKontekstAction={handleKontekstAction}
                erSimplificeret={erSimplificeret}
                onMarkerVagt={(v) => setMarkerVagtModal({ vagt: v })}
              />
            )}
            {view === "maaned" && (
              <MaanedGrid
                year={activeMaaned.year}
                month={activeMaaned.month}
                fraDato={activePeriode.fra}
                tilDato={activePeriode.til}
                vagter={sectionVagter}
                clockRecords={sectionClockRecords}
                medarbejdere={filteredMedarbejdere}
                canManage={canManageVagt}
                onCellClick={(dato, name) => !selectMode && openNyVagt(dato, name)}
                onVagtClick={(v) => selectMode ? toggleSelectVagt(v.vagt_id) : openEditVagt(v)}
                selectMode={selectMode}
                selectedVagter={selectedVagter}
              />
            )}
            {view === "ferie" && (
              <FerieView
                year={activeMaaned.year}
                month={activeMaaned.month}
                fraDato={activePeriode.fra}
                tilDato={activePeriode.til}
                vagter={sectionVagter}
                medarbejdere={filteredMedarbejdere}
                feriePerioder={feriePerioder}
                token={token}
                onPeriodeClick={(isSuperAdmin || kanGodkendeFerie) ? ((p) => {
                  if (p?.status === "afventer") {
                    setAnmodningPopup(p);
                    setAnmodningAfvisModus(false);
                    setAnmodningAfvisGrund("");
                  } else if (isSuperAdmin) {
                    setRetFeriePeriode(p);
                  }
                }) : undefined}
              />
            )}
            {view === "oversigt" && (
              <Timeoversigt
                vagter={sectionVagter}
                clockRecords={sectionClockRecords}
                medarbejdere={filteredMedarbejdere}
                showLoen={showLoen}
                loenSats={loenSats}
                isSuperAdmin={isSuperAdmin}
                token={token}
                kanAabneStamkort={isAdminRole || !!user?.rettigheder?.kan_se_medarbejdere}
              />
            )}
            {view === "stempel" && (
              <StempelGodkendelse token={token} />
            )}
            {view === "koersel" && (
              <KoerselGodkendelse token={token} />
            )}
          </div>
        )}

        {/* Multi-select bundlinje */}
        {selectMode && (() => {
          const synligeIds = sectionVagter.map(v => v.vagt_id);
          const alleValgt = synligeIds.length > 0 && synligeIds.every(id => selectedVagter.has(id));
          const toggleAlle = () => {
            if (alleValgt) {
              setSelectedVagter(prev => {
                const next = new Set(prev);
                for (const id of synligeIds) next.delete(id);
                return next;
              });
            } else {
              setSelectedVagter(prev => {
                const next = new Set(prev);
                for (const id of synligeIds) next.add(id);
                return next;
              });
            }
          };
          return (
          <div className="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-wrap items-center justify-center gap-2 bg-background border border-border rounded-2xl shadow-2xl px-3 sm:px-4 py-3 max-w-[95vw] max-h-[60vh] overflow-y-auto">
            <span className="text-[13px] font-semibold text-muted-foreground mr-1 w-full sm:w-auto text-center sm:text-left">{selectedVagter.size} valgt</span>
            <button onClick={toggleAlle} disabled={synligeIds.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-[13px] font-semibold hover:bg-secondary disabled:opacity-50 transition-colors">
              {alleValgt ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
              {alleValgt ? "Fjern alle" : "Vælg alle"}
            </button>
            {/* Opfølgning #32 (#1): bulk-markér valgte vagter som syg/ferie */}
            <button onClick={() => handleBulkVagttype("Syg")} disabled={bulkBusy || selectedVagter.size === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-red-500/40 text-red-600 text-[13px] font-semibold hover:bg-red-500/10 disabled:opacity-50 transition-colors">
              Markér som syg
            </button>
            <button onClick={() => handleBulkVagttype("Ferie/Fri")} disabled={bulkBusy || selectedVagter.size === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-yellow-500/50 text-yellow-700 dark:text-yellow-500 text-[13px] font-semibold hover:bg-yellow-500/10 disabled:opacity-50 transition-colors">
              Markér som ferie/fri
            </button>
            <button onClick={handleBulkKopier} disabled={bulkBusy || selectedVagter.size === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-500 text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
              {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Kopier
            </button>
            {kanGodkendeStempling && (
              <button onClick={handleBulkGodkend} disabled={bulkBusy || selectedVagter.size === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-green-500 text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
                Godkend
              </button>
            )}
            <button onClick={handleBulkSlet} disabled={bulkBusy || selectedVagter.size === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-500 text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
              Slet
            </button>
            <button onClick={() => { setSelectMode(false); setSelectedVagter(new Set()); }}
              className="ml-1 p-1.5 rounded-full hover:bg-secondary transition-colors text-muted-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
          );
        })()}
      </div>

      {/* Vagt-modal */}
      {showVagtModal && (
        <VagtModal
          draft={draft}
          setDraft={setDraft}
          editVagt={editVagt}
          onSave={handleSave}
          onDelete={handleDelete}
          onKopier={() => { setShowVagtModal(false); handleKopierVagt(draft); }}
          onClose={() => { setShowVagtModal(false); setEditVagt(null); setEditStemplinger([]); setDraft(BLANK_DRAFT); }}
          saving={saving}
          medarbejdere={medarbejdere}
          stemplinger={editStemplinger}
          isSuperAdmin={isSuperAdmin}
          byOptions={tilladteByer || undefined}
          erSimplificeret={erSimplificeret}
          erMedarbejderMedVagtplanRet={harVagtplanRet && !isAdminRole}
          onGodkendStempling={isSuperAdmin ? handleGodkendStemplingFraModal : null}
          onAfvisStempling={isSuperAdmin ? handleAfvisStemplingFraModal : null}
          // Opfølgning #60 (DEL 6): kun for planlagte vagter (ikke ad-hoc) når
          // brugeren har vagtplan-adgang. Lukker VagtModal og åbner anmod-dialog.
          onAnmodAendring={
            (canManageVagt && editVagt && !editVagt._isAdhoc && editVagt.vagt_id)
              ? () => {
                  const v = editVagt;
                  setShowVagtModal(false);
                  setEditVagt(null);
                  setEditStemplinger([]);
                  setDraft(BLANK_DRAFT);
                  setAnmodAendringVagt(v);
                }
              : undefined
          }
        />
      )}

      {/* Opfølgning #60 (DEL 6): Anmod om ændring-dialog */}
      {anmodAendringVagt && (
        <AnmodOmAendringModal
          vagt={anmodAendringVagt}
          hdrs={hdrs}
          onClose={() => setAnmodAendringVagt(null)}
          onSent={async () => {
            setAnmodAendringVagt(null);
            setAnmodningVersion(v => v + 1);
            alert("Anmodning sendt");
            await loadVagter();
          }}
        />
      )}

      {/* #68 DEL 1d: Marker vagt-dialog for simplificerede brugere */}
      {markerVagtModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-2xl shadow-2xl border border-border p-5 w-full max-w-sm">
            <h3 className="font-bold text-sm mb-0.5">Marker vagt</h3>
            <p className="text-[13px] text-muted-foreground mb-4">
              {markerVagtModal.vagt.medarbejder} · {markerVagtModal.vagt.dato}
              {markerVagtModal.vagt.fra && ` · ${markerVagtModal.vagt.fra}–${markerVagtModal.vagt.til}`}
              {markerVagtModal.vagt.city && ` · ${markerVagtModal.vagt.city}`}
            </p>
            <p className="text-[13px] font-semibold text-muted-foreground mb-2">Markér som:</p>
            <div className="flex gap-2 flex-wrap mb-4">
              {[
                { vagttype: "Syg", label: "⚠ Syg", cls: "border-amber-500/40 text-amber-600 hover:bg-amber-500/10" },
                { vagttype: "Ferie/Fri", label: "🌴 Ferie/Fri", cls: "border-blue-500/40 text-blue-600 hover:bg-blue-500/10" },
              ].map(({ vagttype, label, cls }) => (
                <button key={vagttype} onClick={() => markerVagt(markerVagtModal.vagt, vagttype)}
                  disabled={markerBusy}
                  className={`flex-1 px-3 py-2 rounded-xl border text-[13px] font-semibold disabled:opacity-50 transition-colors ${cls}`}>
                  {label}
                </button>
              ))}
            </div>
            {markerVagtModal.vagt.vagttype && markerVagtModal.vagt.vagttype !== "Normal timer" && (
              <button onClick={() => markerVagt(markerVagtModal.vagt, "Normal timer")}
                disabled={markerBusy}
                className="w-full px-3 py-2 rounded-xl border border-border text-[13px] font-semibold hover:bg-secondary disabled:opacity-50 transition-colors mb-2">
                ↩ Annullér ændring (tilbage til Normal timer)
              </button>
            )}
            <button onClick={() => setMarkerVagtModal(null)}
              className="w-full px-3 py-2 rounded-xl border border-border text-[13px] text-muted-foreground hover:bg-secondary transition-colors">
              Luk
            </button>
          </div>
        </div>
      )}

      {/* Konflikt-modal: vises hvis backend returnerer 409 ved oprettelse */}
      {conflictData && (
        <ConflictModal
          existing={conflictData.existing}
          newDraft={conflictData.newDraft}
          saving={saving}
          onResolve={handleConflictResolve}
        />
      )}

      {/* Ferie-modal */}
      {showFerieModal && (
        <FerieModal
          medarbejdere={medarbejdere}
          onClose={() => setShowFerieModal(false)}
          onCreated={loadVagter}
          hdrs={hdrs}
        />
      )}

      {/* Ret/slet ferie-modal — kun superadmin (vises ved klik på bjælke) */}
      {retFeriePeriode && (
        <RetFerieModal
          periode={retFeriePeriode}
          onClose={() => setRetFeriePeriode(null)}
          onSaved={loadVagter}
          hdrs={hdrs}
        />
      )}

      {/* Anmodning godkend/afvis-popup */}
      {anmodningPopup && (() => {
        const DK_HELLIGDAGE = new Set([
          "2024-01-01","2024-03-28","2024-03-29","2024-04-01","2024-04-26","2024-05-09","2024-05-19","2024-05-20","2024-06-05","2024-12-25","2024-12-26",
          "2025-01-01","2025-04-17","2025-04-18","2025-04-21","2025-05-16","2025-05-29","2025-06-08","2025-06-09","2025-06-05","2025-12-25","2025-12-26",
          "2026-01-01","2026-04-02","2026-04-03","2026-04-06","2026-05-01","2026-05-14","2026-05-24","2026-05-25","2026-06-05","2026-12-25","2026-12-26",
          "2027-01-01","2027-03-25","2027-03-26","2027-03-29","2027-04-23","2027-06-03","2027-06-13","2027-06-14","2027-06-05","2027-12-25","2027-12-26",
        ]);
        const beregnFerieDage = (fra, til) => {
          if (!fra || !til) return { hverdage: 0, weekender: 0, helligdage: 0 };
          const fd = new Date(fra + "T00:00:00");
          const td = new Date(til + "T00:00:00");
          if (isNaN(fd) || isNaN(td) || td < fd) return { hverdage: 0, weekender: 0, helligdage: 0 };
          let hverdage = 0, weekender = 0, helligdage = 0;
          const cur = new Date(fd);
          while (cur <= td) {
            const iso = cur.toISOString().slice(0, 10);
            const dow = cur.getDay();
            if (DK_HELLIGDAGE.has(iso)) { helligdage += 1; }
            else if (dow === 0 || dow === 6) { weekender += 1; }
            else { hverdage += 1; }
            cur.setDate(cur.getDate() + 1);
          }
          return { hverdage, weekender, helligdage };
        };
        const ferieDage = beregnFerieDage(anmodningPopup.fra_dato, anmodningPopup.til_dato);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/10 backdrop-blur-[2px] p-4"
               onClick={() => !anmodningBusy && setAnmodningPopup(null)}>
            <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md p-5"
                 onClick={e => e.stopPropagation()}>
              <h3 className="font-bold text-base mb-4">Anmodning om ferie</h3>
              <div className="space-y-2 text-sm mb-4">
                <p><span className="text-muted-foreground">Medarbejder:</span> <span className="font-semibold">{anmodningPopup.medarbejder}</span></p>
                <p><span className="text-muted-foreground">Periode:</span> {anmodningPopup.fra_dato} – {anmodningPopup.til_dato}</p>
                <p><span className="text-muted-foreground">Feriedage:</span> <span className="font-semibold">{ferieDage.hverdage}</span> hverdage{ferieDage.weekender > 0 ? ` · ${ferieDage.weekender} weekend` : ""}{ferieDage.helligdage > 0 ? ` · ${ferieDage.helligdage} helligdag${ferieDage.helligdage !== 1 ? "e" : ""}` : ""}</p>
                {anmodningPopup.note_fra_medarbejder && (
                  <p><span className="text-muted-foreground">Note:</span> "{anmodningPopup.note_fra_medarbejder}"</p>
                )}
                {anmodningPopup.anmodet_at && (
                  <p className="text-[13px] text-muted-foreground">
                    Anmodet {new Date(anmodningPopup.anmodet_at).toLocaleString("da-DK")}
                  </p>
                )}
              </div>

              {anmodningAfvisModus && (
                <div className="mb-3">
                  <label className="text-[13px] text-muted-foreground mb-1 block">Begrundelse (valgfri)</label>
                  <textarea value={anmodningAfvisGrund} onChange={e => setAnmodningAfvisGrund(e.target.value)}
                    placeholder="Fx: passer ikke i kalenderen"
                    className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm min-h-[60px]" />
                </div>
              )}

              <div className="flex justify-end gap-2">
                {!anmodningAfvisModus ? (
                  <>
                    <button onClick={() => setAnmodningAfvisModus(true)} disabled={anmodningBusy}
                      className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary disabled:opacity-50">
                      Afvis
                    </button>
                    <button onClick={godkendAnmodning} disabled={anmodningBusy}
                      className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                      {anmodningBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      Godkend
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { setAnmodningAfvisModus(false); setAnmodningAfvisGrund(""); }}
                      disabled={anmodningBusy}
                      className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary disabled:opacity-50">
                      Fortryd
                    </button>
                    <button onClick={afvisAnmodning} disabled={anmodningBusy}
                      className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-500 text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                      {anmodningBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      Bekræft afvisning
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Bulk-kopier modal */}
      {showBulkKopierModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-2xl shadow-2xl border border-border p-5 w-full max-w-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm">Kopier {selectedVagter.size} vagt{selectedVagter.size !== 1 ? "er" : ""}</h3>
              <button onClick={() => setShowBulkKopierModal(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div>
              <label className="flex items-center gap-2 text-[13px] text-muted-foreground mb-2 cursor-pointer select-none">
                <input type="checkbox" checked={bulkBeholdMedarb}
                  onChange={e => setBulkBeholdMedarb(e.target.checked)}
                  className="w-4 h-4 accent-[#FF2A6D]" />
                Behold oprindelig medarbejder pr. vagt
              </label>
              {!bulkBeholdMedarb && (
                <>
                  <label className="block text-[13px] text-muted-foreground mb-1">Kopier til medarbejder</label>
                  <select value={bulkKopierTil} onChange={e => setBulkKopierTil(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30">
                    <option value="">Vælg medarbejder...</option>
                    {[...medarbejdere].sort((a,b) => a.name.localeCompare(b.name)).map(m => (
                      <option key={m.name} value={m.name}>{m.name}</option>
                    ))}
                  </select>
                </>
              )}
            </div>
            <div>
              <label className="block text-[13px] text-muted-foreground mb-2">Kopiér til</label>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => setBulkKopierMode("naeste_uge")}
                  className={`px-2 py-2 rounded-xl border text-[13px] font-semibold transition-colors ${bulkKopierMode === "naeste_uge" ? "bg-[#FF2A6D] text-white border-[#FF2A6D]" : "border-border hover:bg-secondary"}`}>
                  Næste uge
                </button>
                <button onClick={() => setBulkKopierMode("resten_maaned")}
                  className={`px-2 py-2 rounded-xl border text-[13px] font-semibold transition-colors ${bulkKopierMode === "resten_maaned" ? "bg-[#FF2A6D] text-white border-[#FF2A6D]" : "border-border hover:bg-secondary"}`}>
                  Resten af måneden
                </button>
                <button onClick={() => setBulkKopierMode("frem_til_uge")}
                  className={`px-2 py-2 rounded-xl border text-[13px] font-semibold transition-colors ${bulkKopierMode === "frem_til_uge" ? "bg-[#FF2A6D] text-white border-[#FF2A6D]" : "border-border hover:bg-secondary"}`}>
                  Frem til uge
                </button>
              </div>
              {bulkKopierMode === "frem_til_uge" && (
                <div className="mt-2">
                  <label className="block text-[13px] text-muted-foreground mb-1">Slut-uge</label>
                  <input type="week" value={bulkKopierTilUge}
                    onChange={e => setBulkKopierTilUge(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
                </div>
              )}
              <p className="text-[13px] text-muted-foreground mt-2">
                {bulkKopierMode === "naeste_uge"
                  ? "Kopierer vagterne én uge frem, på de samme ugedage."
                  : bulkKopierMode === "resten_maaned"
                  ? "Kopierer vagterne til alle matchende ugedage resten af måneden."
                  : "Kopierer vagterne ugentligt frem til og med den valgte uge (samme ugedag)."}
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowBulkKopierModal(false)} className="px-4 py-2 rounded-xl border border-border text-sm hover:bg-secondary transition-colors">Annuller</button>
              <button onClick={execBulkKopier}
                disabled={bulkBusy || (!bulkBeholdMedarb && !bulkKopierTil) || (bulkKopierMode === "frem_til_uge" && !bulkKopierTilUge)}
                className="px-4 py-2 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-2">
                {bulkBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                Kopier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kopier-modal */}
      {showKopierModal && (
        <KopierModal
          mode={showKopierModal}
          view={view}
          currentMonday={currentMonday}
          activeMaaned={activeMaaned}
          medarbejdere={medarbejdere}
          onClose={() => setShowKopierModal(null)}
          onKopier={view === "uge" ? handleKopierUge : handleKopierMaaned}
          hdrs={hdrs}
          loadVagter={loadVagter}
        />
      )}

      {/* Undo-toast */}
      {undoToast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-card border border-border rounded-2xl shadow-2xl px-4 py-3 text-sm">
          <span className="text-muted-foreground">{undoToast.message}</span>
          <button onClick={handleUndo} className="font-semibold text-[#FF2A6D] hover:opacity-80">Fortryd</button>
          <button onClick={() => { setUndoToast(null); if (undoTimerRef.current) clearTimeout(undoTimerRef.current); }} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Eksport-modal */}
      {showExportModal && (
        <ExportModal
          vagter={sectionVagter}
          activeMaaned={activeMaaned}
          currentMonday={currentMonday}
          view={view}
          token={token}
          isSuperAdmin={isSuperAdmin}
          cityFilter={cityFilter}
          fetchRange={fetchRange}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
}
